<template>
  <div class="container content">
    <b-alert v-model="isDowntime" variant="danger">
      Cedience Platform is currently undergoing maintenance. You may experience
      errors and delays in your results for the next hour. We apologize for any
      inconvenience.
    </b-alert>
    <div class="row">
      <div id="sidebar" class="mx-md-3" style="width: 25%">
        <b-card>
          <div>
            Topic Filters
            <font-awesome-icon
              id="page-filter-options-icon"
              v-b-modal.page-filter-options
              :icon="['far', 'question-circle']"
              class="mx-2"
              size="1x"
            />
            <b-modal
              id="add-page-filters-modal"
              size="lg"
              hide-header
              hide-footer
              no-fade
              @hide="loadedPageFiltersForm = false"
            >
              <iframe
                class="airtable-embed"
                src="https://airtable.com/embed/shr6A8TnMI5MZDCVd"
                frameborder="0"
                onmousewheel=""
                width="100%"
                height="533"
                style="background: transparent; border: 1px solid #ccc"
                @load="loadedPageFiltersForm = true"
              ></iframe>
              <span v-if="!loadedPageFiltersForm">Loading...</span>
            </b-modal>
          </div>
          <b-form-radio-group
            id="search-modes"
            v-model="selectedSearchMode"
            :options="getFilteredSearchModes"
            html-field="description"
            value-field="mode"
            stacked
          ></b-form-radio-group>
          <b-button
            size="sm"
            class="ml-4 mt-3"
            variant="outline-primary"
            @click="$bvModal.show('add-page-filters-modal')"
            >+ Add Filter</b-button
          >
          <div v-if="selectedSearchMode.endsWith('(Experimental)')">
            <br />
            <div class="text-center">AI Confidence: >={{ percMatchMode }}%</div>
            <b-form-input
              id="perc-match-mode"
              v-model="percMatchMode"
              class="w-70"
              type="range"
              min="50"
              max="99"
              @change="refreshExpSearchModes()"
            ></b-form-input>
            <div class="float-left font-weight-light text-sm">Low</div>
            <div class="float-right font-weight-light text-sm">High</div>
          </div>
        </b-card>
        <b-card class="mt-md-1" style="width: 100%">
          <div id="filter-tour"></div>
          Document Filters
          <font-awesome-icon
            id="filter-options-icon"
            v-b-modal.filter-options
            :icon="['far', 'question-circle']"
            class="mx-2"
            size="1x"
          />
          <br />
          <b-modal
            id="add-document-filters-modal"
            size="lg"
            hide-header
            hide-footer
            no-fade
            @hide="loadedDocumentFiltersForm = false"
          >
            <iframe
              class="airtable-embed"
              src="https://airtable.com/embed/shrv5Ko0KeU0v8Q5V"
              frameborder="0"
              onmousewheel=""
              width="100%"
              height="533"
              style="background: transparent; border: 1px solid #ccc"
              @load="loadedDocumentFiltersForm = true"
            ></iframe>
            <span v-if="!loadedDocumentFiltersForm">Loading...</span>
          </b-modal>
          <div
            v-if="
              !(
                !searchLoading &&
                searchResults.length === 0 &&
                currSearchTerm.length > 0
              )
            "
          >
            <div
              v-for="filterGroup in Object.keys(collapsibleFilterGroups)"
              :key="filterGroup"
            >
              <div v-if="filterGroup !== 'Dates'">
                <b-button
                  variant="link"
                  class="p-0 text-dark text-decoration-none font-weight-bold"
                  @click="
                    collapsibleFilterGroups[filterGroup].visible =
                      !collapsibleFilterGroups[filterGroup].visible
                  "
                  ><font-awesome-icon
                    :icon="[
                      'fas',
                      collapsibleFilterGroups[filterGroup].visible
                        ? 'chevron-down'
                        : 'chevron-right',
                    ]"
                    class="mr-2"
                    size="1x"
                  />{{ filterGroup }}</b-button
                >
                <b-collapse
                  v-model="collapsibleFilterGroups[filterGroup].visible"
                >
                  <div
                    v-for="(filter, key) in Object.fromEntries(
                      Object.entries(filters).filter(
                        ([k, f]) => f.group === filterGroup
                      )
                    )"
                    :key="key"
                  >
                    <div
                      v-if="
                        filter.numPages &&
                        filter.numPages.checkbox &&
                        !(
                          filters.docCategory.selected.indexOf('Guidelines') ===
                            -1 &&
                          ['guidance-status', 'guidance-topic'].indexOf(
                            filter.numPages.id
                          ) >= 0
                        )
                      "
                      class="mx-md-1"
                    >
                      <b-overlay
                        :show="filter.loading"
                        :opacity="1.0"
                        :blur="loadingBlur"
                        no-center
                      >
                        <template #overlay>
                          <div class="mt-5 text-center"></div>
                        </template>
                        <checkbox-filter
                          v-show="filter.numPages.options.length > 0"
                          class="mt-md-2"
                          :filterid="filter.numPages.id"
                          :filtername="filter.numPages.label"
                          :options="filter.numPages.options"
                          :search="filter.search"
                          :selected="filter.selected"
                          @apply-selections="
                            onFilterSelectionChange(
                              filter.numPages.name,
                              ...arguments
                            )
                          "
                        />
                      </b-overlay>
                    </div>
                  </div>
                </b-collapse>
              </div>
              <div v-else>
                <b-button
                  variant="link"
                  class="p-0 text-dark text-decoration-none font-weight-bold"
                  @click="
                    collapsibleFilterGroups['Dates'].visible =
                      !collapsibleFilterGroups['Dates'].visible
                  "
                  ><font-awesome-icon
                    :icon="[
                      'fas',
                      collapsibleFilterGroups['Dates'].visible
                        ? 'chevron-down'
                        : 'chevron-right',
                    ]"
                    class="mr-2"
                    size="1x"
                  />Dates</b-button
                >
                <b-collapse v-model="collapsibleFilterGroups['Dates'].visible">
                  <div class="mx-md-1 mt-md-3">
                    From:
                    <b-form-datepicker
                      id="filter-date-from"
                      v-model="actionDateStartValue"
                      dropright
                      :min="minActionDate"
                      :max="maxActionDate"
                      :date-format-options="{
                        year: 'numeric',
                        month: 'short',
                        day: '2-digit',
                      }"
                      :disabled="dateFilterLoading"
                      today-button
                      reset-button
                      close-button
                      locale="en"
                      @input="onFilterSelectionChange('action_date')"
                    ></b-form-datepicker>
                  </div>
                  <div class="mx-md-1 mt-md-3">
                    To:
                    <b-form-datepicker
                      id="filter-date-to"
                      v-model="actionDateEndValue"
                      dropright
                      :min="minActionDate"
                      :max="maxActionDate"
                      :date-format-options="{
                        year: 'numeric',
                        month: 'short',
                        day: '2-digit',
                      }"
                      today-button
                      reset-button
                      close-button
                      locale="en"
                      @input="onFilterSelectionChange('action_date')"
                    ></b-form-datepicker>
                  </div>
                </b-collapse>
              </div>
            </div>
            <div
              v-if="
                !(
                  !searchLoading &&
                  searchResults.length === 0 &&
                  currSearchTerm.length > 0
                )
              "
            ></div>
          </div>
          <div v-else class="mt-2">No results found.</div>
          <b-button
            size="sm"
            class="ml-3 mt-2"
            variant="outline-primary"
            @click="$bvModal.show('add-document-filters-modal')"
            >+ Add Filter</b-button
          >
        </b-card>
      </div>
      <div
        id="search-results-section"
        class="ml-md-3"
        style="width: 70%"
        no-body
      >
        <div id="search-results" ref="searchResults">
          <div class="mb-1 tab-text">
            <b-tabs id="tab-group" v-model="tabIndex">
              <b-tab
                v-show="showAnalytics"
                id="analytics-tab"
                ref="analyticsTabRef"
              >
                <template #title>
                  Analytics
                  <font-awesome-icon
                    id="analytics-options-icon"
                    v-b-modal.analytics-options
                    :icon="['far', 'question-circle']"
                    class="mx-2"
                    size="1x"
                  />
                </template>
              </b-tab>
              <b-tab id="search-results-tab">
                <template #title>
                  Search Results
                  <font-awesome-icon
                    id="search-options-icon"
                    v-b-modal.search-options
                    :icon="['far', 'question-circle']"
                    class="mx-2"
                    size="1x"
                  />
                </template>
              </b-tab>
            </b-tabs>
          </div>
          <autocomplete
            id="search-input"
            ref="searchInput"
            :search="getAcItems"
            aria-label="Search documents"
            :value="searchTerm"
            placeholder="Type here to search..."
            @submit="onSearchSubmit"
          ></autocomplete>
          <div
            v-if="getFilterTags.length > 0"
            class="mt-3 p-1 border-top border-bottom"
          >
            <b-form-tag
              v-for="tag in getFilterTags.slice(0, 10)"
              :id="'filter-tag-' + tag.val"
              :key="tag.val"
              :title="tag.val"
              variant="primary"
              class="mr-1"
              :disabled="
                (['fromDate', 'toDate'].indexOf(tag.name) >= 0 &&
                  filters.timeline.loading) ||
                (['fromDate', 'toDate'].indexOf(tag.name) < 0 &&
                  filters[tag.name].loading)
              "
              @remove="removeFilterTag(tag)"
              >{{ tag.val }}
            </b-form-tag>
            <a
              v-if="getFilterTags.length > 10 && !showTags"
              id="filter-tag-show-all"
              class="show-all-tags"
              @click="showTags = true"
            >
              Show all
            </a>
            <span v-else>
              <b-form-tag
                v-for="tag in getFilterTags.slice(10)"
                :id="'filter-tag-' + tag.val"
                :key="tag.val"
                :title="tag.val"
                variant="primary"
                class="mr-1"
                @remove="removeFilterTag(tag)"
                >{{ tag.val }}</b-form-tag
              >
              <a
                v-show="getFilterTags.length > 10 && showTags"
                id="filter-tag-hide"
                class="hide-tags"
                @click="showTags = false"
              >
                Hide
              </a>
            </span>
            <a
              v-show="getFilterTags.length > 0 && !searchLoading"
              id="filter-tag-clear-all"
              class="clear-filters"
              @click="clearAllFilters()"
              >Clear all</a
            >
          </div>
          <div
            class="col-md-12 text-muted align-left"
            v-show="
              isSuggestFiltersUngated &&
              suggestResponse &&
              suggestResponse['fields']['products.product_name'].length > 0 &&
              suggestResponse['fields']['products.product_name'].filter(
                (p) => getFilterTags.map((f) => f.val).indexOf(p) < 0
              ).length > 0
            "
          >
            Did you mean to add these filters:
            <b-link
              class="mx-1 my-1"
              v-b-tooltip.hover.html="
                productName.length > 20 ? productName : ''
              "
              @click="addFilterTag('productName', productName)"
              v-for="productName in suggestResponse['fields'][
                'products.product_name'
              ]
                .filter((p) => getFilterTags.map((f) => f.val).indexOf(p) < 0)
                .slice(0, 3)"
              :key="productName"
            >
              {{
                productName.slice(0, 20) +
                (productName.length > 20 ? '...' : '')
              }}
            </b-link>
          </div>
          <div class="col-md-12 text-muted align-left">
            <div class="p-1">
              <b-link
                class="ml-2 text-decoration-none"
                variant="outline-primary"
                @click="$bvModal.show('request-assistance-modal')"
              >
                Request search assistance
              </b-link>
              <b-link
                class="ml-4 text-decoration-none"
                variant="outline-primary"
                href="https://calendly.com/cediencesupport"
                target="_blank"
                >Schedule support call</b-link
              >
            </div>
            <div class="p-1" v-if="isDev">
              <b-form-checkbox v-model="semanticSearch" switch>
                Semantic Search
              </b-form-checkbox>
            </div>
            <div v-if="searchLoading" class="p-2">
              Loading number of matches...
            </div>
            <div v-else-if="queryError.length > 0" class="error-message p-2">
              {{ queryError }}
            </div>
            <div
              v-else-if="
                !searchLoading && searchResults.length > 0 && semanticSearch
              "
              class="p-2"
            >
              <b-row>
                <b-col cols="10">
                  Showing results from the top 100 matching documents
                </b-col>
              </b-row>
            </div>
            <div
              v-else-if="!searchLoading && searchResults.length > 0"
              class="p-2"
            >
              <b-row>
                <b-col cols="10">
                  Found
                  {{ numHits.toLocaleString() }}
                  {{ numHits > 1 ? 'matches' : 'match' }} from
                  <b-link
                    v-if="numDocs <= 200"
                    class="text-underline"
                    @click="grabDocs(true)"
                  >
                    {{ numDocs.toLocaleString() }}
                    {{ numDocs > 1 ? 'documents' : 'document' }}
                  </b-link>
                  <span v-else>
                    {{ numDocs.toLocaleString() }}
                    {{ numDocs > 1 ? 'documents' : 'document' }}
                  </span>
                  <span v-if="numProducts">
                    and
                    <b-link
                      v-if="numProducts <= 200"
                      class="text-underline"
                      @click="grabProducts()"
                    >
                      {{ numProducts.toLocaleString() }}
                      {{ numProducts > 1 ? 'products' : 'product' }}
                    </b-link>
                    <span v-else>
                      {{ numProducts.toLocaleString() }}
                      {{ numProducts > 1 ? 'products' : 'product' }}
                    </span>
                  </span>
                  <span v-if="currPage > 1"> (Page {{ currPage }})</span>
                  <span
                    v-if="show_select"
                    v-show="!trialMode && !isLabelSectionSearch"
                    v-b-tooltip.hover.html="'Download selected snippets'"
                    class="float-right mx-2"
                    size="sm"
                    @click="downloadData(select_download, 'search-results')"
                  >
                    <b-icon
                      v-if="downloading"
                      icon="arrow-clockwise"
                      animation="spin"
                      style="width: 20px; height: 20px"
                    ></b-icon>
                    <b-icon
                      v-else
                      icon="download"
                      style="width: 20px; height: 20px"
                    ></b-icon>
                  </span>
                  <span
                    v-else
                    v-show="!trialMode && !isLabelSectionSearch"
                    v-b-tooltip.hover.html="
                      'Download results from the first 200 documents'
                    "
                    class="float-right mx-2"
                    size="sm"
                    @click="downloadData(searchResults, 'search-results')"
                  >
                    <b-icon
                      v-if="downloading"
                      icon="arrow-clockwise"
                      animation="spin"
                      style="width: 20px; height: 20px"
                    ></b-icon>
                    <b-icon
                      v-else
                      icon="download"
                      style="width: 20px; height: 20px"
                    ></b-icon>
                  </span>
                  <span
                    v-show="!trialMode && !isLabelSectionSearch"
                    v-b-tooltip.hover.html="'Select pages to download'"
                    class="float-right mx-2 link"
                    size="sm"
                    @click="toggle_show_select()"
                  >
                    <b-icon
                      v-if="show_select"
                      icon="check-square-fill"
                      style="width: 20px; height: 20px"
                    ></b-icon>
                    <b-icon
                      v-else
                      icon="check-square"
                      style="width: 20px; height: 20px"
                    ></b-icon>
                  </span>
                  <span
                    v-b-tooltip.hover.html="tooltipMsg('Bookmark')"
                    class="save-search float-right mx-2"
                  >
                    <b-icon
                      v-if="!search_saved"
                      icon="bookmarks"
                      style="width: 20px; height: 20px"
                      @click="saveSearch"
                    ></b-icon>
                    <b-icon
                      v-else
                      icon="bookmarks-fill"
                      style="width: 20px; height: 20px"
                      @click="deleteSearch"
                    ></b-icon>
                  </span>
                </b-col>
                <b-col cols="2" class="align-right float-right">
                  <b-form-select
                    id="sort-order"
                    v-model="sortOrder"
                    size="sm"
                    :options="sortOptions"
                    @change="onFilterSelectionChange"
                  ></b-form-select>
                </b-col>
              </b-row>
              <b-row
                v-if="isDev && trialInfoByQuery && trialInfoByQuery.length > 0"
              >
                <b-col class="ml-5">
                  + {{ trialInfoByQueryNum.toLocaleString() }}
                  CT.gov clinical studies (
                  <a id="popover-matching-trials" class="num-clinical-trials"
                    >view here</a
                  >
                  )
                  <b-popover
                    target="popover-matching-trials"
                    triggers="hover"
                    placement="bottom"
                    custom-class="w-75"
                  >
                    <b-table :items="trialInfoByQuery" striped hover>
                      <template #cell(study_title)="data">
                        <span v-html="data.value"></span>
                      </template>
                      <!-- <template #table-colgroup="scope">
                <col
                  v-for="field in scope.fields"
                  :key="field.key"
                  :style="{
                    width:
                      field.key === 'foo' ? '120px' : '180px',
                  }"
                />
              </template> -->
                    </b-table>
                  </b-popover>
                </b-col>
              </b-row>
            </div>
            <div
              v-else-if="
                !searchLoading &&
                searchResults.length === 0 &&
                currSearchTerm.length > 0 &&
                !searchError
              "
            >
              No results found for your query. Please try again after modifying
              the filters.
            </div>
            <div v-else-if="searchError" id="search-error">
              We're experiencing some issues retrieving your results.<br />
              Please
              <a class="text-primary link" @click="reload()">refresh</a>
              the page or try again later. Contact us for support.
            </div>
          </div>
          <b-modal
            id="request-assistance-modal"
            size="lg"
            hide-header
            hide-footer
            no-fade
            @hide="loadedRequestAssistanceForm = false"
          >
            <iframe
              class="airtable-embed"
              src="https://airtable.com/embed/shrEGPDoP2btYRcTm"
              frameborder="0"
              onmousewheel=""
              width="100%"
              height="533"
              style="background: transparent; border: 1px solid #ccc"
              @load="loadedRequestAssistanceForm = true"
            ></iframe>
            <span v-if="!loadedRequestAssistanceForm">Loading...</span>
          </b-modal>
          <div v-if="showAnalytics && tabIndex === 0" class="mt-2 text-center">
            <b-form-radio-group
              id="analytics-groups"
              v-model="selectedAnalyticsGroup"
              :options="analyticsGroups"
              buttons
              button-variant="outline-primary"
            ></b-form-radio-group>
            <b-container ref="chartContainer" fluid="md">
              <b-row
                v-if="selectedAnalyticsGroup === 'Database Content'"
                cols="1"
              >
                <!-- dialog for Database Content over Time -->
                <el-dialog
                  :visible.sync="fullSizeChartVisible"
                  width="90%"
                  :center="true"
                >
                  <b-col
                    v-for="timelineFilter in Object.fromEntries(
                      Object.entries(filters).filter(
                        ([k, v]) =>
                          v.numPages &&
                          v.numPages.visualize &&
                          v.type === 'timeline' &&
                          v.numPages.visualizeOptions &&
                          v.numPages.visualizeOptions.length > 0
                      )
                    )"
                    :key="timelineFilter.numPages.id"
                  >
                    <span class="d-inline-block float-right" tabindex="0">
                      <b-button
                        :disabled="
                          trialMode ||
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          timelineFilter.loading ||
                          timelineFilter.numPages.visualizeOptions.length === 0
                        "
                        class="float-right"
                        size="sm"
                        @click="
                          downloadData(timelineFilter.numPages, '-full-size')
                        "
                      >
                        <font-awesome-icon
                          :icon="['fas', 'download']"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <span class="d-inline-block float-right" tabindex="0">
                      <b-button
                        :disabled="
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          timelineFilter.loading ||
                          timelineFilter.numPages.visualizeOptions.length === 0
                        "
                        variant="outline-dark"
                        class="float-right mx-1"
                        size="sm"
                        @click="
                          () => {
                            timelineFilter.numPages.asTable =
                              !timelineFilter.numPages.asTable;
                          }
                        "
                      >
                        <font-awesome-icon
                          :icon="[
                            'fas',
                            timelineFilter.numPages.asTable
                              ? 'chart-bar'
                              : 'table',
                          ]"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <div
                      v-if="
                        !(
                          !searchLoading &&
                          searchResults.length === 0 &&
                          currSearchTerm.length > 0
                        )
                      "
                    >
                      <b-skeleton-img
                        v-if="timelineFilter.loading"
                        height="400px"
                      ></b-skeleton-img>
                      <chart
                        v-else-if="
                          timelineFilter.numPages.visualizeOptions.length > 1 &&
                          !timelineFilter.numPages.asTable
                        "
                        :width="chartWidth * 3"
                        :height="chartHeight * 2"
                        fontSize="20px"
                        :chartId="`${timelineFilter.numPages.id}-full-size`"
                        :chartData="timelineFilter.numPages.visualizeOptions"
                        :chartDataSelected="[]"
                        :numTopOptions="timelineFilter.numPages.numTopOptions"
                        chartType="timeline"
                        :chartTitle="timelineFilter.numPages.title"
                        :chartYLabel="timelineFilter.numPages.yLabel"
                        :chartYDataType="timelineFilter.numPages.yDataType"
                        :chartYDataPerc="timelineFilter.numPages.yDataPerc"
                        @chart-click="
                          selectFilterValue(
                            timelineFilter.numPages.name,
                            ...arguments
                          )
                        "
                      ></chart>
                      <b-table
                        v-else-if="
                          timelineFilter.numPages.visualizeOptions.length > 1 &&
                          timelineFilter.numPages.asTable
                        "
                        class="mt-5 w-100"
                        sticky-header="750px"
                        :items="
                          timelineFilter.numPages.visualizeOptions
                            .slice(0, 100)
                            .reverse()
                        "
                        :fields="[
                          {
                            key: 'key',
                            label: `${timelineFilter.numPages.xLabel}`,
                          },
                          {
                            key: 'doc_count',
                            label: timelineFilter.numPages.yLabel,
                            formatter: (value) =>
                              timelineFilter.numPages.yDataPerc
                                ? parseFloat(value).toFixed(2) + '%'
                                : value.toLocaleString(),
                          },
                        ]"
                        outlined
                      >
                      </b-table>
                      <span
                        v-else-if="
                          timelineFilter.numPages.visualizeOptions.length === 1
                        "
                      >
                        {{ timelineFilter.numPages.title }}
                        <br />
                        Only 1 value present:
                        {{ timelineFilter.numPages.visualizeOptions[0].key }}
                      </span>
                    </div>
                  </b-col>
                </el-dialog>
                <b-col
                  v-for="timelineFilter in Object.fromEntries(
                    Object.entries(filters).filter(
                      ([k, v]) =>
                        v.numPages &&
                        v.numPages.visualize &&
                        v.type === 'timeline' &&
                        v.numPages.visualizeOptions &&
                        v.numPages.visualizeOptions.length > 0
                    )
                  )"
                  :key="timelineFilter.numPages.id"
                >
                  <b-card class="text-center mt-1 mb-1 chart-card">
                    <span
                      v-b-tooltip.hover.html="
                        tooltipMsg('Download', timelineFilter.numPages.asTable)
                      "
                      class="d-inline-block float-right"
                      tabindex="0"
                    >
                      <b-button
                        :disabled="
                          trialMode ||
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          timelineFilter.loading ||
                          timelineFilter.numPages.visualizeOptions.length === 0
                        "
                        class="float-right"
                        size="sm"
                        @click="downloadData(timelineFilter.numPages)"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'download']"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <span
                      v-b-tooltip.hover.html="
                        tooltipMsg('Show', timelineFilter.numPages.asTable)
                      "
                      class="d-inline-block float-right"
                      tabindex="0"
                    >
                      <b-button
                        :disabled="
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          timelineFilter.loading ||
                          timelineFilter.numPages.visualizeOptions.length === 0
                        "
                        variant="outline-dark"
                        class="float-right mx-1"
                        size="sm"
                        @click="
                          () => {
                            timelineFilter.numPages.asTable =
                              !timelineFilter.numPages.asTable;
                          }
                        "
                      >
                        <font-awesome-icon
                          :icon="[
                            'fas',
                            timelineFilter.numPages.asTable
                              ? 'chart-bar'
                              : 'table',
                          ]"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <span
                      v-b-tooltip.hover.html="
                        tooltipMsg('Expand', timelineFilter.numPages.asTable)
                      "
                      class="d-inline-block float-right"
                      tabindex="0"
                    >
                      <b-button
                        :disabled="
                          trialMode ||
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          timelineFilter.loading ||
                          timelineFilter.numPages.visualizeOptions.length === 0
                        "
                        variant="outline-dark"
                        class="float-right mx-1"
                        size="sm"
                        @click="toggleToFullSizeTimeline"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'expand-alt']"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <div
                      v-if="
                        !(
                          !searchLoading &&
                          searchResults.length === 0 &&
                          currSearchTerm.length > 0
                        )
                      "
                    >
                      <b-skeleton-img
                        v-if="timelineFilter.loading"
                        height="400px"
                      ></b-skeleton-img>
                      <chart
                        v-else-if="
                          timelineFilter.numPages.visualizeOptions.length > 1 &&
                          !timelineFilter.numPages.asTable
                        "
                        :width="chartWidth * 2"
                        :height="chartHeight"
                        :chartId="timelineFilter.numPages.id"
                        :chartData="timelineFilter.numPages.visualizeOptions"
                        :chartDataSelected="[]"
                        :numTopOptions="timelineFilter.numPages.numTopOptions"
                        chartType="timeline"
                        :chartTitle="timelineFilter.numPages.title"
                        :chartYLabel="timelineFilter.numPages.yLabel"
                        :chartYDataType="timelineFilter.numPages.yDataType"
                        :chartYDataPerc="timelineFilter.numPages.yDataPerc"
                        @chart-click="
                          selectFilterValue(
                            timelineFilter.numPages.name,
                            ...arguments
                          )
                        "
                      ></chart>
                      <b-table
                        v-else-if="
                          timelineFilter.numPages.visualizeOptions.length > 1 &&
                          timelineFilter.numPages.asTable
                        "
                        class="mt-5 w-100"
                        sticky-header="375px"
                        :items="
                          timelineFilter.numPages.visualizeOptions
                            .slice(-100)
                            .reverse()
                        "
                        :fields="[
                          {
                            key: 'key',
                            label: `${timelineFilter.numPages.xLabel}`,
                          },
                          {
                            key: 'doc_count',
                            label: timelineFilter.numPages.yLabel,
                            formatter: (value) =>
                              timelineFilter.numPages.yDataPerc
                                ? parseFloat(value).toFixed(2) + '%'
                                : value.toLocaleString(),
                          },
                        ]"
                        outlined
                      >
                      </b-table>
                      <span
                        v-else-if="
                          timelineFilter.numPages.visualizeOptions.length === 1
                        "
                      >
                        {{ timelineFilter.numPages.title }}
                        <br />
                        Only 1 value present:
                        {{ timelineFilter.numPages.visualizeOptions[0].key }}
                      </span>
                    </div>
                  </b-card>
                </b-col>
              </b-row>
              <b-row
                v-else-if="selectedAnalyticsGroup === 'Number of Products'"
                cols="1"
              >
                <!-- dialog for No. of Products over Time -->
                <el-dialog
                  :visible.sync="fullSizeChartVisible"
                  width="90%"
                  :center="true"
                >
                  <b-col
                    v-for="timelineFilter in Object.fromEntries(
                      Object.entries(filters).filter(
                        ([k, v]) =>
                          v.numProducts &&
                          v.numProducts.visualize &&
                          v.type === 'timeline' &&
                          v.numProducts.visualizeOptions &&
                          v.numProducts.visualizeOptions.length > 0
                      )
                    )"
                    :key="timelineFilter.numProducts.id"
                  >
                    <span class="d-inline-block float-right" tabindex="0">
                      <b-button
                        :disabled="
                          trialMode ||
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          timelineFilter.loading ||
                          timelineFilter.numProducts.visualizeOptions.length ===
                            0
                        "
                        class="float-right"
                        size="sm"
                        @click="
                          downloadData(timelineFilter.numProducts, '-full-size')
                        "
                      >
                        <font-awesome-icon
                          :icon="['fas', 'download']"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <span class="d-inline-block float-right" tabindex="0">
                      <b-button
                        :disabled="
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          timelineFilter.loading ||
                          timelineFilter.numProducts.visualizeOptions.length ===
                            0
                        "
                        variant="outline-dark"
                        class="float-right mx-1"
                        size="sm"
                        @click="
                          () => {
                            timelineFilter.numProducts.asTable =
                              !timelineFilter.numProducts.asTable;
                          }
                        "
                      >
                        <font-awesome-icon
                          :icon="[
                            'fas',
                            timelineFilter.numProducts.asTable
                              ? 'chart-bar'
                              : 'table',
                          ]"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <div
                      v-if="
                        !(
                          !searchLoading &&
                          searchResults.length === 0 &&
                          currSearchTerm.length > 0
                        )
                      "
                    >
                      <b-skeleton-img
                        v-if="timelineFilter.loading"
                        height="400px"
                      ></b-skeleton-img>
                      <chart
                        v-else-if="
                          timelineFilter.numProducts.visualizeOptions.length >
                            1 && !timelineFilter.numProducts.asTable
                        "
                        :width="chartWidth * 3"
                        :height="chartHeight * 2"
                        fontSize="20px"
                        :chartId="`${timelineFilter.numProducts.id}-full-size`"
                        :chartData="timelineFilter.numProducts.visualizeOptions"
                        :chartDataSelected="[]"
                        :numTopOptions="
                          timelineFilter.numProducts.numTopOptions
                        "
                        chartType="timeline"
                        :chartTitle="timelineFilter.numProducts.title"
                        :chartYLabel="timelineFilter.numProducts.yLabel"
                        :chartYDataType="timelineFilter.numProducts.yDataType"
                        :chartYDataPerc="timelineFilter.numProducts.yDataPerc"
                        @chart-click="
                          selectFilterValue(
                            timelineFilter.numProducts.name,
                            ...arguments
                          )
                        "
                      ></chart>
                      <b-table
                        v-else-if="
                          timelineFilter.numProducts.visualizeOptions.length >
                            1 && timelineFilter.numProducts.asTable
                        "
                        class="mt-5 w-100"
                        sticky-header="750px"
                        :items="
                          timelineFilter.numProducts.visualizeOptions
                            .slice(0, 100)
                            .reverse()
                        "
                        :fields="[
                          {
                            key: 'key',
                            label: `${timelineFilter.numProducts.xLabel}`,
                          },
                          {
                            key: 'doc_count',
                            label: timelineFilter.numProducts.yLabel,
                            formatter: (value) =>
                              timelineFilter.numProducts.yDataPerc
                                ? parseFloat(value).toFixed(2) + '%'
                                : value.toLocaleString(),
                          },
                        ]"
                        outlined
                      ></b-table>
                      <span
                        v-else-if="
                          timelineFilter.numProducts.visualizeOptions.length ==
                          1
                        "
                      >
                        {{ timelineFilter.numProducts.title }}
                        <br />
                        Only 1 value present:
                        {{ timelineFilter.numProducts.visualizeOptions[0].key }}
                      </span>
                    </div>
                  </b-col>
                </el-dialog>
                <b-col
                  v-for="timelineFilter in Object.fromEntries(
                    Object.entries(filters).filter(
                      ([k, v]) =>
                        v.numProducts &&
                        v.numProducts.visualize &&
                        v.type === 'timeline' &&
                        v.numProducts.visualizeOptions &&
                        v.numProducts.visualizeOptions.length > 0
                    )
                  )"
                  :key="timelineFilter.numProducts.id"
                >
                  <b-card class="text-center mt-1 mb-1 chart-card">
                    <span
                      v-b-tooltip.hover.html="
                        tooltipMsg(
                          'Download',
                          timelineFilter.numProducts.asTable
                        )
                      "
                      class="d-inline-block float-right"
                      tabindex="0"
                    >
                      <b-button
                        :disabled="
                          trialMode ||
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          timelineFilter.loading ||
                          timelineFilter.numProducts.visualizeOptions.length ===
                            0
                        "
                        class="float-right"
                        size="sm"
                        @click="downloadData(timelineFilter.numProducts)"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'download']"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <span
                      v-b-tooltip.hover.html="
                        tooltipMsg('Show', timelineFilter.numProducts.asTable)
                      "
                      class="d-inline-block float-right"
                      tabindex="0"
                    >
                      <b-button
                        :disabled="
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          timelineFilter.loading ||
                          timelineFilter.numProducts.visualizeOptions.length ===
                            0
                        "
                        variant="outline-dark"
                        class="float-right mx-1"
                        size="sm"
                        @click="
                          () => {
                            timelineFilter.numProducts.asTable =
                              !timelineFilter.numProducts.asTable;
                          }
                        "
                      >
                        <font-awesome-icon
                          :icon="[
                            'fas',
                            timelineFilter.numProducts.asTable
                              ? 'chart-bar'
                              : 'table',
                          ]"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <span
                      v-b-tooltip.hover.html="
                        tooltipMsg('Expand', timelineFilter.numProducts.asTable)
                      "
                      class="d-inline-block float-right"
                      tabindex="0"
                    >
                      <b-button
                        :disabled="
                          trialMode ||
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          timelineFilter.loading ||
                          timelineFilter.numProducts.visualizeOptions.length ===
                            0
                        "
                        variant="outline-dark"
                        class="float-right mx-1"
                        size="sm"
                        @click="toggleToFullSizeTimeline"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'expand-alt']"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <div
                      v-if="
                        !(
                          !searchLoading &&
                          searchResults.length === 0 &&
                          currSearchTerm.length > 0
                        )
                      "
                    >
                      <b-skeleton-img
                        v-if="timelineFilter.loading"
                        height="400px"
                      ></b-skeleton-img>
                      <chart
                        v-else-if="
                          timelineFilter.numProducts.visualizeOptions.length >
                            1 && !timelineFilter.numProducts.asTable
                        "
                        :width="chartWidth * 2"
                        :height="chartHeight"
                        :chartId="timelineFilter.numProducts.id"
                        :chartData="timelineFilter.numProducts.visualizeOptions"
                        :chartDataSelected="[]"
                        :numTopOptions="
                          timelineFilter.numProducts.numTopOptions
                        "
                        chartType="timeline"
                        :chartTitle="timelineFilter.numProducts.title"
                        :chartYLabel="timelineFilter.numProducts.yLabel"
                        :chartYDataType="timelineFilter.numProducts.yDataType"
                        :chartYDataPerc="timelineFilter.numProducts.yDataPerc"
                        @chart-click="
                          selectFilterValue(
                            timelineFilter.numProducts.name,
                            ...arguments
                          )
                        "
                      ></chart>
                      <b-table
                        v-else-if="
                          timelineFilter.numProducts.visualizeOptions.length >
                            1 && timelineFilter.numProducts.asTable
                        "
                        class="mt-5 w-100"
                        sticky-header="375px"
                        :items="
                          timelineFilter.numProducts.visualizeOptions
                            .slice(0, 100)
                            .reverse()
                        "
                        :fields="[
                          {
                            key: 'key',
                            label: `${timelineFilter.numProducts.xLabel}`,
                          },
                          {
                            key: 'doc_count',
                            label: timelineFilter.numProducts.yLabel,
                            formatter: (value) =>
                              timelineFilter.numProducts.yDataPerc
                                ? parseFloat(value).toFixed(2) + '%'
                                : value.toLocaleString(),
                          },
                        ]"
                        outlined
                      ></b-table>
                      <span
                        v-else-if="
                          timelineFilter.numProducts.visualizeOptions.length ==
                          1
                        "
                      >
                        {{ timelineFilter.numProducts.title }}
                        <br />
                        Only 1 value present:
                        {{ timelineFilter.numProducts.visualizeOptions[0].key }}
                      </span>
                    </div>
                  </b-card>
                </b-col>
              </b-row>
              <b-row
                v-if="selectedAnalyticsGroup === 'Database Content'"
                cols="2"
              >
                <b-col
                  v-for="filter in Object.fromEntries(
                    Object.entries(filters).filter(
                      ([k, v]) =>
                        v.numPages &&
                        v.numPages.visualize &&
                        v.numPages.visualizeOptions &&
                        v.numPages.visualizeOptions.length > 0 &&
                        v.type !== 'timeline'
                    )
                  )"
                  :key="filter.numPages.id"
                >
                  <!-- Dialog for others in Database Content -->
                  <el-dialog
                    :visible="currentChart === filter.numPages.id"
                    width="90%"
                    :before-close="handleClose"
                    :center="true"
                  >
                    <span class="d-inline-block float-right" tabindex="0">
                      <b-button
                        :disabled="
                          trialMode ||
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          filter.loading ||
                          filter.numPages.visualizeOptions.length === 0
                        "
                        class="float-right"
                        size="sm"
                        @click="downloadData(filter.numPages, '-full-size')"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'download']"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <span class="d-inline-block float-right" tabindex="0">
                      <b-button
                        :disabled="
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          filter.loading ||
                          filter.numPages.visualizeOptions.length === 0
                        "
                        variant="outline-dark"
                        class="float-right mx-1"
                        size="sm"
                        @click="
                          () => {
                            filter.numPages.asTable = !filter.numPages.asTable;
                          }
                        "
                      >
                        <font-awesome-icon
                          :icon="[
                            'fas',
                            filter.numPages.asTable ? 'chart-bar' : 'table',
                          ]"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <div
                      v-if="
                        !(
                          !searchLoading &&
                          searchResults.length === 0 &&
                          currSearchTerm.length > 0
                        )
                      "
                    >
                      <b-skeleton-img
                        v-if="filter.loading"
                        height="400px"
                      ></b-skeleton-img>
                      <chart
                        v-else-if="
                          filter.numPages.visualizeOptions.length > 1 &&
                          !filter.numPages.asTable
                        "
                        :width="chartWidth * 3"
                        :height="chartHeight * 2"
                        fontSize="20px"
                        :chartId="`${filter.numPages.id}-full-size`"
                        :chartData="filter.numPages.visualizeOptions"
                        :chartDataSelected="
                          filters[filter.numPages.name] !== undefined
                            ? filters[filter.numPages.name].selected
                            : []
                        "
                        :numTopOptions="filter.numPages.numTopOptions * 2"
                        chartType="non-timeline"
                        :chartTitle="filter.numPages.title"
                        :chartYLabel="filter.numPages.yLabel"
                        :chartYDataType="filter.numPages.yDataType"
                        :chartYDataPerc="filter.numPages.yDataPerc"
                        @chart-click="
                          selectFilterValue(filter.numPages.name, ...arguments)
                        "
                      ></chart>
                      <b-table
                        v-else-if="
                          filter.numPages.visualizeOptions.length > 1 &&
                          filter.numPages.asTable
                        "
                        class="mt-5 w-100"
                        sticky-header="750px"
                        :items="filter.numPages.visualizeOptions.slice(0, 100)"
                        :fields="[
                          {
                            key: 'key',
                            label: `${filter.numPages.xLabel}`,
                          },
                          {
                            key: 'doc_count',
                            label: filter.numPages.yLabel,
                            formatter: (value) =>
                              filter.numPages.yDataPerc
                                ? parseFloat(value).toFixed(2) + '%'
                                : value.toLocaleString(),
                          },
                        ]"
                        outlined
                      ></b-table>
                      <span
                        v-else-if="
                          filter.numPages.visualizeOptions.length === 1
                        "
                      >
                        {{ filter.numPages.title }}
                        <br />
                        Only 1 value present:
                        {{ filter.numPages.visualizeOptions[0].key }}
                      </span>
                    </div>
                  </el-dialog>
                  <b-card class="text-center mt-1 mb-1 chart-card">
                    <span
                      v-b-tooltip.hover.html="
                        tooltipMsg('Download', filter.numPages.asTable)
                      "
                      class="d-inline-block float-right"
                      tabindex="0"
                    >
                      <b-button
                        :disabled="
                          trialMode ||
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          filter.loading ||
                          filter.numPages.visualizeOptions.length === 0
                        "
                        class="float-right"
                        size="sm"
                        @click="downloadData(filter.numPages)"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'download']"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <span
                      v-b-tooltip.hover.html="
                        tooltipMsg('Show', filter.numPages.asTable)
                      "
                      class="d-inline-block float-right"
                      tabindex="0"
                    >
                      <b-button
                        :disabled="
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          filter.loading ||
                          filter.numPages.visualizeOptions.length === 0
                        "
                        variant="outline-dark"
                        class="float-right mx-1"
                        size="sm"
                        @click="
                          () => {
                            filter.numPages.asTable = !filter.numPages.asTable;
                          }
                        "
                      >
                        <font-awesome-icon
                          :icon="[
                            'fas',
                            filter.numPages.asTable ? 'chart-bar' : 'table',
                          ]"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <span
                      v-b-tooltip.hover.html="
                        tooltipMsg('Expand', filter.numPages.asTable)
                      "
                      class="d-inline-block float-right"
                      tabindex="0"
                    >
                      <b-button
                        :disabled="
                          trialMode ||
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          filter.loading ||
                          filter.numPages.visualizeOptions.length === 0
                        "
                        variant="outline-dark"
                        class="float-right mx-1"
                        size="sm"
                        @click="toggleToFullSizeOthers(filter.numPages.id)"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'expand-alt']"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <div
                      v-if="
                        !(
                          !searchLoading &&
                          searchResults.length === 0 &&
                          currSearchTerm.length > 0
                        )
                      "
                    >
                      <b-skeleton-img
                        v-if="filter.loading"
                        height="400px"
                      ></b-skeleton-img>
                      <chart
                        v-else-if="
                          filter.numPages.visualizeOptions.length > 1 &&
                          !filter.numPages.asTable
                        "
                        :width="chartWidth"
                        :height="chartHeight"
                        :chartId="filter.numPages.id"
                        :chartData="filter.numPages.visualizeOptions"
                        :chartDataSelected="
                          filters[filter.numPages.name] !== undefined
                            ? filters[filter.numPages.name].selected
                            : []
                        "
                        :numTopOptions="filter.numPages.numTopOptions"
                        chartType="non-timeline"
                        :chartTitle="filter.numPages.title"
                        :chartYLabel="filter.numPages.yLabel"
                        :chartYDataType="filter.numPages.yDataType"
                        :chartYDataPerc="filter.numPages.yDataPerc"
                        @chart-click="
                          selectFilterValue(filter.numPages.name, ...arguments)
                        "
                      ></chart>
                      <b-table
                        v-else-if="
                          filter.numPages.visualizeOptions.length > 1 &&
                          filter.numPages.asTable
                        "
                        class="mt-5 w-100"
                        sticky-header="375px"
                        :items="filter.numPages.visualizeOptions.slice(0, 100)"
                        :fields="[
                          {
                            key: 'key',
                            label: `${filter.numPages.xLabel}`,
                          },
                          {
                            key: 'doc_count',
                            label: filter.numPages.yLabel,
                            formatter: (value) =>
                              filter.numPages.yDataPerc
                                ? parseFloat(value).toFixed(2) + '%'
                                : value.toLocaleString(),
                          },
                        ]"
                        outlined
                      ></b-table>
                      <span
                        v-else-if="
                          filter.numPages.visualizeOptions.length === 1
                        "
                      >
                        {{ filter.numPages.title }}
                        <br />
                        Only 1 value present:
                        {{ filter.numPages.visualizeOptions[0].key }}
                      </span>
                    </div>
                  </b-card>
                </b-col>
              </b-row>
              <b-row
                v-else-if="selectedAnalyticsGroup === 'Number of Products'"
                cols="2"
              >
                <b-col
                  v-for="filter in Object.fromEntries(
                    Object.entries(filters).filter(
                      ([k, v]) =>
                        v.numProducts &&
                        v.numProducts.visualize &&
                        v.numProducts.visualizeOptions &&
                        v.numProducts.visualizeOptions.length > 0 &&
                        v.type !== 'timeline'
                    )
                  )"
                  :key="filter.numProducts.id"
                >
                  <!-- Dialog for others in Number of Products -->
                  <el-dialog
                    :visible="currentChart === filter.numProducts.id"
                    :before-close="handleClose"
                    width="90%"
                    :center="true"
                  >
                    <span class="d-inline-block float-right" tabindex="0">
                      <b-button
                        :disabled="
                          trialMode ||
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          filter.loading ||
                          filter.numProducts.visualizeOptions.length === 0
                        "
                        class="float-right"
                        size="sm"
                        @click="downloadData(filter.numProducts, '-full-size')"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'download']"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <span class="d-inline-block float-right" tabindex="0">
                      <b-button
                        :disabled="
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          filter.loading ||
                          filter.numProducts.visualizeOptions.length === 0
                        "
                        variant="outline-dark"
                        class="float-right mx-1"
                        size="sm"
                        @click="
                          () => {
                            filter.numProducts.asTable =
                              !filter.numProducts.asTable;
                          }
                        "
                      >
                        <font-awesome-icon
                          :icon="[
                            'fas',
                            filter.numProducts.asTable ? 'chart-bar' : 'table',
                          ]"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <div
                      v-if="
                        !(
                          !searchLoading &&
                          searchResults.length === 0 &&
                          currSearchTerm.length > 0
                        )
                      "
                    >
                      <b-skeleton-img
                        v-if="filter.loading"
                        height="400px"
                      ></b-skeleton-img>
                      <chart
                        v-else-if="
                          filter.numProducts.visualizeOptions.length > 1 &&
                          !filter.numProducts.asTable
                        "
                        :width="chartWidth * 3"
                        :height="chartHeight * 2"
                        fontSize="20px"
                        :chartId="`${filter.numProducts.id}-full-size`"
                        :chartData="filter.numProducts.visualizeOptions"
                        :chartDataSelected="
                          filters[filter.numProducts.name] !== undefined
                            ? filters[filter.numProducts.name].selected
                            : []
                        "
                        :numTopOptions="filter.numProducts.numTopOptions"
                        chartType="non-timeline"
                        :chartTitle="filter.numProducts.title"
                        :chartYLabel="filter.numProducts.yLabel"
                        :chartYDataType="filter.numProducts.yDataType"
                        :chartYDataPerc="filter.numProducts.yDataPerc"
                        @chart-click="
                          selectFilterValue(
                            filter.numProducts.name,
                            ...arguments
                          )
                        "
                      ></chart>
                      <b-table
                        v-else-if="
                          filter.numProducts.visualizeOptions.length > 1 &&
                          filter.numProducts.asTable
                        "
                        class="mt-5 w-100"
                        sticky-header="750px"
                        :items="
                          filter.numProducts.visualizeOptions.slice(0, 100)
                        "
                        :fields="[
                          {
                            key: 'key',
                            label: `${filter.numProducts.xLabel}`,
                          },
                          {
                            key: 'doc_count',
                            label: filter.numProducts.yLabel,
                            formatter: (value) =>
                              filter.numProducts.yDataPerc
                                ? parseFloat(value).toFixed(2) + '%'
                                : value.toLocaleString(),
                          },
                        ]"
                        outlined
                      >
                      </b-table>
                      <span
                        v-else-if="
                          filter.numProducts.visualizeOptions.length === 1
                        "
                      >
                        {{ filter.numProducts.title }}
                        <br />
                        Only 1 value present:
                        {{ filter.numProducts.visualizeOptions[0].key }}
                      </span>
                    </div>
                  </el-dialog>
                  <b-card class="text-center mt-1 mb-1 chart-card">
                    <span
                      v-b-tooltip.hover.html="
                        tooltipMsg('Download', filter.numProducts.asTable)
                      "
                      class="d-inline-block float-right"
                      tabindex="0"
                    >
                      <b-button
                        :disabled="
                          trialMode ||
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          filter.loading ||
                          filter.numProducts.visualizeOptions.length === 0
                        "
                        class="float-right"
                        size="sm"
                        @click="downloadData(filter.numProducts)"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'download']"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <span
                      v-b-tooltip.hover.html="
                        tooltipMsg('Show', filter.numProducts.asTable)
                      "
                      class="d-inline-block float-right"
                      tabindex="0"
                    >
                      <b-button
                        :disabled="
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          filter.loading ||
                          filter.numProducts.visualizeOptions.length === 0
                        "
                        variant="outline-dark"
                        class="float-right mx-1"
                        size="sm"
                        @click="
                          () => {
                            filter.numProducts.asTable =
                              !filter.numProducts.asTable;
                          }
                        "
                      >
                        <font-awesome-icon
                          :icon="[
                            'fas',
                            filter.numProducts.asTable ? 'chart-bar' : 'table',
                          ]"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <span
                      v-b-tooltip.hover.html="
                        tooltipMsg('Expand', filter.numProducts.asTable)
                      "
                      class="d-inline-block float-right"
                      tabindex="0"
                    >
                      <b-button
                        :disabled="
                          trialMode ||
                          (!searchLoading &&
                            searchResults.length === 0 &&
                            currSearchTerm.length > 0) ||
                          filter.loading ||
                          filter.numProducts.visualizeOptions.length === 0
                        "
                        variant="outline-dark"
                        class="float-right mx-1"
                        size="sm"
                        @click="toggleToFullSizeOthers(filter.numProducts.id)"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'expand-alt']"
                          size="1x"
                        />
                      </b-button>
                    </span>
                    <div
                      v-if="
                        !(
                          !searchLoading &&
                          searchResults.length === 0 &&
                          currSearchTerm.length > 0
                        )
                      "
                    >
                      <b-skeleton-img
                        v-if="filter.loading"
                        height="400px"
                      ></b-skeleton-img>
                      <chart
                        v-else-if="
                          filter.numProducts.visualizeOptions.length > 1 &&
                          !filter.numProducts.asTable
                        "
                        :width="chartWidth"
                        :height="chartHeight"
                        :chartId="filter.numProducts.id"
                        :chartData="filter.numProducts.visualizeOptions"
                        :chartDataSelected="
                          filters[filter.numProducts.name] !== undefined
                            ? filters[filter.numProducts.name].selected
                            : []
                        "
                        :numTopOptions="filter.numProducts.numTopOptions"
                        chartType="non-timeline"
                        :chartTitle="filter.numProducts.title"
                        :chartYLabel="filter.numProducts.yLabel"
                        :chartYDataType="filter.numProducts.yDataType"
                        :chartYDataPerc="filter.numProducts.yDataPerc"
                        @chart-click="
                          selectFilterValue(
                            filter.numProducts.name,
                            ...arguments
                          )
                        "
                      ></chart>
                      <b-table
                        v-else-if="
                          filter.numProducts.visualizeOptions.length > 1 &&
                          filter.numProducts.asTable
                        "
                        class="mt-5 w-100"
                        sticky-header="375px"
                        :items="
                          filter.numProducts.visualizeOptions.slice(0, 100)
                        "
                        :fields="[
                          {
                            key: 'key',
                            label: `${filter.numProducts.xLabel}`,
                          },
                          {
                            key: 'doc_count',
                            label: filter.numProducts.yLabel,
                            formatter: (value) =>
                              filter.numProducts.yDataPerc
                                ? parseFloat(value).toFixed(2) + '%'
                                : value.toLocaleString(),
                          },
                        ]"
                        outlined
                      >
                      </b-table>
                      <span
                        v-else-if="
                          filter.numProducts.visualizeOptions.length === 1
                        "
                      >
                        {{ filter.numProducts.title }}
                        <br />
                        Only 1 value present:
                        {{ filter.numProducts.visualizeOptions[0].key }}
                      </span>
                    </div>
                  </b-card>
                </b-col>
              </b-row>
            </b-container>
          </div>
          <div
            v-else-if="
              (showAnalytics && tabIndex === 1) ||
              (!showAnalytics && tabIndex === 0)
            "
          >
            <b-overlay
              :show="searchLoading"
              :opacity="1.0"
              :blur="loadingBlur"
              no-center
            >
              <template #overlay>
                <div class="mt-5 text-center"></div>
              </template>
              <div
                v-if="searchLoading && searchResults.length === 0"
                class="card mt-3 shadow"
              >
                <div class="card-body loading-card-body"></div>
              </div>
              <div
                v-for="(hit, hitIdx) in searchResults"
                :key="hit._id"
                class="card mt-3 shadow"
              >
                <b-overlay
                  :show="trialMode && hitIdx > 0"
                  :opacity="0.8"
                  :blur="loadingBlur"
                  no-center
                >
                  <template #overlay>
                    <div class="mt-5 text-center">Disabled in trial mode.</div>
                  </template>
                  <div class="card-body search-result-title">
                    <input
                      v-if="show_select"
                      :id="hit._id"
                      v-model="select_download"
                      type="checkbox"
                      :value="hit"
                    />
                    <a
                      :id="'show-full-doc-' + hit._id + '-' + hitIdx"
                      class="show-full-doc"
                      @click="showFullDoc(hit, hitIdx)"
                    >
                      <span v-if="hit._source.doc_title">
                        {{ displayList(hit._source.doc_title) }}
                      </span>
                      <span v-else-if="hit._source.doc_type">
                        {{ displayList(hit._source.doc_type) }}
                      </span>
                      <span v-else-if="hit._source.doc_category">
                        {{ displayList(hit._source.doc_category) }}
                      </span>
                    </a>

                    <a
                      v-if="
                        hit._source.source_url &&
                        !(
                          hit._source.doc_type &&
                          (typeof hit._source.doc_type === 'string' ||
                            hit._source.doc_type instanceof String) &&
                          hit._source.doc_type.search(/archive/i) >= 0
                        )
                      "
                      :href="hit._source.source_url"
                      target="_blank"
                    >
                      <span
                        v-b-tooltip.hover.v-light
                        class="ml-2"
                        style="cursor: pointer"
                        title="Source"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'external-link-alt']"
                          size="1x"
                        />
                      </span>
                    </a>
                    <span v-show="!isLabelSectionSearch">
                      <span
                        v-show="snippetExistsArray[hitIdx]"
                        class="float-right"
                      >
                        <span
                          v-b-tooltip.hover.v-light
                          style="cursor: pointer"
                          title="Saved"
                          @click="
                            !isSavingSnippetLoading &&
                              deleteSnippet(hit, hitIdx)
                          "
                        >
                          <font-awesome-icon
                            :icon="['fas', 'star']"
                            size="1x"
                          />
                        </span>
                        <span
                          v-b-tooltip.hover.v-light
                          class="text-muted save-snippet ml-3"
                          title="Add Notes"
                          @click="
                            !isSavingSnippetLoading && editSnippet(hit, hitIdx)
                          "
                          ><font-awesome-icon
                            :icon="['far', 'file-alt']"
                            size="1x"
                          />
                        </span>
                      </span>
                      <span
                        v-show="!snippetExistsArray[hitIdx]"
                        class="float-right"
                      >
                        <span
                          v-b-tooltip.hover.v-light
                          style="cursor: pointer"
                          title="Save"
                          @click="
                            !isDestroyingSnippetLoading &&
                              saveSnippetQuick(hit, hitIdx)
                          "
                        >
                          <font-awesome-icon
                            :icon="['far', 'star']"
                            size="1x"
                          />
                        </span>
                        <span
                          v-b-tooltip.hover.v-light
                          class="text-muted save-snippet ml-3"
                          title="Add Notes"
                          @click="saveSnippet(hit, hitIdx)"
                          ><font-awesome-icon
                            :icon="['far', 'file-alt']"
                            size="1x"
                          />
                        </span>
                      </span>
                    </span>
                    <span v-if="isCompareDocsUngated" class="float-right mr-2">
                      <b-button
                        v-if="
                          compareDocs.length === 0 ||
                          compareDocs.filter((c) => c.hitIdx !== hitIdx)
                            .length > 0
                        "
                        variant="light"
                        size="sm"
                        @click="addToCompare(hit, hitIdx)"
                      >
                        {{
                          compareDocs.length === 0
                            ? 'Select for Compare'
                            : 'Compare with Selected'
                        }}
                      </b-button>
                      <b-button v-else variant="light" size="sm" disabled>
                        Selected for Compare
                      </b-button>
                    </span>

                    <br />
                    <div class="text-muted search-result-sub-title">
                      <span
                        v-if="
                          hit._source.action_date !== undefined &&
                          hit._source.action_date.length === 10
                        "
                      >
                        {{
                          [hit._source.action_date, 'YYYY-MM-DD']
                            | moment('MMMM D, YYYY')
                        }}<span
                          v-if="
                            hit._source.doc_type === 'Guidance Document' &&
                            hit._source.agency_name === 'FDA'
                          "
                        >
                          (Issue Date)</span
                        ><span
                          v-else-if="
                            hit._source.action_date_type &&
                            hit._source.action_date_type.length > 0
                          "
                        >
                          ({{ hit._source.action_date_type }})</span
                        ><br
                      /></span>
                      {{ displayList(hit._source.doc_category) }}
                      <span
                        v-if="
                          hit._source.doc_category !== undefined &&
                          hit._source.doc_category === 'Guidelines' &&
                          hit._source.doc_type !== undefined &&
                          hit._source.doc_type.length > 0
                        "
                      >
                        -
                        {{ displayList(hit._source.doc_type) }}</span
                      >
                      <span
                        v-if="
                          hit._source.guidance_status !== undefined &&
                          hit._source.guidance_status.length > 0
                        "
                      >
                        ({{ hit._source.guidance_status }})</span
                      >
                      <span
                        v-if="
                          hit._source.doc_sub_category &&
                          hit._source.doc_sub_category.length > 0
                        "
                      >
                        - {{ hit._source.doc_sub_category }}
                      </span>
                      <b-badge
                        v-if="
                          hit._source.doc_type &&
                          (typeof hit._source.doc_type === 'string' ||
                            hit._source.doc_type instanceof String) &&
                          hit._source.doc_type.search(/archive/i) >= 0
                        "
                        style="cursor: default"
                        variant="danger"
                        class="mx-2"
                      >
                        Archive
                      </b-badge>
                      <b-badge
                        v-if="
                          hit._source.guidance_status &&
                          hit._source.guidance_status.search(/draft/i) >= 0
                        "
                        style="cursor: default"
                        variant="warning"
                        class="mx-2"
                      >
                        Draft
                      </b-badge>
                      <br />
                      {{ hit._source.agency_name }}
                      <span
                        v-if="
                          hit._source.agency_division_name &&
                          hit._source.agency_division_name.length > 0
                        "
                      >
                        {{
                          Array.isArray(hit._source.agency_division_name)
                            ? hit._source.agency_division_name.join(', ')
                            : hit._source.agency_division_name
                        }}
                      </span>
                    </div>
                    <span v-if="hit._source.products">
                      <font-awesome-icon
                        :icon="['fas', 'pills']"
                        class="mx-2"
                        size="1x"
                        fixed-width
                      />
                      {{
                        displayList(getUniqueProductNames(hit._source.products))
                      }}
                    </span>
                  </div>
                  <hr />
                  <span
                    v-if="
                      !isLabelSectionSearch &&
                      (!hit.matched_queries ||
                        (hit.matched_queries &&
                          (hit.matched_queries.includes('page_content_match') ||
                            hit.matched_queries.includes(
                              'page_content_fuzzy_match'
                            ))))
                    "
                    class="card-body search-result-body"
                  >
                    {{ hit.inner_hits.by_doc.hits.total.value }}
                    matching
                    <span v-if="hit.inner_hits.by_doc.hits.total.value > 1"
                      >pages</span
                    ><span v-else>page</span>
                  </span>
                  <div
                    v-else-if="
                      !isLabelSectionSearch &&
                      hit.matched_queries &&
                      hit.matched_queries.includes('doc_title_match')
                    "
                    class="card-body search-result-body"
                  >
                    Your search query matched with the document title. No
                    matches found within the document.
                  </div>
                  <div
                    v-if="
                      !hit.matched_queries ||
                      (hit.matched_queries &&
                        (hit.matched_queries.includes('page_content_match') ||
                          hit.matched_queries.includes(
                            'page_content_fuzzy_match'
                          )))
                    "
                    :style="
                      isLabelSectionSearch
                        ? {
                            'max-height': '200px',
                            'overflow-y': 'auto',
                          }
                        : hit.inner_hits.by_doc.hits.total.value > 3
                        ? { height: '200px', 'overflow-y': 'scroll' }
                        : {}
                    "
                    class="card-body search-result-body"
                  >
                    <div
                      v-for="inner_hit in hit.inner_hits.by_doc.hits.hits"
                      :key="inner_hit._id"
                      class="mt-2"
                    >
                      <input
                        v-if="show_select"
                        :id="inner_hit._id"
                        v-model="select_download"
                        type="checkbox"
                        :value="inner_hit"
                      />
                      <!-- match single section -->
                      <span
                        v-if="
                          getFilterTags.filter((f) =>
                            f.val.startsWith('LABEL: ')
                          ).length === 1 &&
                          inner_hit.highlight &&
                          inner_hit.highlight['sections.section_text'] &&
                          inner_hit.highlight['sections.section_text'].length >
                            0
                        "
                      >
                        <!-- eslint-disable -->
                        <span v-for="sectionDict in inner_hit._source.sections">
                          <span
                            v-if="
                              sectionDict['section_name'].includes(
                                getFilterTags
                                  .map((f) => f.val)
                                  .filter((v) => v.startsWith('LABEL: '))[0]
                              )
                            "
                          >
                            <i
                              >Matched
                              {{
                                getFilterTags
                                  .map((f) => f.val)
                                  .filter((v) => v.startsWith('LABEL: '))[0]
                              }}</i
                            >
                            <br />
                            <span
                              class="ml-2"
                              v-for="sectionText in sectionDict['section_text']"
                            >
                              <span
                                class="ml-2"
                                v-for="sentence in processSectionHighlight(
                                  sectionText
                                ).split('<br/>')"
                                v-html="sentence + '<br/>'"
                              ></span>
                            </span>
                          </span>
                        </span>
                        <!-- eslint-enable -->
                      </span>
                      <!-- match multiple sections (need to check which 
                      section is matched)-->
                      <span
                        v-else-if="
                          getFilterTags.filter((f) =>
                            f.val.startsWith('LABEL: ')
                          ).length > 1 &&
                          inner_hit.highlight &&
                          inner_hit.highlight['sections.section_text'] &&
                          inner_hit.highlight['sections.section_text'].length >
                            0
                        "
                      >
                        <!-- eslint-disable -->
                        <span
                          v-for="sectionName in getFilterTags
                            .map((f) => f.val)
                            .filter((v) => v.startsWith('LABEL: '))"
                        >
                          <span
                            v-for="sectionDict in inner_hit._source.sections"
                          >
                            <span
                              v-if="
                                sectionDict['section_name'].includes(
                                  sectionName
                                ) &&
                                sectionDict['section_text'].some((s) =>
                                  processSectionHighlight(s).includes('<mark>')
                                )
                              "
                            >
                              <i>Matched {{ sectionName }}</i>
                              <br />
                              <span
                                class="ml-2"
                                v-for="sectionText in sectionDict[
                                  'section_text'
                                ]"
                              >
                                <span
                                  class="ml-2"
                                  v-for="sentence in processSectionHighlight(
                                    sectionText
                                  ).split('<br/>')"
                                  v-html="sentence + '<br/>'"
                                ></span>
                              </span>
                            </span>
                          </span>
                        </span>
                        <!-- eslint-enable -->
                      </span>
                      <span
                        v-else-if="
                          inner_hit.highlight &&
                          inner_hit.highlight['page_content.original'] &&
                          inner_hit.highlight['page_content.original'].length >
                            0
                        "
                      >
                        <i
                          ><a
                            :id="
                              'show-full-doc-page-' +
                              hit._id +
                              '-' +
                              hitIdx +
                              '-' +
                              inner_hit._source.page_number
                            "
                            class="show-full-doc-page"
                            @click="
                              showFullDoc(
                                hit,
                                hitIdx,
                                inner_hit._source.page_number
                              )
                            "
                            >Page {{ inner_hit._source.page_number }}</a
                          ></i
                        >
                        <!-- eslint-disable -->
                        ...<span
                          v-html="
                            inner_hit.highlight['page_content.original'][0]
                          "
                        ></span
                        >...
                        <!-- eslint-enable -->
                      </span>
                      <span
                        v-else-if="
                          inner_hit.highlight &&
                          inner_hit.highlight.page_content &&
                          inner_hit.highlight.page_content.length > 0
                        "
                      >
                        <i
                          ><a
                            :id="
                              'show-full-doc-page-' +
                              hit._id +
                              '-' +
                              hitIdx +
                              '-' +
                              inner_hit._source.page_number
                            "
                            class="show-full-doc-page"
                            @click="
                              showFullDoc(
                                hit,
                                hitIdx,
                                inner_hit._source.page_number
                              )
                            "
                            >Page {{ inner_hit._source.page_number }}</a
                          ></i
                        >
                        <!-- eslint-disable -->
                        ...<span
                          v-html="
                            processHighlight(
                              inner_hit.highlight.page_content[0]
                            )
                          "
                        ></span
                        >...
                        <!-- eslint-enable -->
                      </span>
                      <span v-else>
                        <i
                          ><a
                            :id="
                              'show-full-doc-page-' +
                              hit._id +
                              '-' +
                              hitIdx +
                              '-' +
                              inner_hit._source.page_number
                            "
                            class="show-full-doc-page"
                            @click="
                              showFullDoc(
                                hit,
                                hitIdx,
                                inner_hit._source.page_number
                              )
                            "
                            >Page {{ inner_hit._source.page_number }}</a
                          ></i
                        >
                        <!-- eslint-disable -->
                        ...
                        <span
                          v-html="
                            inner_hit._source.page_content.slice(
                              inner_hit._source.page_content.indexOf(' ', 100),
                              inner_hit._source.page_content.indexOf(' ', 300)
                            )
                          "
                        ></span
                        >...
                        <!-- eslint-enable -->
                      </span>
                      <!-- <span
                        v-if="
                          inner_hit._source.nct_ids &&
                          inner_hit._source.nct_ids.length > 0
                        "
                      >
                        <br />
                        <b-badge variant="primary">
                          Assocatied Studies:
                        </b-badge>
                        <b-badge
                          variant="light"
                          v-for="nctNum in inner_hit._source.nct_ids"
                          :key="nctNum"
                        >
                          <a
                            :href="
                              'https://clinicaltrials.gov/ct2/show/' + nctNum
                            "
                            target="_blank"
                          >
                            {{ 'NCT' + nctNum }}</a
                          >
                        </b-badge>
                      </span> -->
                    </div>
                  </div>
                  <br />
                  <hr />
                  <div
                    v-if="
                      (hit._source.guidance_topics &&
                        hit._source.guidance_topics.length > 0) ||
                      trialInfoByDoc[hitIdx].length > 0 ||
                      trialInfoByProduct[hitIdx].length > 0
                    "
                    class="card-body search-result-footer"
                  >
                    <!-- trialIdsByDoc[hitIdx].length > 0 || -->
                    <div class="search-result-footer">
                      <div
                        v-if="
                          hit._source.guidance_topics &&
                          hit._source.guidance_topics.length > 1
                        "
                        class="float-left w-30"
                      >
                        Topics:
                        {{ hit._source.guidance_topics.join(', ') }}
                      </div>
                      <div
                        v-else-if="
                          hit._source.guidance_topics &&
                          hit._source.guidance_topics.length === 1
                        "
                        class="float-left w-30"
                      >
                        Topic:
                        {{ hit._source.guidance_topics[0] }}
                      </div>
                      <!-- eslint-disable -->
                      <div class="float-left w-30">
                        <b-button
                          :id="'popover-trials-' + hit._id"
                          class="mb-2"
                          size="sm"
                          variant="light"
                          v-show="
                            trialInfoByDoc[hitIdx].length > 0 ||
                            trialInfoByProduct[hitIdx].length > 0
                          "
                          >See Associated Clinical Studies</b-button
                        >
                        <b-popover
                          :target="'popover-trials-' + hit._id"
                          triggers="hover"
                          placement="bottom"
                          custom-class="w-75"
                        >
                          <b-tabs content-class="mt-3">
                            <b-tab
                              v-if="trialInfoByDoc[hitIdx].length > 0"
                              title="Identified within Document"
                              active
                            >
                              <b-table
                                :items="trialInfoByDoc[hitIdx]"
                                striped
                                hover
                              >
                                <template #cell(study_title)="data">
                                  <span v-html="data.value"></span>
                                </template>
                                <!-- <template #table-colgroup="scope">
                                <col
                                  v-for="field in scope.fields"
                                  :key="field.key"
                                  :style="{
                                    width:
                                      field.key === 'foo' ? '120px' : '180px',
                                  }"
                                />
                              </template> -->
                              </b-table>
                            </b-tab>
                            <b-tab
                              v-if="trialInfoByProduct[hitIdx].length > 0"
                              title="Related to Product"
                            >
                              <b-table
                                :items="trialInfoByProduct[hitIdx]"
                                striped
                                hover
                              >
                                <template #cell(study_title)="data">
                                  <span v-html="data.value"></span>
                                </template>
                                <!-- <template #table-colgroup="scope">
                                <col
                                  v-for="field in scope.fields"
                                  :key="field.key"
                                  :style="{
                                    width:
                                      field.key === 'foo' ? '120px' : '180px',
                                  }"
                                />
                              </template> -->
                              </b-table>
                            </b-tab>
                          </b-tabs>
                        </b-popover>
                        <b-button
                          :id="'popover-endpoints-' + hit._id"
                          class="mb-2 mx-2"
                          size="sm"
                          variant="light"
                          v-show="endpointsByDoc[hitIdx].length > 0"
                          >See Associated Endpoints</b-button
                        >
                        <b-popover
                          :target="'popover-endpoints-' + hit._id"
                          triggers="hover"
                          placement="bottom"
                          custom-class="w-40"
                        >
                          <b-table
                            :items="endpointsByDoc[hitIdx]"
                            striped
                            hover
                          >
                          </b-table>
                        </b-popover>
                      </div>
                      <!-- eslint-enable -->
                    </div>
                  </div>
                </b-overlay>
              </div>
            </b-overlay>
            <div
              v-if="
                !searchLoading && numDocs > 0 && searchOffset + 10 >= numDocs
              "
              class="mt-4 text-center"
            >
              End of Results
            </div>

            <b-pagination
              v-if="numDocs >= 10"
              id="search-result-pagination"
              v-model="currPage"
              class="mt-5"
              :disabled="trialMode"
              :per-page="perPage"
              :total-rows="numDocs"
              :limit="10"
              align="center"
              prev-text="Prev"
              next-text="Next"
              hide-goto-end-buttons
              first-number
              @change="changePage"
            >
              <!-- add to above if needed in dev env -->
              <!-- :last-number="isDev && numDocs <= 10000" -->
            </b-pagination>
            <!-- use if needed in dev env -->
            <!-- <b-container v-if="isDev" class="text-center">
              <b-row v-if="numDocs >= 10" align-h="center">
                <b-col sm="2"> Page No. </b-col>
                <b-col sm="2">
                  <b-form-input
                    id="page-number"
                    v-model="currPageNum"
                    type="number"
                    min="1"
                    :max="numDocs % 10 === 0 ? numDocs / 10 : numDocs / 10 + 1"
                  ></b-form-input>
                </b-col>
                <b-col sm="1">
                  <b-button
                    v-if="currPageNum <= numDocs / 10 + 1 && currPageNum > 0"
                    id="page-number-go"
                    @click="changePage(currPageNum)"
                    >Go</b-button
                  >
                </b-col>
              </b-row>
            </b-container> -->
          </div>
        </div>

        <!-- Full QA Window -->
        <div v-if="showPDFViewerModal">
          <PDFJSViewer
            :selectedDoc="selectedDoc"
            :searchTerm="searchTerm"
            :selectedDocPageNum="selectedDocPageNum"
            :editSnippet="snippetExistsArray[selectedDocIdx]"
            :trialInfoByDoc="trialInfoByDoc[selectedDocIdx]"
            :trialInfoByProduct="trialInfoByProduct[selectedDocIdx]"
            :endpointsByDoc="endpointsByDoc[selectedDocIdx]"
            :endpointsTextByDoc="endpointsTextByDoc[selectedDocIdx]"
            @closePdfViewerModal="closePdfViewerModal"
            @editSnippet="editSnippet(selectedDoc, selectedDocIdx)"
            @deleteSnippet="deleteSnippet(selectedDoc, selectedDocIdx)"
            @saveSnippet="saveSnippet(selectedDoc, selectedDocIdx)"
            @saveSnippetQuick="saveSnippetQuick(selectedDoc, selectedDocIdx)"
          />
        </div>
        <div v-if="showCompareDocsModal">
          <CompareDocsViewer
            :compareDocs="compareDocs"
            @closeCompareDocsModal="closeCompareDocsModal"
          />
        </div>
        <b-modal
          id="analytics-options"
          title="Analytics"
          size="lg"
          hide-footer
          no-fade
        >
          The Analytics tab displays graphs and tables specific to the keywords
          and phrases that you intend to search across the full body of
          regulatory knowledge.
          <br /><br />
          You can click on any of the bars in the graphs to select or deselect
          the filter options, and then click the Apply button on the top of the
          graph. In case of the timeline graphs, you can only choose the bar for
          one year at a time. In case of others, you may choose multiple bars.
          <br /><br />
          There are two groups of analytics:
          <p class="font-weight-bold">1. Database Content</p>
          These correspond to the graphs and tables showing the amount of
          content in our database, grouped by:
          <ul>
            <li>Year in which documents were published</li>
            <li>Indications</li>
            <li>Product Classes</li>
            <li>Companies</li>
            <li>Products</li>
          </ul>
          <p class="font-weight-bold">2. Number of Products</p>
          These correspond to the graphs and tables showing the number of
          products, grouped in the same manner as above.
          <br /><br />
          You may click on the icons on the top right corner of each card to:
          <ul>
            <li>View graphs/tables in <i>expanded</i> full screen view</li>
            <li>Switch between graph and table views</li>
            <li>
              <i>Download</i> graphs as images, or tables as CSV files (which
              can be opened using Excel)
            </li>
          </ul>
        </b-modal>
        <b-modal
          id="search-options"
          title="Search Options"
          size="lg"
          hide-footer
          no-fade
        >
          This tool was developed to search for specific keywords and phrases
          across the full body of knowledge published by the regulatory
          agencies. The returned results will find the specific pages of the
          regulatory documents where the keywords and phrases of interest were
          found.
          <br /><br />
          <p class="font-weight-bold">Default Search</p>
          By default, the results will include pages that contain all the
          keywords and phrases. By default, these are sorted by
          <i>Most Relevant</i>, which means preference is given to recent
          documents with pages with highest relevancy to all the keywords and
          phrases. You can also choose to sort solely by <i>Best Match</i> or
          solely by <i>Most Recent</i>. <br /><br />
          <p class="font-weight-bold">Advanced Search</p>
          <ul>
            <li>
              Use the <code>|</code> pipe symbol to search for pages that
              contain AT LEAST ONE of the keywords or phrases.<br />Example:
              <code>Immunogenicity | assay</code>
            </li>
            <br />
            <li>
              Use the <code>+</code> plus symbol to search for pages that
              contain ALL keywords and phrases. <br />Example:
              <code>Immunogenicity + assay</code>
              <br />
              <i
                >Note: This is the default behavior. So, it is equivalent to not
                using any symbol.</i
              >
            </li>
            <br />
            <li>
              Use the <code>-</code> minus symbol to EXCLUDE pages with specific
              keywords or phrases. <br />Example:
              <code>Immunogenicity -test</code>
              <br />
              <i
                >Note: There should be a space before <code>-</code>, but no
                space between <code>-</code> and the keyword or phrase you want
                to exclude.</i
              >
            </li>
            <br />
            <li>
              Use the <code>" "</code> quotation marks to find pages with EXACT
              matches to a phrase. <br />Example:
              <code>"anti drug antibody"</code>
            </li>
            <br />
            <li>
              Use the <code>( )</code> brackets to combine keywords, phrases and
              operators. <br />Example:
              <code>immunogenicity+(test | assay)</code>
            </li>
          </ul>
        </b-modal>
        <b-modal
          id="filter-options"
          title="Filter Options"
          size="lg"
          hide-footer
          no-fade
        >
          You can use multiple document filters to narrow down your search
          results, including a date range filter. Each filter contains multiple
          options, displayed along with the number of matching pages for that
          option.
          <br /><br />
          For example, the Agency filter may have the following option:
          <code>FDA</code>
          <b-badge variant="light">42,731</b-badge>
          <br />
          This means that there 42,731 matching pages coming from the
          <code>FDA</code>. <br /><br />
          These filters can help you to:
          <ol>
            <li>
              Reduce the number of search results matching a search query.<br />
              As a regulatory research platform, the objective is to search the
              complete body of knowledge published by the agencies. The
              specificity of the results will greatly increase when the filters
              are used.
            </li>
            <li>
              Determine where the matching results are found across the
              database<br />
              The filter options change with every search query, when filter
              options are selected, and when the page filters are activated. The
              number of matching pages is indicated next to every search option.
            </li>
          </ol>

          <span class="font-weight-bold">Additional Notes</span>
          <ul>
            <li>
              The “Document Category” filter is a grouping based on the source
              of the documents while the “Document Type” filter is a grouping
              based on the content of each document.
            </li>
            <li>
              Some filters include thousands of options. For these, a filter
              search bar is provided with the ability to “select all” search
              results and “clear all” selections. The applied filters are also
              indicated below the main search bar.
            </li>
            <li>
              The "Product Class" filter covers multiple classifications
              indicated by the three letter acronym preceding each option:<br />
              EPC = Established Pharmacologic Class<br />
              MoA = Mechanism of Action<br />
              SC = Structural Class<br />
              TC = Therapeutic Category
            </li>
          </ul>
        </b-modal>
        <b-modal
          id="page-filter-options"
          title="Page Filter Options"
          size="lg"
          hide-footer
          no-fade
        >
          Page Filters can help you find a specific category of information
          based on the type of content present on a page. You can choose one of
          the following options.
          <br /><br />
          <ul>
            <li>
              <span class="font-weight-bold">All Content</span><br />
              Search all content in our database
            </li>
            <li>
              <span class="font-weight-bold">Efficacy Endpoints</span><br />
              Clinical endpoints used to assess efficacy
            </li>
            <li>
              <span class="font-weight-bold">Inclusion/Exclusion Criteria</span
              ><br />
              Find study eligibility criteria
            </li>
            <li>
              <span class="font-weight-bold">Clinical Trials</span><br />
              Clinical studies that supported approval
            </li>
            <li>
              <span class="font-weight-bold">Platform Trials</span><br />
              Basket and umbrella studies in past approvals
            </li>
            <li>
              <span class="font-weight-bold">Single-Arm Studies</span><br />
              Single-arm studies that supported approval
            </li>
            <li>
              <span class="font-weight-bold">Biomarkers</span><br />
              Diagnostic, predictive and prognostic pharmacogenomic biomarkers
            </li>
            <li>
              <span class="font-weight-bold">Accelerated Approval</span><br />
              Studies and endpoints related to accelerated approval
            </li>
            <li>
              <span class="font-weight-bold">Surrogate Endpoints</span><br />
              Surrogate endpoints supporting accelerated and traditional
              approvals
            </li>
            <li>
              <span class="font-weight-bold">Expedited Review</span><br />
              Discussion and confirmation of expedited review status
            </li>
            <li>
              <span class="font-weight-bold"
                >Real World Evidence/Data (RWE/RWD)</span
              ><br />
              Real World Evidence/Data that supported approval
            </li>
            <li>
              <span class="font-weight-bold"
                >Patient Reported Outcomes (PRO) Endpoints</span
              ><br />
              Patient Reported Outcome instruments used as endpoints
            </li>
            <li>
              <span class="font-weight-bold">Missed Endpoints</span><br />
              Approvals despite failed endpoints
            </li>
            <li>
              <span class="font-weight-bold">Pediatric Studies</span><br />
              Pediatric clinical studies and plans
            </li>
            <li>
              <span class="font-weight-bold">Bioequivalence Studies</span><br />
              Bioequivalence studies and tests
            </li>
            <li>
              <span class="font-weight-bold"
                >Post Marketing Commitments & Requirements (PMC/PMR)</span
              ><br />
              Post Marketing Commitments & Requirements
            </li>
            <li>
              <span class="font-weight-bold">CMC-related PMC/PMR</span><br />
              CMC related PMC/PMRs
            </li>
            <li>
              <span class="font-weight-bold"
                >Risk Evaluation and Mitigation Strategy (REMS)</span
              ><br />
              Risk Evaluation & Mitigation Strategies
            </li>
            <li>
              <span class="font-weight-bold">Safety Profile</span><br />
              Adverse events associated with approved products
            </li>
            <li>
              <span class="font-weight-bold">Regulatory History</span><br />
              List of regulatory events leading up to approval
            </li>
            <li>
              <span class="font-weight-bold">Regulatory Interactions</span
              ><br />
              Applicant-agency exchanges and Q&amp;As
            </li>
            <li>
              <span class="font-weight-bold">Reviewer Feedback</span><br />
              Highlighted comments by FDA reviewers
            </li>
            <li>
              <span class="font-weight-bold">Quality Attributes</span><br />
              Quality assessments supporting approval
            </li>
            <li>
              <span class="font-weight-bold">Formulation</span><br />
              Product composition and formulation
            </li>
            <li>
              <span class="font-weight-bold">Product Specification</span><br />
              Specification tests, methods and acceptance criteria
            </li>
            <li>
              <span class="font-weight-bold">Dissolution Method</span><br />
              Dissolution methods and results
            </li>
            <li>
              <span class="font-weight-bold">Nitrosamines</span><br />
              Search content related to nitrosamines
            </li>
            <li>
              <span class="font-weight-bold">Deficiencies</span><br />
              List of identified deficiencies
            </li>
            <li>
              <span class="font-weight-bold">Racial/Ethnic Minorities</span
              ><br />
              Discussion on racial and ethnic representation
            </li>
            <li>
              <span class="font-weight-bold">Drug-Drug Interactions</span><br />
              Discussion on drug-drug interactions
            </li>
          </ul>
        </b-modal>
      </div>
      <DeleteSearch :bookmarkToDelete="bookmarkToDelete" />
    </div>
  </div>
</template>

<script>
import ESService from '@/services/ESService.js';
import { Auth, Analytics, API, graphqlOperation } from 'aws-amplify';
import { getUserAsync } from '../utils/getUserAsync.js';
import { getUserVoteEntry, getUserFlagEntry } from '@/graphql/queries';
import { createUserFlagEntry, deleteUserFlagEntry } from '@/graphql/mutations';
import CheckboxFilter from '@/components/CheckboxFilter.vue';
import Chart from '@/components/Chart.vue';
import PDFJSViewer from '@/components/PDFJSViewer.vue';
import CompareDocsViewer from '@/components/CompareDocsViewer.vue';
import {
  isCompareDocsUngated,
  isSuggestFiltersUngated,
} from '@/constants/gates';
import DeleteSearch from '@/components/modals/DeleteSearch';
import { mapGetters } from 'vuex';
import { eventBus } from '../main';
import { areArraysEqual } from '@/utils/arrayUtils';
import { logError } from '@/utils/loggerUtils.js';
import { getUniqueProductNames } from '@/operators/searchModifiers';

export default {
  name: 'ProjectSearchDocs',
  components: {
    'checkbox-filter': CheckboxFilter,
    chart: Chart,
    PDFJSViewer,
    CompareDocsViewer,
    DeleteSearch,
  },
  props: {
    searchMode: String,
    productsToSearch: Array,
    componentRole: String,
    organizedFilter: Object,
    query: String,
  },
  data() {
    return {
      activeSearch: null,
      pageName: 'searchDocs',
      tabIndex: 0,
      fullSizeChartVisible: false,
      currentChart: '',
      isCollapse: false,
      sidebarStyle: {
        width: '25%',
        padding: '20px',
        overflow: 'hidden',
      },
      searchResultStyle: {
        marginLeft: '20vw',
        marginRight: '20vw',
      },
      filterContentStyle: {
        border: '1px solid rgba(0,0,0,.125)',
        width: '100%',
      },
      searchResultsStyle: {
        width: '70%',
      },

      topicsLoading: true,
      topics: [],
      chartLabels: [],
      chartValues: [],
      search_saved: false,
      searchLoading: false,
      searchTerm: '', // Default search term
      currSearchTerm: '',
      endpointSearchTerm: '',
      searchDebounce: null, // Timeout for search bar debounce
      searchResults: [], // Displayed search results
      downloading: false,
      downloadSearchResults: [], // Search Results for Download
      select_download: [],
      show_select: false,
      numHits: null, // Total search results found
      numDocs: null, // Total documents found
      numProducts: null, // Total products found
      searchOffset: 0, // Search result pagination offset
      selectedDoc: {}, // Selected QA
      selectedDocPageNum: 1,
      selectedDocIdx: null,
      searchError: false,
      filterError: false,
      isDowntime: process.env.VUE_APP_DOWNTIME === 'true',
      isDev: process.env.VUE_APP_PROD !== 'true',

      compareDocs: [],
      showCompareDocsModal: false,

      drug_link: undefined,
      bla_link: undefined,

      currPage: 1,
      currPageNum: 1,
      perPage: 10,

      filters: {
        docIds: {
          group: 'Documents',
          search: false,
          loading: false,
          selected: [],
          numPages: {
            id: 'doc-ids',
            label: 'Document IDs',
            checkbox: false,
            visualize: false,
            options: [],
            visualizeOptions: [],
            name: 'docIds',
          },
        },
        productName: {
          group: 'Products',
          search: true,
          loading: true,
          selected: [],
          numPages: {
            id: 'product-name',
            label: 'Product Name',
            checkbox: true,
            visualize: true,
            options: [],
            visualizeOptions: [],
            name: 'productName',
            title: 'Top Products',
            xLabel: 'Product',
            yLabel: '% of Matched Results',
            yDataType: 'float',
            yDataPerc: true,
            asTable: false,
            numTopOptions: 10,
          },
        },
        productDosageForm: {
          group: 'Products',
          search: true,
          loading: true,
          selected: [],
          numPages: {
            id: 'product-dosage-form',
            label: 'Dosage Form',
            checkbox: true,
            visualize: false,
            options: [],
            visualizeOptions: [],
            name: 'productDosageForm',
          },
        },
        productDosageRoute: {
          group: 'Products',
          search: true,
          loading: true,
          selected: [],
          numPages: {
            id: 'product-dosage-route',
            label: 'Dosage Route',
            checkbox: true,
            visualize: true,
            options: [],
            visualizeOptions: [],
            name: 'productDosageRoute',
          },
        },
        genericName: {
          search: true,
          loading: false,
          selected: [],
          numPages: {
            id: 'generic-name',
            name: 'genericName',
            label: 'Active Ingredient',
            checkbox: false,
            visualize: false,
            options: [],
          },
        },
        brandName: {
          search: true,
          loading: false,
          selected: [],
          numPages: {
            id: 'brand-name',
            name: 'brandName',
            label: 'Brand Name',
            checkbox: false,
            visualize: false,
            options: [],
          },
        },
        productClass: {
          group: 'Products',
          search: true,
          loading: true,
          selected: [],
          numPages: {
            id: 'product-class',
            label: 'Product Class',
            checkbox: true,
            visualize: true,
            options: [],
            visualizeOptions: [],
            name: 'productClass',
            title: 'Top Product Classes',
            xLabel: 'Product Class',
            yLabel: '% of Matched Results',
            yDataType: 'float',
            yDataPerc: true,
            asTable: false,
            numTopOptions: 10,
          },
          numProducts: {
            id: 'products-by-class',
            label: 'Products by Class',
            checkbox: false,
            visualize: true,
            options: [],
            visualizeOptions: [],
            name: 'productClass',
            title: 'No. of Products by Class',
            xLabel: 'Product Class',
            yLabel: 'No. of Products',
            yDataType: 'int',
            yDataPerc: false,
            asTable: false,
            numTopOptions: 10,
          },
        },
        companyName: {
          group: 'Products',
          search: true,
          loading: true,
          selected: [],
          numPages: {
            id: 'company-name',
            label: 'Company Name',
            checkbox: true,
            visualize: true,
            options: [],
            visualizeOptions: [],
            name: 'companyName',
            title: 'Top Companies',
            xLabel: 'Company',
            yLabel: '% of Matched Results',
            yDataType: 'float',
            yDataPerc: true,
            asTable: false,
            numTopOptions: 10,
          },
          numProducts: {
            id: 'products-by-company-name',
            label: 'Products by Company',
            checkbox: false,
            visualize: true,
            options: [],
            visualizeOptions: [],
            name: 'companyName',
            title: 'No. of Products by Company',
            xLabel: 'Company',
            yLabel: 'No. of Products',
            yDataType: 'int',
            yDataPerc: false,
            asTable: false,
            numTopOptions: 10,
          },
        },
        productSubset: {
          group: 'Products',
          search: false,
          loading: true,
          selected: [],
          numPages: {
            id: 'product-subset',
            name: 'productSubset',
            label: 'Product Subset',
            checkbox: true,
            visualize: false,
            options: [],
          },
        },
        endpoint: {
          group: 'Products',
          search: false,
          loading: true,
          selected: [],
          numPages: {
            id: 'endpoint',
            label: 'Endpoint',
            checkbox: false,
            visualize: false,
            options: [],
            visualizeOptions: [],
            name: 'endpoint',
            title: 'Top Endpoints',
            xLabel: 'Endpoint',
            yLabel: '% of Matched Results',
            yDataType: 'float',
            yDataPerc: true,
            asTable: false,
            numTopOptions: 10,
          },
          numProducts: {
            id: 'products-by-endpoint',
            label: 'Products by Endpoint',
            checkbox: false,
            visualize: false,
            options: [],
            visualizeOptions: [],
            name: 'endpoint',
            title: 'No. of Products by Endpoint',
            xLabel: 'Endpoint',
            yLabel: 'No. of Products',
            yDataType: 'int',
            yDataPerc: false,
            asTable: false,
            numTopOptions: 10,
          },
        },
        manufacturingSite: {
          group: 'Products',
          search: true,
          loading: true,
          selected: [],
          numPages: {
            id: 'manufacturing-site',
            label: 'Manufacturing Site',
            checkbox: false,
            visualize: false,
            options: [],
            visualizeOptions: [],
            name: 'manufacturingSite',
            title: 'Top Manufacturing Sites',
            xLabel: 'Manufacturing Site',
            yLabel: '% of Matched Results',
            yDataType: 'float',
            yDataPerc: true,
            asTable: false,
            numTopOptions: 10,
          },
          numProducts: {
            id: 'products-by-manufacturing-site',
            label: 'Products by Manufacturing Site',
            checkbox: false,
            visualize: false,
            options: [],
            visualizeOptions: [],
            name: 'manufacturingSite',
            title: 'No. of Products by Manufacturing Site',
            xLabel: 'Manufacturing Site',
            yLabel: 'No. of Products',
            yDataType: 'int',
            yDataPerc: false,
            asTable: false,
            numTopOptions: 10,
          },
        },
        productType: {
          group: 'Products',
          search: true,
          loading: true,
          selected: [],
          numPages: {
            id: 'product-type',
            name: 'productType',
            label: 'Product Type',
            checkbox: true,
            visualize: false,
            options: [],
          },
        },
        therapeuticArea: {
          group: 'Medical Conditions',
          search: true,
          loading: true,
          selected: [],
          numPages: {
            id: 'therapeutic-area',
            label: 'Therapeutic Area',
            checkbox: true,
            visualize: true,
            options: [],
            visualizeOptions: [],
            name: 'therapeuticArea',
            title: 'Top Therapeutic Areas',
            xLabel: 'Therapeutic Area',
            yLabel: '% of Matched Results',
            yDataType: 'float',
            yDataPerc: true,
            asTable: false,
            numTopOptions: 10,
          },
          numProducts: {
            id: 'products-by-therapeutic-area',
            label: 'Products by Therapeutic Area',
            checkbox: false,
            visualize: true,
            options: [],
            visualizeOptions: [],
            name: 'therapeuticArea',
            title: 'No. of Products by Therapeutic Area',
            xLabel: 'Therapeutic Area',
            yLabel: 'No. of Products',
            yDataType: 'int',
            yDataPerc: false,
            asTable: false,
            numTopOptions: 10,
          },
        },
        indication: {
          group: 'Medical Conditions',
          search: true,
          loading: true,
          selected: [],
          numPages: {
            id: 'indication',
            label: 'Associated Medical Conditions',
            checkbox: true,
            visualize: true,
            options: [],
            visualizeOptions: [],
            name: 'indication',
            title: 'Top Indications',
            xLabel: 'Indication',
            yLabel: '% of Matched Results',
            yDataType: 'float',
            yDataPerc: true,
            asTable: false,
            numTopOptions: 10,
          },
          numProducts: {
            id: 'products-by-indication',
            label: 'Products by Indication / Off-Label Use',
            checkbox: false,
            visualize: true,
            options: [],
            visualizeOptions: [],
            name: 'indication',
            title: 'No. of Products by Indication',
            xLabel: 'Indication',
            yLabel: 'No. of Products',
            yDataType: 'int',
            yDataPerc: false,
            asTable: false,
            numTopOptions: 10,
          },
        },
        biomarker: {
          group: 'Medical Conditions',
          search: true,
          loading: true,
          selected: [],
          numPages: {
            id: 'biomarker',
            name: 'biomarker',
            label: 'Biomarker',
            checkbox: true,
            visualize: false,
            options: [],
          },
        },
        biomarkerCategory: {
          group: 'Medical Conditions',
          search: false,
          loading: true,
          selected: [],
          numPages: {
            id: 'biomarker-category',
            name: 'biomarkerCategory',
            label: 'Biomarker Category',
            checkbox: true,
            visualize: false,
            options: [],
          },
        },
        drugDesignationOrProgram: {
          group: 'Regulatory Process',
          search: false,
          loading: true,
          selected: [],
          numPages: {
            id: 'drug-designation-or-program',
            name: 'drugDesignationOrProgram',
            label: 'Special Designations, Reviews & Approvals',
            checkbox: true,
            visualize: false,
            options: [],
          },
        },
        applicationType: {
          group: 'Regulatory Process',
          search: false,
          loading: true,
          selected: [],
          numPages: {
            id: 'application-type',
            name: 'applicationType',
            label: 'Application Type',
            checkbox: true,
            visualize: false,
            options: [],
          },
        },
        docSubCategory: {
          group: 'Regulatory Process',
          search: false,
          loading: true,
          selected: [],
          numPages: {
            id: 'approval-type',
            name: 'docSubCategory',
            label: 'Approval Type',
            checkbox: true,
            visualize: false,
            options: [],
          },
        },
        submissionType: {
          group: 'Regulatory Process',
          search: false,
          loading: true,
          selected: [],
          numPages: {
            id: 'submission-type',
            name: 'submissionType',
            label: 'Submission Type',
            checkbox: true,
            visualize: false,
            options: [],
          },
        },
        pmcRequiredUnder: {
          group: 'Regulatory Process',
          search: false,
          loading: true,
          selected: [],
          numPages: {
            id: 'pmc-required-under',
            name: 'pmcRequiredUnder',
            label: 'Commitments, Requirements and Restrictions',
            checkbox: true,
            visualize: false,
            options: [],
          },
        },
        dbSource: {
          group: 'Databases',
          search: false,
          loading: true,
          selected: [],
          numPages: {
            id: 'db-source',
            name: 'dbSource',
            label: '',
            checkbox: true,
            visualize: false,
            options: [],
          },
        },
        docCategory: {
          group: 'Documents',
          search: false,
          loading: true,
          selected: [],
          numPages: {
            id: 'document-category',
            name: 'docCategory',
            label: 'Document Category',
            checkbox: true,
            visualize: false,
            options: [],
          },
        },
        docType: {
          group: 'Documents',
          search: true,
          loading: true,
          selected: [],
          numPages: {
            id: 'document-type',
            name: 'docType',
            label: 'Document Type',
            checkbox: true,
            visualize: false,
            options: [],
          },
        },
        docSubType: {
          group: 'Documents',
          search: true,
          loading: true,
          selected: [],
          numPages: {
            id: 'document-sub-type',
            name: 'docSubType',
            label: 'Document Format',
            checkbox: true,
            visualize: false,
            options: [],
          },
        },
        sectionName: {
          group: 'Documents',
          search: true,
          loading: true,
          selected: [],
          numPages: {
            id: 'section-name',
            name: 'sectionName',
            label: 'Section',
            checkbox: true,
            visualize: false,
            options: [],
          },
        },
        guidanceTopic: {
          group: 'Documents',
          search: true,
          loading: true,
          selected: [],
          numPages: {
            id: 'guidance-topic',
            name: 'guidanceTopic',
            label: 'Guidance Topic',
            checkbox: true,
            visualize: false,
            options: [],
          },
        },
        guidanceStatus: {
          group: 'Documents',
          search: false,
          loading: true,
          selected: [],
          numPages: {
            id: 'guidance-status',
            name: 'guidanceStatus',
            label: 'Guidance Status',
            checkbox: true,
            visualize: false,
            options: [],
          },
        },
        regAgency: {
          group: 'Agencies',
          search: false,
          loading: true,
          selected: [],
          numPages: {
            id: 'agency',
            name: 'regAgency',
            label: 'Agency',
            checkbox: true,
            visualize: false,
            options: [],
          },
        },
        regAgencyDivision: {
          group: 'Agencies',
          search: true,
          loading: true,
          selected: [],
          numPages: {
            id: 'agency-division',
            name: 'regAgencyDivision',
            label: 'Agency Division/Office',
            checkbox: true,
            visualize: false,
            options: [],
          },
        },
        timeline: {
          search: false,
          loading: true,
          selected: [],
          type: 'timeline',
          numPages: {
            id: 'timeline',
            label: 'Timeline',
            checkbox: false,
            visualize: true,
            options: [],
            visualizeOptions: [],
            name: 'timeline',
            title: 'Database Content over Time',
            xLabel: 'Year',
            yLabel: '% of Matched Results',
            yDataType: 'float',
            yDataPerc: true,
            asTable: false,
            numTopOptions: 25,
          },
          numProducts: {
            id: 'products-timeline',
            label: 'Products Timeline',
            checkbox: false,
            visualize: true,
            options: [],
            visualizeOptions: [],
            name: 'timeline',
            title: 'No. of Products over Time',
            xLabel: 'Year',
            yLabel: 'No. of Products',
            yDataType: 'int',
            yDataPerc: false,
            asTable: false,
            numTopOptions: 25,
          },
        },
      },

      collapsibleFilterGroups: {
        Databases: {
          visible: false,
        },
        Products: {
          visible: false,
        },
        'Medical Conditions': {
          visible: false,
        },
        // Biomarkers: {
        //   visible: false,
        // },
        'Regulatory Process': {
          visible: false,
        },
        Documents: {
          visible: false,
        },
        Agencies: {
          visible: false,
        },
        Dates: {
          visible: false,
        },
      },

      // action date filters
      actionDateStartValue: '',
      actionDateEndValue: '',
      minActionDate: '',
      maxActionDate: '',

      searchModes: [
        {
          id: 'all-content',
          mode: 'All Content',
          description:
            'All Content<br/><small class="text-secondary">Search all content in our database</small>',
        },
        {
          id: 'efficacy-endpoints',
          mode: 'Efficacy Endpoints',
          description:
            'Efficacy Endpoints<br/><small class="text-secondary">Clinical endpoints used to assess efficacy</small>',
        },
        {
          id: 'safety-endpoints',
          mode: 'Safety Endpoints',
          description:
            'Safety Endpoints<br/><small class="text-secondary">Clinical endpoints used to assess safety</small>',
        },
        {
          id: 'efficacy-endpoints-exp',
          mode: 'Efficacy Endpoints (Experimental)',
          description:
            'Efficacy Endpoints <small class="text-success font-weight-bold">(Experimental)</small><br/><small class="text-secondary">Clinical endpoints used to assess efficacy</small>',
        },
        {
          id: 'inclusion-exclusion-criteria',
          mode: 'Inclusion/Exclusion Criteria',
          description:
            'Inclusion/Exclusion Criteria<br/><small class="text-secondary">Find study eligibility criteria</small>',
        },
        {
          id: 'clinical-trials',
          mode: 'Clinical Trials',
          description:
            'Clinical Trials<br/><small class="text-secondary">Clinical studies that supported approval</small>',
        },
        {
          id: 'clinical-trials-exp',
          mode: 'Clinical Trials (Experimental)',
          description:
            'Clinical Trials <small class="text-success font-weight-bold">(Experimental)</small><br/><small class="text-secondary">Clinical studies that supported approval</small>',
        },
        {
          id: 'platform-trials',
          mode: 'Platform Trials',
          description:
            'Platform Trials<br/><small class="text-secondary">Basket and umbrella studies in past approvals</small>',
        },
        {
          id: 'single-arm-studies',
          mode: 'Single-Arm Studies',
          description:
            'Single-Arm Studies<br/><small class="text-secondary">Single-arm studies that supported approval</small>',
        },
        {
          id: 'single-arm-studies-exp',
          mode: 'Single-Arm Studies (Experimental)',
          description:
            'Single-Arm Studies<small class="text-success font-weight-bold">(Experimental)</small><br/><small class="text-secondary">Single-arm studies that supported approval</small>',
        },
        {
          id: 'biomarkers',
          mode: 'Biomarkers',
          description:
            'Biomarkers<br/><small class="text-secondary">Diagnostic, predictive and prognostic pharmacogenomic biomarkers</small>',
        },
        {
          id: 'biomarkers-exp',
          mode: 'Biomarkers (Experimental)',
          description:
            'Biomarkers <small class="text-success font-weight-bold">(Experimental)</small><br/><small class="text-secondary">Biomarkers related content as predicted by our AI.</small>',
        },
        {
          id: 'accelerated-approval',
          mode: 'Accelerated Approval',
          description:
            'Accelerated Approval<br/><small class="text-secondary">Studies and endpoints related to accelerated approval</small>',
        },
        {
          id: 'surrogate-endpoints',
          mode: 'Surrogate Endpoints',
          description:
            'Surrogate Endpoints<br/><small class="text-secondary">Surrogate endpoints supporting accelerated and traditional approvals</small>',
        },
        {
          id: 'expedited-review',
          mode: 'Expedited Review',
          description:
            'Expedited Review<br/><small class="text-secondary">Discussion and confirmation of expedited review status</small>',
        },
        {
          id: 'rwe-rwd',
          mode: 'RWE/RWD',
          description:
            'Real World Evidence/Data (RWE/RWD)<br/><small class="text-secondary">Real World Evidence/Data that supported approval</small>',
        },
        {
          id: 'rwe-rwd-exp',
          mode: 'RWE/RWD (Experimental)',
          description:
            'RWE/RWD <small class="text-success font-weight-bold">(Experimental)</small><br/><small class="text-secondary">Real World Evidence/Data related content as predicted by our AI.</small>',
        },
        {
          id: 'pro-endpoints',
          mode: 'PRO Endpoints',
          description:
            'Patient Reported Outcomes (PRO) Endpoints<br/><small class="text-secondary">Patient Reported Outcome instruments used as endpoints</small>',
        },
        {
          id: 'pro-endpoints-exp',
          mode: 'PRO Endpoints (Experimental)',
          description:
            'PRO Endpoints <small class="text-success font-weight-bold">(Experimental)</small><br/><small class="text-secondary">Patient Reported Outcome instruments used as endpoints</small>',
        },
        {
          id: 'missed-endpoints',
          mode: 'Missed Endpoints',
          description:
            'Missed Endpoints<br/><small class="text-secondary">Approvals despite failed endpoints</small>',
        },
        {
          id: 'missed-endpoints-exp',
          mode: 'Missed Endpoints (Experimental)',
          description:
            'Missed Endpoints <small class="text-success font-weight-bold">(Experimental)</small><br/><small class="text-secondary">Approvals despite failed endpoints</small>',
        },
        {
          id: 'pediatric-studies',
          mode: 'Pediatric Studies',
          description:
            'Pediatric Studies<br/><small class="text-secondary">Pediatric clinical studies and plans</small>',
        },
        {
          id: 'pediatric-studies-exp',
          mode: 'Pediatric Studies (Experimental)',
          description:
            'Pediatric Studies <small class="text-success font-weight-bold">(Experimental)</small><br/><small class="text-secondary">Pediatric clinical studies and plans</small>',
        },
        {
          id: 'bioequivalence-studies',
          mode: 'Bioequivalence Studies',
          description:
            'Bioequivalence Studies<br/><small class="text-secondary">Bioequivalence studies and tests</small>',
        },
        {
          id: 'pmc-pmr',
          mode: 'PMC/PMR',
          description:
            'Post Marketing Commitments & Requirements (PMC/PMR)<br/><small class="text-secondary">Post Marketing Commitments & Requirements</small>',
        },
        {
          id: 'pmc-pmr-exp',
          mode: 'PMC/PMR (Experimental)',
          description:
            'PMC/PMR <small class="text-success font-weight-bold">(Experimental)</small><br/><small class="text-secondary">Post Marketing Commitments & Requirements</small>',
        },
        {
          id: 'cmc-pmc-pmr',
          mode: 'CMC-related PMC/PMR',
          description:
            'CMC-related PMC/PMR<br/><small class="text-secondary">CMC-related PMC/PMRs</small>',
        },
        {
          id: 'rems',
          mode: 'REMS',
          description:
            'Risk Evaluation and Mitigation Strategy (REMS)<br/><small class="text-secondary">Risk Evaluation & Mitigation Strategies</small>',
        },
        {
          id: 'rems-exp',
          mode: 'REMS (Experimental)',
          description:
            'REMS <small class="text-success font-weight-bold">(Experimental)</small><br/><small class="text-secondary">Risk Evaluation & Mitigation Strategies</small>',
        },
        {
          id: 'safety-profile',
          mode: 'Safety Profile',
          description:
            'Safety Profile<br/><small class="text-secondary">Adverse events associated with approved products</small>',
        },
        {
          id: 'safety-profile-exp',
          mode: 'Safety Profile (Experimental)',
          description:
            'Safety Profile <small class="text-success font-weight-bold">(Experimental)</small><br/><small class="text-secondary">Adverse events associated with approved products</small>',
        },
        {
          id: 'regulatory-history',
          mode: 'Regulatory History',
          description:
            'Regulatory History<br/><small class="text-secondary">List of regulatory events leading up to approval</small>',
        },
        {
          id: 'regulatory-history-exp',
          mode: 'Regulatory History (Experimental)',
          description:
            'Regulatory History <small class="text-success font-weight-bold">(Experimental)</small><br/><small class="text-secondary">List of regulatory events leading up to approval</small>',
        },
        {
          id: 'regulatory-interactions',
          mode: 'Regulatory Interactions',
          description:
            'Regulatory Interactions<br/><small class="text-secondary">Applicant-agency exchanges and Q&As</small>',
        },
        {
          id: 'regulatory-interactions-exp',
          mode: 'Regulatory Interactions (Experimental)',
          description:
            'Regulatory Interactions <small class="text-success font-weight-bold">(Experimental)</small><br/><small class="text-secondary">Applicant-agency exchanges and Q&As</small>',
        },
        {
          id: 'reviewer-feedback',
          mode: 'Reviewer Feedback',
          description:
            'Reviewer Feedback<br/><small class="text-secondary">Highlighted comments by FDA reviewers</small>',
        },
        {
          id: 'reviewer-feedback-exp',
          mode: 'Reviewer Feedback (Experimental)',
          description:
            'Reviewer Feedback <small class="text-success font-weight-bold">(Experimental)</small><br/><small class="text-secondary">Highlighted comments by FDA reviewers</small>',
        },
        {
          id: 'quality-attributes',
          mode: 'Quality Attributes',
          description:
            'Quality Attributes<br/><small class="text-secondary">Quality assessments supporting approval</small>',
        },
        {
          id: 'quality-attributes-exp',
          mode: 'Quality Attributes (Experimental)',
          description:
            'Quality Attributes <small class="text-success font-weight-bold">(Experimental)</small><br/><small class="text-secondary">Quality assessments supporting approval</small>',
        },
        {
          id: 'formulation',
          mode: 'Formulation',
          description:
            'Formulation<br/><small class="text-secondary">Product composition and formulation</small>',
        },
        {
          id: 'formulation-exp',
          mode: 'Formulation (Experimental)',
          description:
            'Formulation <small class="text-success font-weight-bold">(Experimental)</small><br/><small class="text-secondary">Product composition and formulation</small>',
        },
        {
          id: 'product-specification',
          mode: 'Product Specification',
          description:
            'Product Specification<br/><small class="text-secondary">Specification tests, methods and acceptance criteria</small>',
        },
        {
          id: 'product-specification-exp',
          mode: 'Product Specification (Experimental)',
          description:
            'Product Specification <small class="text-success font-weight-bold">(Experimental)</small><br/><small class="text-secondary">Specification tests, methods and acceptance criteria</small>',
        },
        {
          id: 'dissolution-method',
          mode: 'Dissolution Method',
          description:
            'Dissolution Method<br/><small class="text-secondary">Dissolution methods and results</small>',
        },
        {
          id: 'nitrosamines',
          mode: 'Nitrosamines',
          description:
            'Nitrosamines<br/><small class="text-secondary">Search content related to nitrosamines</small>',
        },
        {
          id: 'deficiencies',
          mode: 'Deficiencies',
          description:
            'Deficiencies<br/><small class="text-secondary">List of identified deficiencies</small>',
        },
        {
          id: 'racial-ethnic-minorities',
          mode: 'Racial/Ethnic Minorities',
          description:
            'Racial/Ethnic Minorities<br/><small class="text-secondary">Discussion on racial and ethnic representation</small>',
        },
        {
          id: 'drug-drug-interactions',
          mode: 'Drug-Drug Interactions',
          description:
            'Drug-Drug Interactions<br/><small class="text-secondary">Discussion on drug-drug interactions</small>',
        },
      ],
      selectedSearchMode: 'All Content',
      // start offset of displayed search modes
      searchModeOffset: 0,
      // number of search modes to scroll past when scrolling left or right
      searchModeScrollSize: 3,
      // number of search modes to display at one time
      numDisplaySearchModes: 5,

      modeAvgConf: 50,
      modeMaxConf: 98,
      numModeChunks: 5,
      percMatchMode: 80,

      showTags: false,

      sortOptions: [
        { value: 'relevant', text: 'Most Relevant' },
        { value: 'recent', text: 'Most Recent' },
      ],
      sortOrder: 'relevant',

      loadingBlur: '',

      acItems: [],

      dateFilterLoading: false,

      selectedFilters: [],
      chartWidth: 400,
      chartHeight: 400,

      analyticsGroups: ['Database Content', 'Number of Products'],
      selectedAnalyticsGroup: 'Database Content',
      showPDFViewerModal: false,

      // only for dev
      flags: [],
      showAnalytics: false,
      trialMode: false,

      loadedPageFiltersForm: false,
      loadedDocumentFiltersForm: false,
      loadedRequestAssistanceForm: false,
      bookmarkToDelete: { id: '', note: '', title: '' },

      showClinStudies: false,
      // showClinStudies: [
      //   false,
      //   false,
      //   false,
      //   false,
      //   false,
      //   false,
      //   false,
      //   false,
      //   false,
      //   false,
      // ],
      trialItems: [[], [], [], [], [], [], [], [], [], []],
      suggestResponse: { fields: { 'products.product_name': [] } },

      trialIdsByDoc: [],
      trialIdsByProduct: [],
      endpointsByDoc: [[], [], [], [], [], [], [], [], [], []],
      endpointsTextByDoc: [[], [], [], [], [], [], [], [], [], []],
      trialInfoByDoc: [[], [], [], [], [], [], [], [], [], []],
      trialInfoByProduct: [[], [], [], [], [], [], [], [], [], []],
      trialInfoByQuery: [],
      trialInfoByQueryNum: 0,
      trialLastIdx: 8,
      showAllTrials: false,
      semanticSearch: false,
    };
  },
  computed: {
    isLabelSectionSearch() {
      return this.getFilterTags.some((f) => f.val.startsWith('LABEL: '));
    },
    getFilteredSearchModes() {
      // if (this.isDev) return this.searchModes;
      // else
      return this.searchModes.filter((m) => {
        return !m.mode.endsWith('(Experimental)');
      });
    },
    snippetArgs() {
      const args = {
        filters: this.getFilterTags,
        query: this.searchTerm,
        mode: this.selectedSearchMode,
        actionDateStart: this.actionDateStartValue,
        actionDateEnd: this.actionDateEndValue,
        pageNum: this.currPageNum,
        project: this.selectedProjectId,
      };
      return args;
    },
    selectedProjectId() {
      return this.project.id;
    },
    ...mapGetters([
      'project',
      'projects',
      'savedSearch',
      'snippets',
      'snippetExistsArray',
      'isSavingSnippetLoading',
      'isDestroyingSnippetLoading',
    ]),
    getFilterTags() {
      const tags = [];

      if (this.filters.docIds.selected.length > 0) {
        tags.push({
          name: 'docIds',
          val: 'Filtered ' + this.filters.docIds.selected.length + ' Documents',
        });
      }

      for (const filter in this.filters) {
        if (
          this.filters[filter].numPages.name !== 'docIds' &&
          this.filters[filter].selected
        ) {
          this.filters[filter].selected.forEach((sel) => {
            if (sel.trim().length > 0) tags.push({ name: filter, val: sel });
          });
        }
      }
      if (this.actionDateStartValue.length > 0) {
        tags.push({
          name: 'fromDate',
          val: 'From: ' + this.actionDateStartValue,
        });
      }
      if (this.actionDateEndValue.length > 0) {
        tags.push({ name: 'toDate', val: 'To: ' + this.actionDateEndValue });
      }
      return tags;
    },
    getQuotedSearchTerms() {
      return this.currSearchTerm.split('"').filter((a, i) => i % 2 === 1);
    },
    getUnquotedSearchTerms() {
      // return this.currSearchTerm
      //   .split('"')
      //   .filter((a, i) => i % 2 === 0).forEach((t) => t.split(/[^A-Za-z]+/));
      return this.currSearchTerm
        .split('"')
        .filter((a, i) => i % 2 === 0)
        .reduce(function (acc, curr) {
          acc.push(...curr.split(/[^A-Za-z\\-]+/));
          return acc;
        }, []);
    },
    queryError() {
      let error = '';
      this.getQuotedSearchTerms.forEach((term) => {
        try {
          new RegExp(' (' + term.trim() + ') ', 'ig');
        } catch (err) {
          logError(err);
          error =
            'Your query is invalid. Please check for any typos with parentheses, quotes, etc.';
        }
      });
      return error;
    },
  },
  watch: {
    async semanticSearch() {
      if (this.searchTerm.length > 0) this.setLoading();
      this.setNewSearchAttributes();
      await this.refreshFilters();
      if (this.searchTerm.length > 0) {
        this.newSearch();
      }
    },
    async selectedSearchMode() {
      if (this.searchTerm.length > 0) this.setLoading();
      this.setNewSearchAttributes();
      await this.refreshFilters();
      if (this.searchTerm.length > 0) {
        this.newSearch();
      }
    },
    currPage(val) {
      this.currPageNum = val;
    },
    savedSearch(val) {
      const isEqual = (...objects) =>
        objects.every(
          (obj) => JSON.stringify(obj) === JSON.stringify(objects[0])
        );
      this.search_saved = false;
      this.savedSearches = val.map((search) =>
        this.reOrganizeSearchObject(search)
      );
      this.savedSearches.forEach((search) => {
        if (
          search.query === this.searchTerm &&
          search.mode === this.selectedSearchMode &&
          isEqual(search.filters, this.reOrganizeFilters(this.getFilterTags))
        ) {
          this.search_saved = true;
        }
      });
    },
  },
  async beforeCreate() {
    this.userinfo = await Auth.currentUserInfo();
    Analytics.record({
      name: 'visitPage',
      attributes: {
        email: this.userinfo.attributes.email,
        pageName: this.pageName,
      },
    });
    // get user role
    const response = await getUserAsync(this.userinfo.attributes.email);

    this.activateUserRole(response);
    this.setLoading(undefined, false);
    await this.refreshFilters();
    window.addEventListener('resize', this.updateChartWidth);
    this.updateChartWidth();
  },
  async created() {
    this.trialIdsByDoc = [];
    this.trialIdsByProduct = [];
    this.trialLastIdx = 8;
    this.showAllTrials = false;

    this.getUniqueProductNames = getUniqueProductNames;
    // ensure that dev features are displayed only in dev env
    this.isDev = process.env.VUE_APP_PROD !== 'true';
    this.isCompareDocsUngated = isCompareDocsUngated;
    this.isSuggestFiltersUngated = isSuggestFiltersUngated;
    await this.$store.dispatch('FETCH_SAVED_SEARCH', {
      project_id: this.project.id,
    });
    this.savedSearches = this.savedSearch.map((search) =>
      this.reOrganizeSearchObject(search)
    );
    if (this.componentRole === 'project') {
      this.selectedSearchMode = this.searchMode;
      this.filters.productName.selected = this.productsToSearch;

      if (this.searchTerm || this.getFilterTags.length > 0) {
        await this.setLoading();
        this.setNewSearchAttributes();
        await this.newSearch();
        await this.refreshFilters();
      }

      eventBus.$on('updateProject', async (args) => {
        //update the project and do the searching again
        this.selectedSearchMode = args.seachMode;
        this.filters.productName.selected = args.productsToSearch;
        if (this.searchTerm || this.getFilterTags.length > 0) {
          await this.setLoading();
          this.setNewSearchAttributes();
          await this.newSearch();
          await this.refreshFilters();
        }
      });
    } else if (this.componentRole === 'search') {
      this.sortOrder = 'recent';
      this.selectedSearchMode = this.searchMode; //search mode

      // first clear all selections
      Object.keys(this.filters).forEach((key) => {
        this.filters[key].selected = [];
      });

      // now add organized filter selections into filters
      Object.keys(this.organizedFilter).forEach((key) => {
        this.filters[key].selected = this.organizedFilter[key];
      });

      this.searchTerm = this.query;
      if (this.searchTerm || this.getFilterTags.length > 0) {
        await this.setLoading();
        this.setNewSearchAttributes();
        await this.newSearch();
        await this.refreshFilters();
      }
    }
    this.$store.dispatch('FETCH_SNIPPETS', {
      project_id: this.project.id,
    });
  },
  methods: {
    async grabProducts() {
      // get all product names
      this.filters.productName.loading = true;
      try {
        // composite terms aggregation since there are >10k unique product
        // names
        const response = await ESService.getCompositeTermsRegDocPage(
          this.getApiArgs({
            nestedPath: 'products',
            termFieldName: 'product_name',
            excludeFilters: [],
            numterms: 10000,
          })
        );

        this.filters.productName.selected =
          response.data.aggregations.termsAgg.termsAgg.buckets.map(
            (b) => b.key
          );
      } catch (err) {
        this.filters.productName.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.productName.loading = false;
      }

      this.setLoading();
      this.setNewSearchAttributes();
      this.refreshFilters();
      this.newSearch();
    },
    async getTrialItems(hits) {
      Promise.all(
        hits
          .map((inner_hit) => {
            return inner_hit._source.nct_ids &&
              inner_hit._source.nct_ids.length > 0
              ? inner_hit._source.nct_ids
              : [];
          })
          .reduce(function (accumulator, curValue) {
            return accumulator.concat(curValue);
          }, [])
          .map(async (nctNum) => {
            try {
              return Promise.resolve(
                ESService.getTrialInfo({ nctNum: 'NCT' + nctNum })
              );
            } catch (error) {
              return Promise.reject(error);
            }
          })
      ).then((responses) => {
        const data = responses
          .map((resp) => resp.data)
          .map(({ brief_title, condition, completion_date }) => {
            return {
              brief_title: brief_title,
              condition,
              completion_date,
            };
          });
        return data || [];
      });
      // return [{ 'brief_title': 'study 1', 'condition': 'cancer', 'completion_date': 'Jan 21, 2020'}]
      // return [
      //   {
      //     study: 'test title',
      //     condition: 'cancer',
      //     completion_date: '2020-01-01',
      //   },
      // ];
    },
    async grabDocs(filter = true) {
      // filter all doc IDs
      this.filters.docIds.loading = true;

      if (!filter) {
        this.filters.docIds.selected = [];
        this.filters.docIds.loading = false;
        return;
      }

      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'doc_id',
            excludeFilters: [],
            numterms: 10000,
          })
        );

        this.filters.docIds.selected =
          response.data.aggregations.termsAgg.buckets.map((b) => b.key);
      } catch (err) {
        this.filters.docIds.selected = [];
        this.filters.docIds.loading = false;
        logError(err);
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.docIds.loading = false;
      }
    },
    async refreshExpSearchModes() {
      if (this.searchTerm.length > 0) await this.setLoading();
      this.setNewSearchAttributes();
      await this.refreshFilters();
      this.newSearch();
    },
    setLoading(excludeFilter = undefined, search = true) {
      Object.keys(this.filters).forEach((f) => {
        if (excludeFilter !== f) this.filters[f].loading = true;
      });
      if (search) this.searchLoading = true;
    },
    switchProject(project_id) {
      this.$store.commit('CLEAR_SNIPPETS');
      this.$store.commit('SET_PROJECT', project_id);
      this.$store.dispatch('FETCH_SNIPPETS', {
        project_id: project_id,
      });
      this.$store.dispatch('FETCH_SAVED_SEARCH', {
        project_id: project_id,
      });
    },
    applySavedFilter(id) {
      this.resetSearch();
      this.clearAllFilters();
      this.savedSearch.map((search) => {
        if (search.id === id) {
          const filters = JSON.parse(search.filters);
          this.getAcItems(search.query);
          this.selectedSearchMode = search.mode;
          filters.map((filter) => {
            this.filters[filter.name].selected.push(filter.val);
          });
        }
      });
      this.activeSearch = id;
    },
    checkSnippet(id) {
      const found_id = this.snippets.find((s) => s.id === id);
      if (found_id) return true;
      return false;
    },
    inviteUsers() {
      this.$store.commit('SHOW_PROJECT_INVITE_MODAL', true);
    },
    createProject() {
      this.$store.commit('SHOW_PROJECT_MODAL', true);
    },
    editSnippet(hit, hitIdx) {
      this.snippetArgs.hit = hit;
      this.snippetArgs.hitIdx = hitIdx;
      this.snippetArgs.status = true;
      this.snippetArgs.edit = true;
      // TODO this may not be the up to date snippet description because
      // snippets get fetched only on page refresh, and the description
      // may have been edited from saved results page
      this.$store.commit('SHOW_MODAL_SNIPPET', this.snippetArgs);
    },
    deleteSnippet(hit, hitIdx) {
      const snippet = this.snippets.find(
        (snip) =>
          JSON.parse(snip.hit_content).fields.doc_id[0] ===
            hit.fields.doc_id[0] ||
          (JSON.parse(snip.hit_content)._source.doc_id === hit._source.doc_id &&
            snip.query === this.searchTerm &&
            snip.mode === this.selectedSearchMode)
      );
      const deletePayload = {
        snippetId: snippet !== undefined ? snippet.id : undefined,
        hitIdx: hitIdx,
      };
      this.$store.dispatch('DESTROY_SNIPPET', deletePayload);
    },
    saveSnippet(hit, hitIdx) {
      this.snippetArgs.hit = hit;
      this.snippetArgs.hitIdx = hitIdx;
      this.snippetArgs.status = true;
      this.snippetArgs.edit = false;
      this.$store.commit('SHOW_MODAL_SNIPPET', this.snippetArgs);
    },
    saveSnippetQuick(hit, hitIdx) {
      this.snippetArgs.hit = hit;
      this.snippetArgs.hitIdx = hitIdx;
      this.$store.dispatch('SAVE_SNIPPET', this.snippetArgs);
    },
    setSnippetExistsArray() {
      const payload = {
        query: this.searchTerm,
        mode: this.selectedSearchMode,
        docIds: this.searchResults.map((hit) => hit._source.doc_id),
      };
      this.$store.dispatch('SNIPPET_EXISTS', payload);
    },
    saveSearch() {
      const saveData = {};
      saveData.projectID = this.selectedProjectId;
      saveData.keyword = this.currSearchTerm;
      saveData.page = this.currPage;
      this.$store.commit('SHOW_MODAL_SEARCH', {
        filters: this.getFilterTags,
        query: this.currSearchTerm,
        mode: this.selectedSearchMode,
        actionDateStart: this.actionDateStartValue,
        actionDateEnd: this.actionDateEndValue,
        offset: this.searchOffset,
        prevNumPages: this.numHits,
      });

      this.$bvModal.show('modal-search');
    },
    deleteSearch() {
      const toDelete = this.getBookmark();

      if (!toDelete) {
        return;
      }

      if (toDelete.description) {
        this.bookmarkToDelete.id = toDelete.id;
        this.bookmarkToDelete.note = toDelete.description;
        this.bookmarkToDelete.title = toDelete.title;
        this.$bvModal.show('modal-delete-search');
      } else {
        this.$store.dispatch('DELETE_SEARCH', {
          id: toDelete.id,
        });
      }
    },
    getBookmark() {
      const filterTags = this.getFilterTags;
      return this.savedSearch.find((search) => {
        return (
          search.query === this.currSearchTerm &&
          search.mode === this.selectedSearchMode &&
          areArraysEqual(JSON.parse(search.filters), filterTags)
        );
      });
    },
    tooltipMsg(action = 'Download', table = true) {
      let msg = '';
      if (action === 'Bookmark') {
        if (this.search_saved) msg = 'Bookmarked';
        else msg = 'Add Bookmark';
      } else {
        if (['Download', 'Expand'].indexOf(action) >= 0)
          msg += action + ' ' + (table ? 'Table' : 'Graph');
        else if (action === 'Show')
          msg += action + ' ' + (table ? 'Graph' : 'Table');
        msg +=
          this.trialMode && action !== 'Show' ? '<br/>(Disabled in trial)' : '';
      }
      return msg;
    },
    activateUserRole(response) {
      if (
        response.data.getUserRole === null ||
        ['ADMIN', 'PREMIUM', 'BASIC', 'TRIAL'].indexOf(
          response.data.getUserRole.role
        ) === -1
      ) {
        this.showAnalytics = false;
      } else {
        this.showAnalytics = true;
      }

      if (
        response.data.getUserRole === null ||
        response.data.getUserRole.role === 'TRIAL'
      )
        this.trialMode = true;
      else this.trialMode = false;
    },
    toggleCollapse() {
      this.isCollapse = !this.isCollapse;
      if (!this.isCollapse) {
        this.sidebarStyle.width = '25%';
        this.sidebarStyle.padding = '20px';
        this.filterContentStyle.width = '100%';
        this.filterContentStyle.border = '1px solid rgba(0,0,0,.125)';
        this.searchResultsStyle.width = '70%';
        // this.searchResultStyle.marginRight = "20vw"
        document.getElementById('toggle-button').innerHTML = 'Hide Filters';
      } else {
        this.sidebarStyle.width = '0px';
        this.sidebarStyle.border = 'none';
        this.filterContentStyle.width = '100%';
        this.filterContentStyle.border = 'none';
        this.searchResultsStyle.width = '90%';
        document.getElementById('toggle-button').innerHTML = 'Show Filters';
      }
    },
    updateChartWidth() {
      if (!this.$refs.chartContainer) return;
      this.chartWidth =
        parseInt(window.getComputedStyle(this.$refs.chartContainer).width) / 2 -
        60;
    },
    reload() {
      location.reload();
    },
    handleClose() {
      this.currentChart = '';
    },
    toggleToFullSizeTimeline() {
      this.fullSizeChartVisible = true;
    },
    toggleToFullSizeOthers(chartId) {
      this.currentChart = chartId;
    },
    async removeFilterTag(tag) {
      if (tag.name === 'docIds') {
        this.filters[tag.name].selected = [];
      } else if (this.filters[tag.name] !== undefined) {
        const index = this.filters[tag.name].selected.indexOf(tag.val);
        if (index > -1) {
          this.filters[tag.name].selected.splice(index, 1);
        }
      } else {
        if (tag.name === 'fromDate') {
          this.actionDateStartValue = '';
        } else if (tag.name === 'toDate') {
          this.actionDateEndValue = '';
        }
      }
      this.setLoading();
      this.setNewSearchAttributes();
      this.newSearch();
      await this.refreshFilters();
    },
    async addFilterTag(tagName, tag) {
      if (this.filters[tagName] !== undefined) {
        const idx = this.filters[tagName].selected.indexOf(tag);
        if (idx < 0) {
          this.filters[tagName].selected.push(tag);
        }
      }
      this.setLoading();
      this.setNewSearchAttributes();
      this.newSearch();
      await this.refreshFilters();
    },
    async clearAllFilters() {
      // just emptying array using [] does not work because it does not
      // change the value of the originally referenced array
      for (const filter in this.filters)
        this.filters[filter].selected.splice(0);
      this.actionDateStartValue = '';
      this.actionDateEndValue = '';
      this.setLoading();
      this.setNewSearchAttributes();
      await this.newSearch();
      await this.refreshFilters();
      this.selectedFilters.splice(0);
    },
    async clearSearch() {
      this.searchTerm = '';
      this.currSearchTerm = '';
      this.$refs.searchInput.value = '';
      this.setLoading();
      this.setNewSearchAttributes();
      await this.refreshFilters();
      await this.newSearch();
      document.getElementById('search-input').focus();
    },
    capitalizeFirstLetter(str) {
      return str.charAt(0).toUpperCase() + str.substring(1);
    },
    async onFilterSelectionChange(filter_name, filter_selections = undefined) {
      if (filter_selections !== undefined)
        this.filters[filter_name].selected = filter_selections;
      this.setLoading();
      this.setNewSearchAttributes();
      this.newSearch();
      await this.refreshFilters();
    },
    clearFilterOptions() {
      for (const filter in this.filters) {
        if (this.filters[filter].numPages) {
          this.filters[filter].numPages.options.splice(0);
          if (this.filters[filter].numPages.visualizeOptions) {
            this.filters[filter].numPages.visualizeOptions.splice(0);
          }
        }
        if (this.filters[filter].numProducts) {
          this.filters[filter].numProducts.options.splice(0);
          if (this.filters[filter].numProducts.visualizeOptions) {
            this.filters[filter].numProducts.visualizeOptions.splice(0);
          }
        }
      }
    },
    // refresh available options for other filters when an option
    // is selected for current filter. If there is no curr_filter_name, all
    // filters are refreshed
    async refreshFilters(exclude = undefined) {
      if (exclude !== 'dbSource') this.getDbSourcesFilterValues();
      if (exclude !== 'timeline') await this.getTimelineChartValues();
      if (exclude !== 'productName') await this.getProductNamesFilterValues();
      if (exclude !== 'productDosageForm')
        await this.getProductDosageFormsFilterValues();
      if (exclude !== 'productDosageRoute')
        await this.getProductDosageRoutesFilterValues();
      if (exclude !== 'therapeuticArea') this.getTherapeuticAreasFilterValues();
      if (exclude !== 'indication') this.getIndicationsFilterValues();
      if (exclude !== 'productClass') this.getProductClassesFilterValues();
      if (exclude !== 'companyName') this.getCompanyNamesFilterValues();
      if (exclude !== 'sectionName') this.getSectionNamesFilterValues();

      setTimeout(() => {
        if (exclude !== 'productType') this.getProductTypesFilterValues();
        if (exclude !== 'productSubset') this.getProductSubsetsFilterValues();
        if (exclude !== 'applicationType')
          this.getApplicationTypesFilterValues();
        if (exclude !== 'docSubCategory')
          this.getDocSubCategoriesFilterValues();
        if (exclude !== 'submissionType') this.getSubmissionTypesFilterValues();
        if (exclude !== 'drugDesignationOrProgram')
          this.getDrugDesignationOrProgramsFilterValues();
        if (exclude !== 'pmcRequiredUnder')
          this.getPMCRequiredUnderFilterValues();
      }, 1000);

      setTimeout(() => {
        if (exclude !== 'docCategory') this.getDocCategoriesFilterValues();
        if (exclude !== 'docType') this.getDocTypesFilterValues();
        if (this.isDev && exclude !== 'docSubType')
          this.getDocSubTypesFilterValues();
        if (exclude !== 'guidanceTopic') this.getGuidanceTopicsFilterValues();
        if (exclude !== 'guidanceStatus')
          this.getGuidanceStatusesFilterValues();
        if (exclude !== 'biomarker') this.getBiomarkersFilterValues();
        if (exclude !== 'biomarkerCategory')
          this.getBiomarkerCategoriesFilterValues();
      }, 2000);

      setTimeout(() => {
        if (exclude !== 'regAgency') this.getRegAgenciesFilterValues();
        if (exclude !== 'regAgencyDivision')
          this.getRegAgencyDivisionsFilterValues();
      }, 4000);

      // set docIds filter loading to false (since it is not updated)
      this.filters.docIds.loading = false;
    },
    // get all API arguments in one functions
    getApiArgs({
      offset = undefined,
      sortOrder = undefined,
      termFieldName = undefined,
      numterms = undefined,
      nestedPath = undefined,
      subAggNestedPath = undefined,
      subAggField = undefined,
      histogramInterval = undefined,
      excludeFilters = [],
    }) {
      const apiArgs = {};

      // search query + mode
      apiArgs['q'] = this.currSearchTerm;

      if (excludeFilters.indexOf('endpoint') < 0)
        apiArgs['q'] += this.endpointSearchTerm;

      apiArgs['mode'] = this.selectedSearchMode;
      apiArgs['modeAvgConf'] = this.selectedSearchMode.endsWith(
        '(Experimental)'
      )
        ? this.modeAvgConf * 0.01
        : '';
      apiArgs['modeMaxConf'] = this.selectedSearchMode.endsWith(
        '(Experimental)'
      )
        ? this.modeMaxConf * 0.01
        : '';

      apiArgs['numModeChunks'] = this.selectedSearchMode.endsWith(
        '(Experimental)'
      )
        ? this.numModeChunks
        : '';

      apiArgs['percMatchMode'] = this.selectedSearchMode.endsWith(
        '(Experimental)'
      )
        ? this.percMatchMode
        : '';

      // search offset if applicable
      if (offset !== undefined && typeof offset === 'number')
        apiArgs['offset'] = offset;

      // search sort order if applicable
      if (sortOrder !== undefined && sortOrder.length > 0)
        apiArgs['sortOrder'] = this.sortOrder;

      // term field name if applicable
      if (termFieldName !== undefined && termFieldName.length > 0)
        apiArgs['fieldname'] = termFieldName;

      if (nestedPath !== undefined && nestedPath.length > 0)
        apiArgs['nestedPath'] = nestedPath;

      if (numterms !== undefined && typeof numterms === 'number')
        apiArgs['numterms'] = numterms;

      if (subAggNestedPath !== undefined && subAggNestedPath.length > 0)
        apiArgs['subAggNestedPath'] = subAggNestedPath;

      if (subAggField !== undefined && subAggField.length > 0)
        apiArgs['subAggField'] = subAggField;

      if (histogramInterval !== undefined && histogramInterval.length > 0)
        apiArgs['histogramInterval'] = histogramInterval;

      // add all filters wherever applicable
      if (excludeFilters.indexOf('regAgency') < 0)
        apiArgs['agencyNames'] = this.filters.regAgency.selected.join(';');

      if (excludeFilters.indexOf('regAgencyDivision') < 0)
        apiArgs['agencyDivisionNames'] =
          this.filters.regAgencyDivision.selected.join(';');

      if (excludeFilters.indexOf('applicationType') < 0)
        apiArgs['applicationTypes'] =
          this.filters.applicationType.selected.join(';');

      if (excludeFilters.indexOf('dbSource') < 0)
        apiArgs['dbSources'] = this.filters.dbSource.selected.join(';');

      if (excludeFilters.indexOf('docCategory') < 0)
        apiArgs['docCategories'] = this.filters.docCategory.selected.join(';');

      if (excludeFilters.indexOf('docSubCategory') < 0)
        apiArgs['docSubCategories'] =
          this.filters.docSubCategory.selected.join(';');

      if (excludeFilters.indexOf('docType') < 0)
        apiArgs['docTypes'] = this.filters.docType.selected.join(';');

      if (excludeFilters.indexOf('docSubType') < 0)
        apiArgs['docSubTypes'] = this.filters.docSubType.selected.join(';');

      if (excludeFilters.indexOf('sectionName') < 0)
        apiArgs['sectionNames'] = this.filters.sectionName.selected.join(';');

      if (excludeFilters.indexOf('guidanceTopic') < 0)
        apiArgs['guidanceTopics'] =
          this.filters.guidanceTopic.selected.join(';');

      if (excludeFilters.indexOf('guidanceStatus') < 0)
        apiArgs['guidanceStatuses'] =
          this.filters.guidanceStatus.selected.join(';');

      if (excludeFilters.indexOf('productType') < 0)
        apiArgs['productTypes'] = this.filters.productType.selected.join(';');

      if (excludeFilters.indexOf('therapeuticArea') < 0)
        apiArgs['therapeuticAreas'] =
          this.filters.therapeuticArea.selected.join(';');

      if (excludeFilters.indexOf('indication') < 0)
        apiArgs['indications'] = this.filters.indication.selected.join(';');

      if (excludeFilters.indexOf('productClass') < 0)
        apiArgs['productClasses'] =
          this.filters.productClass.selected.join(';');

      if (excludeFilters.indexOf('companyName') < 0)
        apiArgs['companyNames'] = this.filters.companyName.selected.join(';');

      if (excludeFilters.indexOf('productName') < 0)
        apiArgs['productNames'] =
          this.filters.productName.selected.join('__+__');

      if (excludeFilters.indexOf('productDosageForm') < 0)
        apiArgs['productDosageForms'] =
          this.filters.productDosageForm.selected.join('~');

      if (excludeFilters.indexOf('productDosageRoute') < 0)
        apiArgs['productDosageRoutes'] =
          this.filters.productDosageRoute.selected.join('~');

      if (excludeFilters.indexOf('biomarker') < 0)
        apiArgs['biomarkers'] = this.filters.biomarker.selected.join(';');

      if (excludeFilters.indexOf('biomarkerCategory') < 0)
        apiArgs['biomarkerCategories'] =
          this.filters.biomarkerCategory.selected.join(';');

      if (excludeFilters.indexOf('productSubset') < 0)
        apiArgs['productSubsets'] =
          this.filters.productSubset.selected.join(';');

      if (excludeFilters.indexOf('drugDesignationOrProgram') < 0)
        apiArgs['drugDesignationOrPrograms'] =
          this.filters.drugDesignationOrProgram.selected.join(';');

      if (excludeFilters.indexOf('submissionType') < 0)
        apiArgs['submissionTypes'] =
          this.filters.submissionType.selected.join(';');

      if (excludeFilters.indexOf('manufacturingSite') < 0)
        apiArgs['manufacturingSites'] =
          this.filters.manufacturingSite.selected.join(';');

      if (excludeFilters.indexOf('pmcRequiredUnder') < 0)
        apiArgs['pmcRequiredUnder'] =
          this.filters.pmcRequiredUnder.selected.join(';');

      if (excludeFilters.indexOf('actionDateStart') < 0)
        apiArgs['actionDateStart'] = this.actionDateStartValue;

      if (excludeFilters.indexOf('actionDateEnd') < 0)
        apiArgs['actionDateEnd'] = this.actionDateEndValue;

      if (this.filters.docIds.selected.length > 0) {
        apiArgs['docIds'] = this.filters.docIds.selected.join('__+__');
      }

      return apiArgs;
    },
    async getRegAgenciesFilterValues() {
      // get all reg agencies
      this.filters.regAgency.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'agency_name',
            excludeFilters: ['regAgency'],
            numterms: 10000,
          })
        );

        this.filters.regAgency.numPages.options =
          response.data.aggregations.termsAgg.buckets.sort(
            this.sortByFilterKeys
          );
      } catch (err) {
        this.filters.regAgency.numPages.options = [];
        this.filters.regAgency.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.regAgency.loading = false;
      }
    },
    async getRegAgencyDivisionsFilterValues() {
      // get all reg agency divisions
      this.filters.regAgencyDivision.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'agency_division_name',
            excludeFilters: ['agencyDivision'],
            numterms: 10000,
          })
        );

        this.filters.regAgencyDivision.numPages.options =
          response.data.aggregations.termsAgg.buckets.sort(
            this.sortByFilterKeys
          );
      } catch (err) {
        this.filters.regAgencyDivision.numPages.options = [];
        this.filters.regAgencyDivision.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.regAgencyDivision.loading = false;
      }
    },
    async getDbSourcesFilterValues() {
      // get all database sources
      this.filters.dbSource.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'source_name.keyword',
            excludeFilters: ['dbSource'],
            numterms: 10000,
          })
        );

        this.filters.dbSource.numPages.options =
          response.data.aggregations.termsAgg.buckets.sort(
            this.sortByFilterKeys
          );
      } catch (err) {
        this.filters.dbSource.numPages.options = [];
        this.filters.dbSource.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.dbSource.loading = false;
      }
    },
    async getDocCategoriesFilterValues() {
      // get all doc categories
      this.filters.docCategory.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'doc_category',
            excludeFilters: ['docCategory'],
            numterms: 10000,
          })
        );

        this.filters.docCategory.numPages.options =
          response.data.aggregations.termsAgg.buckets.sort(
            this.sortByFilterKeys
          );
      } catch (err) {
        this.filters.docCategory.numPages.options = [];
        this.filters.docCategory.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.docCategory.loading = false;
      }
    },
    async getApplicationTypesFilterValues() {
      // get all application types
      this.filters.applicationType.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'application_type.keyword',
            excludeFilters: ['applicationType'],
            numterms: 10000,
          })
        );

        this.filters.applicationType.numPages.options =
          response.data.aggregations.termsAgg.buckets.sort(
            this.sortByFilterKeys
          );
      } catch (err) {
        this.filters.applicationType.numPages.options = [];
        this.filters.applicationType.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.applicationType.loading = false;
      }
    },
    async getDocSubCategoriesFilterValues() {
      // get all doc sub categories
      this.filters.docSubCategory.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'doc_sub_category',
            excludeFilters: ['docSubCategory'],
            numterms: 10000,
          })
        );

        this.filters.docSubCategory.numPages.options =
          response.data.aggregations.termsAgg.buckets.sort(
            this.sortByFilterKeys
          );
      } catch (err) {
        this.filters.docSubCategory.numPages.options = [];
        this.filters.docSubCategory.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.docSubCategory.loading = false;
      }
    },
    async getDocTypesFilterValues() {
      // get all doc types
      this.filters.docType.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'doc_type',
            excludeFilters: ['docType'],
            numterms: 10000,
          })
        );

        this.filters.docType.numPages.options =
          response.data.aggregations.termsAgg.buckets.sort(
            this.sortByFilterKeys
          );
      } catch (err) {
        this.filters.docType.numPages.options = [];
        this.filters.docType.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.docType.loading = false;
      }
    },
    async getDocSubTypesFilterValues() {
      // get all doc sub types
      this.filters.docSubType.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            nestedPath: 'page_tags',
            termFieldName: 'type.keyword',
            excludeFilters: ['docSubType'],
            numterms: 10000,
          })
        );

        this.filters.docSubType.numPages.options =
          response.data.aggregations.termsAgg.termsAgg.buckets
            .map((b) => {
              return {
                key: b.key,
                doc_count: b.doc_count,
              };
            })
            .sort(this.sortByFilterKeys);

        const filteredBuckets =
          response.data.aggregations.termsAgg.termsAgg.buckets.filter(
            (b) =>
              this.filters.docSubType.selected.length === 0 ||
              this.filters.docSubType.selected.indexOf(b.key) >= 0
          );

        const sumCounts = filteredBuckets
          .map((b) => b.doc_count)
          .reduce((a, b) => a + b, 0);
        this.filters.docSubType.numPages.visualizeOptions = [...filteredBuckets]
          .map((b) => {
            return {
              key: b.key,
              doc_count: (b.doc_count * 100.0) / sumCounts,
            };
          })
          .sort(this.sortByFilterCounts);
      } catch (err) {
        this.filters.docSubType.numPages.options = [];
        this.filters.docSubType.numPages.visualizeOptions = [];
        this.filters.docSubType.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.docSubType.loading = false;
      }
    },
    async getSectionNamesFilterValues() {
      // get all section names
      this.filters.sectionName.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            nestedPath: 'sections',
            termFieldName: 'section_name.keyword',
            excludeFilters: ['sectionName'],
            numterms: 10000,
          })
        );

        this.filters.sectionName.numPages.options =
          response.data.aggregations.termsAgg.sectionFilterAgg.termsAgg.buckets
            .map((b) => {
              return {
                key: b.key,
                doc_count: b.reverse_nested.doc_count,
              };
            })
            .sort(this.sortByFilterKeys);
      } catch (err) {
        this.filters.sectionName.numPages.options = [];
        this.filters.sectionName.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.sectionName.loading = false;
      }
    },
    async getGuidanceTopicsFilterValues() {
      // get all guidance topics
      this.filters.guidanceTopic.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'guidance_topics.keyword',
            excludeFilters: ['guidanceTopic'],
            numterms: 10000,
          })
        );

        this.filters.guidanceTopic.numPages.options =
          response.data.aggregations.termsAgg.buckets.sort(
            this.sortByFilterKeys
          );
      } catch (err) {
        this.filters.guidanceTopic.numPages.options = [];
        this.filters.guidanceTopic.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.guidanceTopic.loading = false;
      }
    },
    async getGuidanceStatusesFilterValues() {
      // get all guidance statuses
      this.filters.guidanceStatus.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'guidance_status',
            excludeFilters: ['guidanceStatus'],
            numterms: 10000,
          })
        );

        this.filters.guidanceStatus.numPages.options =
          response.data.aggregations.termsAgg.buckets.sort(
            this.sortByFilterKeys
          );
      } catch (err) {
        this.filters.guidanceStatus.numPages.options = [];
        this.filters.guidanceStatus.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.guidanceStatus.loading = false;
      }
    },
    async getCompanyNamesFilterValues() {
      // get all company names
      this.filters.companyName.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'company_name.keyword',
            excludeFilters: ['companyName'],
            numterms: 10000,
            subAggNestedPath: 'products',
            subAggField: 'product_name',
          })
        );

        this.filters.companyName.numPages.options = [
          ...response.data.aggregations.termsAgg.buckets,
        ].sort(this.sortByFilterKeys);

        const filteredBuckets =
          response.data.aggregations.termsAgg.buckets.filter(
            (b) =>
              this.filters.companyName.selected.length === 0 ||
              this.filters.companyName.selected.indexOf(b.key) >= 0
          );

        const sumCounts = filteredBuckets
          .map((b) => b.doc_count)
          .reduce((a, b) => a + b, 0);
        this.filters.companyName.numPages.visualizeOptions = [
          ...filteredBuckets,
        ]
          .map((b) => {
            return {
              key: b.key,
              doc_count: (b.doc_count * 100.0) / sumCounts,
            };
          })
          .sort(this.sortByFilterCounts);

        this.filters.companyName.numProducts.visualizeOptions = [
          ...filteredBuckets,
        ]
          .map((b) => {
            return {
              key: b.key,
              doc_count: b.nestedAggs.subAggs.value,
            };
          })
          .sort(this.sortByFilterCounts);
      } catch (err) {
        this.filters.companyName.numPages.options = [];
        this.filters.companyName.numPages.visualizeOptions = [];
        this.filters.companyName.numProducts.visualizeOptions = [];
        this.filters.companyName.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.companyName.loading = false;
      }
    },
    async getProductTypesFilterValues() {
      // get all product types
      this.filters.productType.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            nestedPath: 'products',
            termFieldName: 'product_type',
            excludeFilters: ['productType'],
            numterms: 10000,
          })
        );

        this.filters.productType.numPages.options =
          response.data.aggregations.termsAgg.termsAgg.buckets
            .map((b) => {
              return {
                key: b.key,
                doc_count: b.reverse_nested.doc_count,
              };
            })
            .sort(this.sortByFilterKeys);
      } catch (err) {
        this.filters.productType.numPages.options = [];
        this.filters.productType.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.productType.loading = false;
      }
    },
    async getBiomarkersFilterValues() {
      // get all biomarkers
      this.filters.biomarker.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            nestedPath: 'nested_biomarker_labeling_sections',
            termFieldName: 'biomarkers.keyword',
            excludeFilters: ['biomarker'],
            numterms: 10000,
          })
        );

        this.filters.biomarker.numPages.options =
          response.data.aggregations.termsAgg.termsAgg.buckets.sort(
            this.sortByFilterKeys
          );
      } catch (err) {
        this.filters.biomarker.numPages.options = [];
        this.filters.biomarker.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.biomarker.loading = false;
      }
    },
    async getBiomarkerCategoriesFilterValues() {
      // get all biomarker categories
      this.filters.biomarkerCategory.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            nestedPath: 'nested_biomarker_labeling_sections',
            termFieldName: 'biomarker_labeling_sections.keyword',
            excludeFilters: ['biomarkerCategory'],
            numterms: 10000,
          })
        );

        this.filters.biomarkerCategory.numPages.options =
          response.data.aggregations.termsAgg.termsAgg.buckets.sort(
            this.sortByFilterKeys
          );
      } catch (err) {
        this.filters.biomarkerCategory.numPages.options = [];
        this.filters.biomarkerCategory.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.biomarkerCategory.loading = false;
      }
    },
    async getProductSubsetsFilterValues() {
      // get all biomarker categories
      this.filters.productSubset.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'product_subsets.keyword',
            excludeFilters: ['productSubset'],
            numterms: 10000,
          })
        );

        this.filters.productSubset.numPages.options =
          response.data.aggregations.termsAgg.buckets.sort(
            this.sortByFilterKeys
          );
      } catch (err) {
        this.filters.productSubset.numPages.options = [];
        this.filters.productSubset.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.productSubset.loading = false;
      }
    },
    async getTherapeuticAreasFilterValues() {
      // get all indications
      this.filters.therapeuticArea.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'therapeutic_areas.keyword',
            excludeFilters: ['companyName'],
            numterms: 10000,
            subAggNestedPath: 'products',
            subAggField: 'product_name',
          })
        );

        this.filters.therapeuticArea.numPages.options = [
          ...response.data.aggregations.termsAgg.buckets,
        ].sort(this.sortByFilterKeys);

        const filteredBuckets =
          response.data.aggregations.termsAgg.buckets.filter(
            (b) =>
              this.filters.therapeuticArea.selected.length === 0 ||
              this.filters.therapeuticArea.selected.indexOf(b.key) >= 0
          );

        const sumCounts = filteredBuckets
          .map((b) => b.doc_count)
          .reduce((a, b) => a + b, 0);
        this.filters.therapeuticArea.numPages.visualizeOptions = [
          ...filteredBuckets,
        ]
          .map((b) => {
            return {
              key: b.key,
              doc_count: (b.doc_count * 100.0) / sumCounts,
            };
          })
          .sort(this.sortByFilterCounts);

        this.filters.therapeuticArea.numProducts.visualizeOptions = [
          ...filteredBuckets,
        ]
          .map((b) => {
            return {
              key: b.key,
              doc_count: b.nestedAggs.subAggs.value,
            };
          })
          .sort(this.sortByFilterCounts);
      } catch (err) {
        this.filters.therapeuticArea.numPages.options = [];
        this.filters.therapeuticArea.numPages.visualizeOptions = [];
        this.filters.therapeuticArea.numProducts.visualizeOptions = [];
        this.filters.therapeuticArea.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.therapeuticArea.loading = false;
      }
    },
    async getIndicationsFilterValues() {
      // get all indications
      this.filters.indication.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'indications.keyword',
            excludeFilters: ['indication'],
            numterms: 10000,
            subAggNestedPath: 'products',
            subAggField: 'product_name',
          })
        );

        this.filters.indication.numPages.options = [
          ...response.data.aggregations.termsAgg.buckets,
        ].sort(this.sortByFilterKeys);

        const filteredBuckets =
          response.data.aggregations.termsAgg.buckets.filter(
            (b) =>
              this.filters.indication.selected.length === 0 ||
              this.filters.indication.selected.indexOf(b.key) >= 0
          );

        const sumCounts = filteredBuckets
          .map((b) => b.doc_count)
          .reduce((a, b) => a + b, 0);
        this.filters.indication.numPages.visualizeOptions = [...filteredBuckets]
          .map((b) => {
            return {
              key: b.key,
              doc_count: (b.doc_count * 100.0) / sumCounts,
            };
          })
          .sort(this.sortByFilterCounts);

        this.filters.indication.numProducts.visualizeOptions = [
          ...filteredBuckets,
        ]
          .map((b) => {
            return {
              key: b.key,
              doc_count: b.nestedAggs.subAggs.value,
            };
          })
          .sort(this.sortByFilterCounts);
      } catch (err) {
        this.filters.indication.numPages.options = [];
        this.filters.indication.numPages.visualizeOptions = [];
        this.filters.indication.numProducts.visualizeOptions = [];
        this.filters.indication.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.indication.loading = false;
      }
    },
    async getGenericNamesFilterValues() {
      // get all generic names
      this.filters.genericName.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            nestedPath: 'products',
            termFieldName: 'generic_name',
            excludeFilters: ['genericName'],
            numterms: 10000,
          })
        );

        this.filters.genericName.numPages.options =
          response.data.aggregations.termsAgg.termsAgg.buckets
            .map((b) => {
              return {
                key: b.key,
                doc_count: b.reverse_nested.doc_count,
              };
            })
            .sort(this.sortByFilterKeys);
      } catch (err) {
        this.filters.genericName.numPages.options = [];
        this.filters.genericName.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.genericName.loading = false;
      }
    },
    async getBrandNamesFilterValues() {
      // get all brand names
      this.filters.brandName.loading = true;

      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            nestedPath: 'products',
            termFieldName: 'brand_name',
            excludeFilters: ['brandName'],
            numterms: 10000,
          })
        );

        this.filters.brandName.numPages.options =
          response.data.aggregations.termsAgg.termsAgg.buckets
            .map((b) => {
              return {
                key: b.key,
                doc_count: b.reverse_nested.doc_count,
              };
            })
            .sort(this.sortByFilterKeys);
      } catch (err) {
        this.filters.brandName.numPages.options = [];
        this.filters.brandName.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.brandName.loading = false;
      }
    },
    async getProductNamesFilterValues() {
      // get all product names
      this.filters.productName.loading = true;
      try {
        // composite terms aggregation since there are >10k unique product
        // names
        const response = await ESService.getCompositeTermsRegDocPage(
          this.getApiArgs({
            nestedPath: 'products',
            termFieldName: 'product_name',
            excludeFilters: ['productName'],
            numterms: 10000,
          })
        );

        this.filters.productName.numPages.options =
          response.data.aggregations.termsAgg.termsAgg.buckets
            .map((b) => {
              return {
                key: b.key,
                doc_count: b.doc_count,
              };
            })
            .sort(this.sortByFilterKeys);

        const filteredBuckets =
          response.data.aggregations.termsAgg.termsAgg.buckets.filter(
            (b) =>
              this.filters.productName.selected.length === 0 ||
              this.filters.productName.selected.indexOf(b.key) >= 0
          );

        const sumCounts = filteredBuckets
          .map((b) => b.doc_count)
          .reduce((a, b) => a + b, 0);
        this.filters.productName.numPages.visualizeOptions = [
          ...filteredBuckets,
        ]
          .map((b) => {
            return {
              key: b.key,
              doc_count: (b.doc_count * 100.0) / sumCounts,
            };
          })
          .sort(this.sortByFilterCounts);
      } catch (err) {
        this.filters.productName.numPages.options = [];
        this.filters.productName.numPages.visualizeOptions = [];
        this.filters.productName.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.productName.loading = false;
      }
    },
    async getProductDosageRoutesFilterValues() {
      // get all product names
      this.filters.productDosageRoute.loading = true;
      try {
        // composite terms aggregation since there are >10k unique product
        // names
        const response = await ESService.getCompositeTermsRegDocPage(
          this.getApiArgs({
            nestedPath: 'products',
            termFieldName: 'dosage_route',
            excludeFilters: ['dosageRoute'],
            numterms: 10000,
          })
        );

        this.filters.productDosageRoute.numPages.options =
          response.data.aggregations.termsAgg.termsAgg.buckets
            .map((b) => {
              return {
                key: b.key,
                doc_count: b.doc_count,
              };
            })
            .sort(this.sortByFilterKeys);
      } catch (err) {
        this.filters.productDosageRoute.numPages.options = [];
        this.filters.productDosageRoute.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.productDosageRoute.loading = false;
      }
    },
    async getProductDosageFormsFilterValues() {
      // get all product names
      this.filters.productDosageForm.loading = true;
      try {
        // composite terms aggregation since there are >10k unique product
        // names
        const response = await ESService.getCompositeTermsRegDocPage(
          this.getApiArgs({
            nestedPath: 'products',
            termFieldName: 'dosage_form',
            excludeFilters: ['productDosageForm'],
            numterms: 10000,
          })
        );
        this.filters.productDosageForm.numPages.options =
          response.data.aggregations.termsAgg.termsAgg.buckets
            .map((b) => {
              return {
                key: b.key,
                doc_count: b.doc_count,
              };
            })
            .sort(this.sortByFilterKeys);
      } catch (err) {
        this.filters.productDosageForm.numPages.options = [];
        this.filters.productDosageForm.numPages.visualizeOptions = [];
        this.filters.productDosageForm.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.productDosageForm.loading = false;
      }
    },
    async getProductClassesFilterValues() {
      // get all product classes
      this.filters.productClass.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'pharm_class_all.keyword',
            excludeFilters: ['productClass'],
            numterms: 10000,
            subAggNestedPath: 'products',
            subAggField: 'product_name',
          })
        );

        this.filters.productClass.numPages.options = [
          ...response.data.aggregations.termsAgg.buckets,
        ].sort(this.sortByFilterKeys);

        const filteredBuckets =
          response.data.aggregations.termsAgg.buckets.filter(
            (b) =>
              this.filters.productClass.selected.length === 0 ||
              this.filters.productClass.selected.indexOf(b.key) >= 0
          );

        const sumCounts = filteredBuckets
          .map((b) => b.doc_count)
          .reduce((a, b) => a + b, 0);
        this.filters.productClass.numPages.visualizeOptions = [
          ...filteredBuckets,
        ]
          .map((b) => {
            return {
              key: b.key,
              doc_count: (b.doc_count * 100.0) / sumCounts,
            };
          })
          .sort(this.sortByFilterCounts);

        this.filters.productClass.numProducts.visualizeOptions = [
          ...filteredBuckets,
        ]
          .map((b) => {
            return {
              key: b.key,
              doc_count: b.nestedAggs.subAggs.value,
            };
          })
          .sort(this.sortByFilterCounts);
      } catch (err) {
        this.filters.productClass.numPages.options = [];
        this.filters.productClass.numPages.visualizeOptions = [];
        this.filters.productClass.numProducts.visualizeOptions = [];
        this.filters.productClass.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.productClass.loading = false;
      }
    },
    async getDrugDesignationOrProgramsFilterValues() {
      // get all drug designations
      this.filters.drugDesignationOrProgram.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'designation_or_program.keyword',
            excludeFilters: ['drugDesignationOrProgram'],
            numterms: 10000,
          })
        );

        this.filters.drugDesignationOrProgram.numPages.options = [
          ...response.data.aggregations.termsAgg.buckets,
        ].sort(this.sortByFilterKeys);
      } catch (err) {
        this.filters.drugDesignationOrProgram.numPages.options = [];
        this.filters.drugDesignationOrProgram.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.drugDesignationOrProgram.loading = false;
      }
    },
    async getSubmissionTypesFilterValues() {
      // get all submission types
      this.filters.submissionType.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'submission_classification.keyword',
            excludeFilters: ['submissionType'],
            numterms: 10000,
          })
        );
        this.filters.submissionType.numPages.options = [
          ...response.data.aggregations.termsAgg.buckets,
        ].sort(this.sortByFilterKeys);
      } catch (err) {
        this.filters.submissionType.numPages.options = [];
        this.filters.submissionType.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.submissionType.loading = false;
      }
    },
    async getEndpointsFilterValues() {
      // get all endpoints
      this.filters.endpoint.loading = true;
      try {
        const response = await ESService.getEndpointsRegDocPage(
          this.getApiArgs({
            excludeFilters: ['endpoint'],
            subAggNestedPath: 'products',
            subAggField: 'product_name',
          })
        );

        const filteredBuckets = [];
        Object.keys(response.data.aggregations.filtersAgg.buckets).forEach(
          (b) => {
            if (
              this.filters.endpoint.selected.length === 0 ||
              this.filters.endpoint.selected.indexOf(b) >= 0
            )
              filteredBuckets.push({
                key: b,
                doc_count:
                  response.data.aggregations.filtersAgg.buckets[b].doc_count,
                product_count:
                  response.data.aggregations.filtersAgg.buckets[b].nestedAggs
                    .subAggs.value,
              });
          }
        );

        let sumCounts = 0;
        Object.keys(filteredBuckets).forEach(
          (b) => (sumCounts += filteredBuckets[b].doc_count)
        );

        this.filters.endpoint.numPages.visualizeOptions = [];
        Object.keys(filteredBuckets).forEach((b) => {
          this.filters.endpoint.numPages.visualizeOptions.push({
            key: filteredBuckets[b].key,
            doc_count: (filteredBuckets[b].doc_count * 100.0) / sumCounts,
          });
        });

        this.filters.endpoint.numPages.visualizeOptions =
          this.filters.endpoint.numPages.visualizeOptions.sort(
            this.sortByFilterCounts
          );

        this.filters.endpoint.numProducts.visualizeOptions = [];
        Object.keys(filteredBuckets).forEach((b) => {
          this.filters.endpoint.numProducts.visualizeOptions.push({
            key: filteredBuckets[b].key,
            doc_count: filteredBuckets[b].product_count,
          });
        });

        this.filters.endpoint.numProducts.visualizeOptions =
          this.filters.endpoint.numProducts.visualizeOptions.sort(
            this.sortByFilterCounts
          );
      } catch (err) {
        this.filters.endpoint.numPages.visualizeOptions = [];
        this.filters.endpoint.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.endpoint.loading = false;
      }
    },
    async getManufacturingSitesFilterValues() {
      // get all manufacturing sites
      this.filters.manufacturingSite.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'manufacturers.keyword',
            excludeFilters: ['manufacturingSite'],
            numterms: 10000,
            subAggNestedPath: 'products',
            subAggField: 'product_name',
          })
        );

        this.filters.manufacturingSite.numPages.options = [
          ...response.data.aggregations.termsAgg.buckets,
        ].sort(this.sortByFilterKeys);

        const filteredBuckets =
          response.data.aggregations.termsAgg.buckets.filter(
            (b) =>
              this.filters.manufacturingSite.selected.length === 0 ||
              this.filters.manufacturingSite.selected.indexOf(b.key) >= 0
          );

        const sumCounts = filteredBuckets
          .map((b) => b.doc_count)
          .reduce((a, b) => a + b, 0);
        this.filters.manufacturingSite.numPages.visualizeOptions = [
          ...filteredBuckets,
        ]
          .map((b) => {
            return {
              key: b.key,
              doc_count: (b.doc_count * 100.0) / sumCounts,
            };
          })
          .sort(this.sortByFilterCounts);

        this.filters.manufacturingSite.numProducts.visualizeOptions = [
          ...filteredBuckets,
        ]
          .map((b) => {
            return {
              key: b.key,
              doc_count: b.nestedAggs.subAggs.value,
            };
          })
          .sort(this.sortByFilterCounts);
      } catch (err) {
        this.filters.manufacturingSite.numPages.options = [];
        this.filters.manufacturingSite.numPages.visualizeOptions = [];
        this.filters.manufacturingSite.numProducts.visualizeOptions = [];
        this.filters.manufacturingSite.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.manufacturingSite.loading = false;
      }
    },
    async getPMCRequiredUnderFilterValues() {
      // get all submission types
      this.filters.pmcRequiredUnder.loading = true;
      try {
        const response = await ESService.getTermsRegDocPage(
          this.getApiArgs({
            termFieldName: 'pmc_required_under.keyword',
            excludeFilters: ['pmcRequiredUnder'],
            numterms: 10000,
          })
        );
        this.filters.pmcRequiredUnder.numPages.options = [
          ...response.data.aggregations.termsAgg.buckets,
        ].sort(this.sortByFilterKeys);
      } catch (err) {
        this.filters.pmcRequiredUnder.numPages.options = [];
        this.filters.pmcRequiredUnder.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.pmcRequiredUnder.loading = false;
      }
    },
    async getActionDateFilterValues() {
      this.dateFilterLoading = true;
      // get min and max agg values
      try {
        const response = await ESService.getMinMaxFieldValues(
          this.getApiArgs({
            termFieldName: 'action_date',
            numterms: 10000,
          })
        );

        if (response.data.aggregations.minAgg.value_as_string !== undefined)
          this.actionDateStartValue =
            response.data.aggregations.minAgg.value_as_string;
        else this.actionDateStartValue = '';

        if (response.data.aggregations.maxAgg.value_as_string !== undefined)
          this.actionDateEndValue =
            response.data.aggregations.maxAgg.value_as_string;
        else this.actionDateEndValue = '';
        this.dateFilterLoading = false;
      } catch (err) {
        this.dateFilterLoading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      }
    },
    async getTimelineChartValues(interval = 'year') {
      // get date histogram of action date
      this.filters.timeline.loading = true;
      try {
        const response = await ESService.getDateHistogramRegDocPage(
          this.getApiArgs({
            termFieldName: 'action_date',
            histogramInterval: interval,
            subAggNestedPath: 'products',
            subAggField: 'product_name',
          })
        );

        const sumCounts = response.data.aggregations.histogramAgg.buckets
          .map((b) => b.doc_count)
          .reduce((a, b) => a + b, 0);
        // chart only recognizes 'key', not 'key_as_string'
        this.filters.timeline.numPages.visualizeOptions =
          response.data.aggregations.histogramAgg.buckets.map((b) => {
            return {
              key:
                interval === 'year'
                  ? b.key_as_string.slice(0, 4)
                  : b.key_as_string.slice(0, 7),
              doc_count: (b.doc_count * 100.0) / sumCounts,
            };
          });

        this.filters.timeline.numProducts.visualizeOptions =
          response.data.aggregations.histogramAgg.buckets.map((b) => {
            return {
              key:
                interval === 'year'
                  ? b.key_as_string.slice(0, 4)
                  : b.key_as_string.slice(0, 7),
              doc_count: b.nestedAggs.subAggs.value,
            };
          });
      } catch (err) {
        this.filters.timeline.numPages.visualizeOptions = [];
        this.filters.timeline.numProducts.visualizeOptions = [];
        this.filters.timeline.loading = false;
        logError(err);
        this.filterError = true;
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      } finally {
        this.filters.timeline.loading = false;
      }
    },
    async onSearchSubmit() {
      this.setLoading();
      this.setNewSearchAttributes();
      // prioritize filter update in case of analytics tab
      if (this.tabIndex === 0) {
        await this.refreshFilters();
      }
      // prioritize search results update in case of search results tab
      await this.newSearch();
      if (
        this.searchError ||
        (this.searchResults.length === 0 && this.searchTerm.length > 0)
      )
        this.clearFilterOptions();
      else if (this.tabIndex !== 0) await this.refreshFilters();
    },
    setNewSearchAttributes() {
      this.searchOffset = 0;
      this.currPage = 1;
      this.currSearchTerm = this.searchTerm;
    },
    async newSearch() {
      // blur the search input element before activating search
      document.getElementById('search-input').blur();
      if (this.queryError) this.resetSearch();
      else {
        await this.search();
        this.setSnippetExistsArray();
      }
    },

    /** Reset search */
    async resetSearch() {
      this.searchResults = [];
      this.trialItems = [[], [], [], [], [], [], [], [], [], []];
      this.suggestResponse = { fields: { 'products.product_name': [] } };
      this.showClinStudies = false;
      // this.showClinStudies = [
      //   false,
      //   false,
      //   false,
      //   false,
      //   false,
      //   false,
      //   false,
      //   false,
      //   false,
      //   false,
      // ];
      this.numHits = 0;
      this.numDocs = 0;
      this.searchLoading = false;
    },

    /** Call API to search */
    async search() {
      const isEqual = (...objects) =>
        objects.every(
          (obj) => JSON.stringify(obj) === JSON.stringify(objects[0])
        );
      this.search_saved = false;
      this.savedSearches.forEach((search) => {
        if (
          search.query === this.searchTerm &&
          search.mode === this.selectedSearchMode &&
          isEqual(search.filters, this.reOrganizeFilters(this.getFilterTags))
        ) {
          this.search_saved = true;
        }
      });
      // don't show results for empty search query
      // earlier, this was restricted only applicable to Default search mode
      if (this.currSearchTerm.trim().length === 0) {
        this.resetSearch();
        return;
      }
      this.searchLoading = true;
      try {
        let response = {};
        if (this.semanticSearch) {
          const semResponse = await ESService.searchRegDocPage({
            q: this.currSearchTerm,
            semanticSearch: true,
            offset: this.searchOffset,
          });
          response['data'] = {
            hits: {
              hits: semResponse.data.hits,
              total: {
                value: 100,
              },
            },
            aggregations: {
              num_docs: {
                value: 100,
              },
              products_agg: {
                num_products: {
                  value: 100,
                },
              },
            },
          };
        } else {
          response = await ESService.searchRegDocPage(
            this.getApiArgs({
              offset: this.searchOffset,
              sortOrder: this.sortOrder,
            })
          );
        }
        this.searchError = false;
        this.numHits = response.data.hits.total.value;
        this.numDocs = response.data.aggregations.num_docs.value;
        this.numProducts =
          response.data.aggregations.products_agg.num_products.value;
        this.searchResults = response.data.hits.hits;

        this.trialInfo = {};

        // this.trialIdsByDoc = [[], [], [], [], [], [], [], [], [], []];
        this.endpointsByDoc = [[], [], [], [], [], [], [], [], [], []];
        this.endpointsTextByDoc = [[], [], [], [], [], [], [], [], [], []];
        this.trialInfoByDoc = [[], [], [], [], [], [], [], [], [], []];
        this.trialInfoByProduct = [[], [], [], [], [], [], [], [], [], []];
        response.data.hits.hits.forEach(async (h, idx) => {
          const endpointsResp = await ESService.getTermsRegDocPage({
            q: '',
            mode: 'All Content',
            nestedPath: 'entities',
            fieldname: 'tag.keyword',
            numterms: 100,
            docIds: h._source.doc_id,
          });

          endpointsResp.data.aggregations.termsAgg.termsAgg.buckets.forEach(
            (b) => {
              this.endpointsByDoc[idx].splice(
                this.endpointsByDoc[idx].length,
                0,
                {
                  endpoint: b.key,
                  'frequency_(# pages)': b.doc_count,
                }
              );
            }
          );

          const endpointsTextResp = await ESService.getTermsRegDocPage({
            q: '',
            mode: 'All Content',
            nestedPath: 'entities',
            fieldname: 'tag_text.keyword',
            numterms: 100,
            docIds: h._source.doc_id,
          });

          endpointsTextResp.data.aggregations.termsAgg.termsAgg.buckets
            .map((b) => b.key.toLowerCase())
            .filter((value, index, self) => self.indexOf(value) === index)
            .forEach((b) => {
              this.endpointsTextByDoc[idx].splice(
                this.endpointsTextByDoc[idx].length,
                0,
                b
              );
            });

          const resp = await ESService.getTrialIds({
            docIds: h._source.doc_id,
          });

          for (let t = 0; t < resp.data.trial_ids.length; t++) {
            ESService.getTrialInfo({
              nctNum: resp.data.trial_ids[t],
            }).then((trialInfoResp) => {
              const trialData = trialInfoResp.data;
              if (Object.keys(trialData).length > 0) {
                trialData.study_title =
                  '<a href="' +
                  trialData.link +
                  '" class="text-decoration-none" ' +
                  'target="_blank">' +
                  trialData.study_title +
                  '</a>';
                delete trialData.link;
                this.trialInfoByDoc[idx].splice(0, 0, trialData);
              }
              this.trialInfoByDoc[idx] = this.trialInfoByDoc[idx].sort(
                (a, b) => {
                  if (a.start_date && b.start_date) {
                    if (a.start_date > b.start_date) return -1;
                    else return 1;
                  } else return 0;
                }
              );
              this.trialInfoByDoc[idx] = this.trialInfoByDoc[idx].slice();
              // this.trialInfoByDoc[idx].isLoading = false;
            });
          }

          if (h._source.products && h._source.products.length > 0) {
            const productQuery = h._source.products
              .map((p) =>
                p.product_name && typeof p.product_name === 'object'
                  ? p.product_name.join(' ')
                  : p.product_name
              )
              .join(' ')
              .trim();

            if (productQuery.length > 0) {
              ESService.searchTrialAttributes({
                q: productQuery,
              }).then((searchTrialAttributesResp) => {
                searchTrialAttributesResp.data.hits.forEach(
                  (trialAttributesData) => {
                    trialAttributesData.study_title =
                      '<a href="' +
                      trialAttributesData.link +
                      '" class="text-decoration-none" ' +
                      'target="_blank">' +
                      trialAttributesData.study_title +
                      '</a>';
                    delete trialAttributesData.link;
                    // ensure that results are sorted in the same order as received
                    this.trialInfoByProduct[idx].splice(
                      this.trialInfoByProduct[idx].length,
                      0,
                      trialAttributesData
                    );
                    // this.trialInfoByDoc[idx].isLoading = false;
                  }
                );
              });
            }
          }
        });

        this.trialInfoByQuery = [];
        this.trialInfoByQueryNum = 0;
        if (this.isDev) {
          if (
            this.currSearchTerm.trim().length > 0 &&
            this.currSearchTerm.trim() !== '*'
          )
            ESService.searchTrialAttributes({
              q: this.currSearchTerm,
            }).then((searchTrialAttributesResp) => {
              this.trialInfoByQueryNum = searchTrialAttributesResp.data.numHits;
              searchTrialAttributesResp.data.hits.forEach(
                (trialAttributesData) => {
                  trialAttributesData.study_title =
                    '<a href="' +
                    trialAttributesData.link +
                    '" class="text-decoration-none" ' +
                    'target="_blank">' +
                    trialAttributesData.study_title +
                    '</a>';
                  delete trialAttributesData.link;
                  // ensure that results are sorted in the same order as received
                  this.trialInfoByQuery.splice(10, 0, trialAttributesData);
                  // this.trialInfoByDoc[idx].isLoading = false;
                }
              );
            });
        }

        // this.trialIdsByProduct = [[], [], [], [], [], [], [], [], [], []];
        // response.data.hits.hits.forEach(async (h, idx) => {
        //   if (h._source.products && h._source.products.length > 0) {
        //     const resp = await ESService.getTrialIds({
        //       productNames: h._source.products
        //         .map((p) => {
        //           if (p.product_name.length > 0) {
        //             if (typeof p.product_name === 'string')
        //               return p.product_name;
        //             else if (typeof p.product_name === 'object')
        //               return p.product_name.join('~');
        //           }
        //         })
        //         .join('~'),
        //     });
        //     // this.trialIdsByDoc.splice(0, 0, ...resp.data.trial_ids);
        //     this.trialIdsByProduct[idx].splice(
        //       0,
        //       resp.data.trial_ids.length,
        //       ...resp.data.trial_ids.map((t) => t.replace('#', ''))
        //     );
        //   }
        //   this.trialIdsByProduct.splice();
        //   this.trialIdsByProduct[idx] = [
        //     ...new Set(this.trialIdsByProduct[idx]),
        //   ];
        //   this.trialIdsByProduct[idx].splice();
        // });

        // this.trialIdsByProduct = [];
        // response.data.hits.hits.forEach(async (h) => {
        //   const resp = await ESService.getTrialIds({
        //     productNames: h._source.products.map((p) =>
        //       p.product_name && p.product_name.length > 0 ? p.product_name : ''
        //     ),
        //   });
        //   this.trialIdsByProduct.push(resp.data.trial_ids);
        // });

        // this.trialIdsByProduct = [...new Set(this.trialIdsByProduct)];

        if (this.currSearchTerm.length > 0) {
          const suggestResponse = await ESService.getMatchingFieldValues({
            q: this.currSearchTerm,
          });
          this.suggestResponse = suggestResponse.data;
        } else this.suggestResponse = {};

        this.searchLoading = false;
        Analytics.record({
          name: 'search',
          attributes: {
            email: this.userinfo.attributes.email,
            pageName: this.pageName,
            searchQuery: this.searchTerm,
            searchMode: this.selectedSearchMode,
            numResults: this.numHits,
            numDocs: this.numDocs,
            regAgencies: this.filters.regAgency.selected.join(', '),
            regAgencyDivisions:
              this.filters.regAgencyDivision.selected.join(', '),
            dbSources: this.filters.dbSource.selected.join(', '),
            docCategories: this.filters.docCategory.selected.join(', '),
            applicationTypes: this.filters.applicationType.selected.join(','),
            docSubCategories: this.filters.docSubCategory.selected.join(', '),
            docTypes: this.filters.docType.selected.join(', '),
            guidanceTopics: this.filters.guidanceTopic.selected.join(', '),
            guidanceStatuses: this.filters.guidanceStatus.selected.join(', '),
            manufacturingSites:
              this.filters.manufacturingSite.selected.join(', '),
            actionDateStart: this.actionDateStartValue,
            actionDateEnd: this.actionDateEndValue,
            productTypes: this.filters.productType.selected.join(', '),
            indications: this.filters.indication.selected.join(', '),
            productClasses: this.filters.productClass.selected.join(', '),
            companyNames: this.filters.companyName.selected.join(', '),
            genericNames: this.filters.genericName.selected.join(', '),
            brandNames: this.filters.brandName.selected.join(', '),
            productNames: this.filters.productName.selected.join(', '),
            productDosageRoutes:
              this.filters.productDosageRoute.selected.join('~'),
            productDosageForms:
              this.filters.productDosageForm.selected.join('~'),
            submissionTypes: this.filters.submissionType.selected.join(', '),
            drugDesignationOrPrograms:
              this.filters.drugDesignationOrProgram.selected.join(', '),
            sortOrder: this.sortOrder,
          },
        });
      } catch (err) {
        this.resetSearch();
        this.searchError = true;
        this.searchLoading = false;
        logError(err);
        Analytics.record({
          name: 'searchError',
          attributes: {
            email: this.userinfo.attributes.email,
            pageName: this.pageName,
            searchQuery: this.searchTerm,
            searchMode: this.selectedSearchMode,
            regAgencies: this.filters.regAgency.selected.join(', '),
            regAgencyDivisions:
              this.filters.regAgencyDivision.selected.join(', '),
            dbSources: this.filters.dbSource.selected.join(', '),
            docCategories: this.filters.docCategory.selected.join(', '),
            applicationTypes: this.filters.applicationType.selected.join(', '),
            docSubCategories: this.filters.docSubCategory.selected.join(', '),
            docTypes: this.filters.docType.selected.join(', '),
            guidanceTopics: this.filters.guidanceTopic.selected.join(', '),
            guidanceStatuses: this.filters.guidanceStatus.selected.join(', '),
            manufacturingSites:
              this.filters.manufacturingSite.selected.join(', '),
            actionDateStart: this.actionDateStartValue,
            actionDateEnd: this.actionDateEndValue,
            productTypes: this.filters.productType.selected.join(', '),
            indications: this.filters.indication.selected.join(', '),
            productClasses: this.filters.productClass.selected.join(', '),
            companyNames: this.filters.companyName.selected.join(', '),
            genericNames: this.filters.genericName.selected.join(', '),
            brandNames: this.filters.brandName.selected.join(', '),
            productNames: this.filters.productName.selected.join(', '),
            productDosageRoutes:
              this.filters.productDosageRoute.selected.join('~'),
            productDosageForms:
              this.filters.productDosageForm.selected.join('~'),
            submissionTypes: this.filters.submissionType.selected.join(', '),
            drugDesignationOrPrograms:
              this.filters.drugDesignationOrProgram.selected.join(', '),
            error: err,
          },
        });
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      }
    },
    async searchDrugInfo(drugBrandName) {
      try {
        const response = await ESService.searchDrug({
          db: drugBrandName,
        });
        if (response.data.hits.hits.length > 0) {
          const drug_info = response.data.hits.hits[0];
          this.drug_link = drug_info._source.drug_link;
          this.bla_link = drug_info._source.bla_link;
        } else {
          this.drug_link = undefined;
          this.bla_link = undefined;
        }
      } catch (err) {
        logError(err);
        if (err.response && err.response.status !== 401) {
          throw err;
        }
      }
    },

    async changePage(page) {
      this.show_select = false;
      if (page) this.currPage = page;
      this.scrollToTop();
      this.searchOffset = (page - 1) * 10;
      this.searchTerm = this.currSearchTerm;
      await this.search();
      this.setSnippetExistsArray();
      Analytics.record({
        name: 'clickPageNum',
        attributes: {
          email: this.userinfo.attributes.email,
          pageName: this.pageName,
          searchQuery: this.searchTerm,
          searchMode: this.selectedSearchMode,
          regAgencies: this.filters.regAgency.selected.join(', '),
          regAgencyDivisions:
            this.filters.regAgencyDivision.selected.join(', '),
          dbSources: this.filters.dbSource.selected.join(', '),
          docCategories: this.filters.docCategory.selected.join(', '),
          applicationTypes: this.filters.applicationType.selected.join(', '),
          docSubCategories: this.filters.docSubCategory.selected.join(', '),
          docTypes: this.filters.docType.selected.join(', '),
          guidanceTopics: this.filters.guidanceTopic.selected.join(', '),
          guidanceStatuses: this.filters.guidanceStatus.selected.join(', '),
          manufacturingSites:
            this.filters.manufacturingSite.selected.join(', '),
          actionDateStart: this.actionDateStartValue,
          actionDateEnd: this.actionDateEndValue,
          productTypes: this.filters.productType.selected.join(', '),
          indications: this.filters.indication.selected.join(', '),
          productClasses: this.filters.productClass.selected.join(', '),
          companyNames: this.filters.companyName.selected.join(', '),
          genericNames: this.filters.genericName.selected.join(', '),
          brandNames: this.filters.brandName.selected.join(', '),
          productNames: this.filters.productName.selected.join(', '),
          productDosageRoutes:
            this.filters.productDosageRoute.selected.join(', '),
          productDosageForms:
            this.filters.productDosageForm.selected.join(', '),
          submissionTypes: this.filters.submissionType.selected.join(', '),
          drugDesignationOrPrograms:
            this.filters.drugDesignationOrProgram.selected.join(', '),
          searchPageNum: this.currPage,
        },
      });
    },
    // TODO move this to backend
    getDocLinkPrefix(sourceName) {
      return (
        'https://highprioritypipelinev1stack-bucket83908e77-nfink9l696lw.s3.us-east-2.amazonaws.com/' +
        sourceName +
        '/pdfs/'
      );
    },
    selectDoc(hit, hitIdx, docPageNum = 1) {
      this.selectedDoc = hit;
      this.selectedDocIdx = hitIdx;
      this.selectedDocPageNum = docPageNum;
    },
    showFullDoc(hit, hitIdx, docPageNum = 1) {
      Analytics.record({
        name: 'showFullSearchResult',
        attributes: {
          email: this.userinfo.attributes.email,
          pageName: this.pageName,
          searchQuery: this.searchTerm,
          searchMode: this.selectedSearchMode,
          regAgencies: this.filters.regAgency.selected.join(', '),
          regAgencyDivisions:
            this.filters.regAgencyDivision.selected.join(', '),
          dbSources: this.filters.dbSource.selected.join(', '),
          docCategories: this.filters.docCategory.selected.join(', '),
          applicationTypes: this.filters.applicationType.selected.join(', '),
          docSubCategories: this.filters.docSubCategory.selected.join(', '),
          docTypes: this.filters.docType.selected.join(', '),
          guidanceTopics: this.filters.guidanceTopic.selected.join(', '),
          guidanceStatuses: this.filters.guidanceStatus.selected.join(', '),
          manufacturingSites:
            this.filters.manufacturingSite.selected.join(', '),
          actionDateStart: this.actionDateStartValue,
          actionDateEnd: this.actionDateEndValue,
          productTypes: this.filters.productType.selected.join(', '),
          indications: this.filters.indication.selected.join(', '),
          productClasses: this.filters.productClass.selected.join(', '),
          companyNames: this.filters.companyName.selected.join(', '),
          genericNames: this.filters.genericName.selected.join(', '),
          brandNames: this.filters.brandName.selected.join(', '),
          productNames: this.filters.productName.selected.join(', '),
          productDosageRoutes:
            this.filters.productDosageRoute.selected.join(', '),
          productDosageForms:
            this.filters.productDosageForm.selected.join(', '),
          submissionTypes: this.filters.submissionType.selected.join(', '),
          drugDesignationOrPrograms:
            this.filters.drugDesignationOrProgram.selected.join(', '),
          searchResultId: hit._id,
          searchResultRank: (this.searchOffset + hitIdx + 1).toString(),
          searchResultPageNum: (this.searchOffset / 10 + 1).toString(),
          docPageNum: docPageNum,
        },
      });

      this.selectDoc(hit, hitIdx, docPageNum);
      this.showPDFViewerModal = true;
    },
    /** Close the full QA modal */
    closeFullDoc() {
      //document.body.style.overflow = 'auto'
      this.selectedDoc = null;
      this.selectedDocIdx = null;
    },
    addToCompare(hit, hitIdx) {
      this.compareDocs.push({ hit: hit, hitIdx: hitIdx });
      if (this.compareDocs.length === 2) {
        this.showCompareDocsModal = true;
      }
    },
    closeCompareDocsModal() {
      this.compareDocs = [];
      this.showCompareDocsModal = false;
    },
    async setUserVotes(searchResults) {
      for (let r = 0; r < searchResults.length; r++) {
        const res_id = searchResults[r]._id;
        const response = await API.graphql(
          graphqlOperation(getUserVoteEntry, {
            id: this.userinfo.attributes.email + '-' + res_id,
          })
        );
        if (response.data.getUserVoteEntry === null) continue;
        else if (response.data.getUserVoteEntry.vote === 'YES')
          this.thumbsUps.push(res_id);
        else if (response.data.getUserVoteEntry.vote === 'NO')
          this.thumbsDowns.push(res_id);
      }
    },
    async clickFlag(hitId) {
      if (!this.isFlag(hitId)) {
        this.flags.push(hitId);
        await API.graphql(
          graphqlOperation(createUserFlagEntry, {
            input: {
              id: this.userinfo.attributes.email + '-' + hitId,
              username: this.userinfo.attributes.email,
              entry_id: hitId,
            },
          })
        );
      } else {
        this.flags = this.flags.filter((t) => {
          return t !== hitId;
        });
        await API.graphql(
          graphqlOperation(deleteUserFlagEntry, {
            input: {
              id: this.userinfo.attributes.email + '-' + hitId,
            },
          })
        );
      }
    },
    isFlag(hitId) {
      if (this.flags.filter((t) => t === hitId).length > 0) return true;
      else return false;
    },
    async setUserFlags(searchResults) {
      for (let r = 0; r < searchResults.length; r++) {
        const res_id = searchResults[r]._id;
        const response = await API.graphql(
          graphqlOperation(getUserFlagEntry, {
            id: this.userinfo.attributes.email + '-' + res_id,
          })
        );
        if (response.data.getUserFlagEntry === null) continue;
        else this.flags.push(res_id);
      }
    },
    getAcItems(input) {
      // store current input so that it's accessible even when none
      // of the autocomplete items are selected
      this.searchTerm = input;
      // show items that have same prefix but not exactly the same
      // content as input
      return this.acItems
        .filter((q) => {
          return (
            q.toLowerCase().startsWith(input.toLowerCase()) &&
            q.toLowerCase() !== input.toLowerCase()
          );
        })
        .slice(0, 5);
    },
    toggleFilters() {
      if (this.showFilters) this.showFilters = false;
      else this.showFilters = true;
    },
    convertDateToString(date) {
      return [
        date.getFullYear(),
        // getMonth + 1 because getMonth uses zero-based indexing
        ('0' + (date.getMonth() + 1)).slice(-2),
        ('0' + date.getDate()).slice(-2),
      ].join('-');
    },
    displayList(list) {
      if (!list || list.length === 0) return '';
      else if (Array.isArray(list)) {
        if (list.length === 1) return list[0];
        else return list.slice(0, -1).join(', ') + ' and ' + list.slice(-1)[0];
      } else return list;
    },
    sortByFilterKeys(a, b) {
      if (a.key.toLowerCase() < b.key.toLowerCase()) return -1;
      if (a.key.toLowerCase() > b.key.toLowerCase()) return 1;
      return 0;
    },
    sortByFilterCounts(a, b) {
      if (a.doc_count > b.doc_count) return -1;
      if (a.doc_count < b.doc_count) return 1;
      return 0;
    },
    displayTruncatedTag(tag) {
      return tag.substring(0, 30) + (tag && tag.length > 30 ? '...' : '');
    },
    scrollToTop() {
      window.scrollTo(0, 0);
    },
    processHighlight(content) {
      const quotedTerms = this.getQuotedSearchTerms;
      let processedContent = content;
      quotedTerms.forEach((term) => {
        try {
          const regex = new RegExp(' (' + term.trim() + ') ', 'ig');
          processedContent = processedContent.replaceAll(
            regex,
            '<mark>$1</mark>'
          );
        } catch (err) {
          logError(err);
        }
      });
      return processedContent;
    },
    processSectionHighlight(content) {
      const quotedTerms = this.getQuotedSearchTerms;
      let processedContent = content;

      // also handling cases where quoted term has symbols between words of a
      // phrase. In this case, we remove the symbols from the highlighted
      // snippet. This is not ideal, but better than showing empty matches.
      quotedTerms
        .filter((t) => t.trim().length > 0)
        .forEach((term) => {
          try {
            const regexSymbols = new RegExp(
              '((\\s*[^A-Za-z0-9]+\\s*|\\s+))',
              'ig'
            );
            term = term.replaceAll(regexSymbols, ' ');
            const regexQuoted = new RegExp(
              '([^A-Za-z\\-])(' + term.trim() + ')([^A-Za-z\\-])',
              'ig'
            );
            processedContent = processedContent
              .replaceAll('-', ' ')
              .replaceAll(regexQuoted, '$1<mark> $2 </mark>$3');
          } catch (err) {
            logError(err);
          }
        });
      const unquotedTerms = this.getUnquotedSearchTerms;
      unquotedTerms
        .filter((t) => t.trim().length > 0)
        .forEach((term) => {
          try {
            const regexUnquoted = new RegExp(
              // '([^A-Za-z\\-])(' + term.trim() + ')([^A-Za-z\\-])',
              // allow highlight of partial text due to match with word
              // variations
              '(' + term.trim() + ')',
              'ig'
            );
            processedContent = processedContent.replaceAll(
              regexUnquoted,
              '<mark>$1</mark>'
            );
          } catch (err) {
            logError(err);
          }
        });
      const referenceRegex = /(\(\s*(\d+(\.\d+)?\s*,?\s*)+\s*\)\s*]?)/gi;
      processedContent = processedContent.replaceAll(referenceRegex, '$1<br/>');
      const bulletPointRegex = /([^(]\s+\d+\.\d+\s+[^)])/gi;
      processedContent = processedContent.replaceAll(
        bulletPointRegex,
        '<br/>$1'
      );
      return processedContent;
      // return processedContent;
      // split by (1.2) and 1.1, etc.

      // if (!processedContent.includes('<mark>')) return processedContent;
      // get snippets
      // const words = processedContent.split(' ');
      // let snippets = '';
      // let startIdx = 0;
      // let endIdx = 1;
      // // eslint-disable-next-line no-constant-condition
      // while (startIdx < words.length) {
      //   endIdx = words.indexOf('<mark>', startIdx);
      //   endIdx = words.indexOf('</mark>', endIdx + 1);
      //   snippets += words
      //     .slice(Math.max(0, startIdx - 10), endIdx + 10)
      //     .join(' ');
      //   startIdx = endIdx + 10;
      // }
      // return snippets;
    },
    selectFilterValue(filter, lst) {
      // if no matching filter found, return
      if (
        this.filters[filter] !== undefined &&
        this.filters[filter].type &&
        this.filters[filter].type === 'timeline'
      ) {
        if (lst.length === 0) {
          this.actionDateStartValue = '';
          this.actionDateEndValue = '';
        } else {
          const startYear = Math.min(
            ...lst.map((y) => {
              return parseInt(y);
            })
          );
          const endYear = Math.max(
            ...lst.map((y) => {
              return parseInt(y);
            })
          );
          this.actionDateStartValue = startYear + '-01-01';
          this.actionDateEndValue = endYear + '-12-31';
        }
        this.setLoading();
        this.setNewSearchAttributes();
        this.refreshFilters();
        this.newSearch();
      } else if (this.filters[filter] !== undefined) {
        this.filters[filter].selected = [...lst];
        if (lst.length === 0) {
          if (this.selectedFilters.includes(filter)) {
            this.selectedFilters.splice(
              this.selectedFilters.indexOf(filter),
              1
            );
          }
        } else if (!this.selectedFilters.includes(filter)) {
          this.selectedFilters.push(filter);
        }
        // endpoint filter needs to be handled separately
        if (filter === 'endpoint') {
          if (lst.length === 0) {
            this.endpointSearchTerm = '';
          } else {
            this.endpointSearchTerm = ' (' + lst.join(' | ') + ') ';
          }
        }
        this.setLoading();
        this.setNewSearchAttributes();
        this.refreshFilters();
        this.newSearch();
      }
    },
    // modified from: https://codepen.io/duc810/pen/MNXOqW
    exportDataToCSV(data, yDataType, headers, filenamePrefix) {
      let csvContent = 'data:text/csv;charset=utf-8,';
      csvContent += [
        '"Date/Time";"' + new Date() + '"',
        '"Search Term";"' +
          (this.currSearchTerm.length > 0 ? this.currSearchTerm : '<empty>') +
          '"',
        '"Page Filter";"' +
          (this.selectedSearchMode.length > 0
            ? this.selectedSearchMode
            : '<empty>') +
          '"',
        'Document Filters;"' +
          (this.getFilterTags.length > 0
            ? this.getFilterTags.map((f) => f.val)
            : '<empty>') +
          '"',
        '',
        '',
        headers
          .map((h) => {
            return '"' + h + '"';
          })
          .join(';'),
        ...data.map(
          (item) =>
            '"' +
            item.key +
            '"' +
            ';' +
            '"' +
            (yDataType === 'int' ? item.doc_count : item.doc_count.toFixed(2)) +
            '"'
        ),
      ]
        .join('\n')
        .replace(/(^\[)|(\]$)/gm, '');

      const csvData = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', csvData);
      link.setAttribute('download', filenamePrefix + '.csv');
      link.click();
    },
    exportSVGToJPEG(chartID, filename) {
      const svg = document.getElementById(chartID);
      const canvas = document.createElement('canvas');
      canvas.width = svg.getAttribute('width');
      canvas.height = svg.getAttribute('height');
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const data = new XMLSerializer().serializeToString(svg);
      const DOMURL = window.URL || window.webkitURL || window;
      const img = new Image();
      const svgBlob = new Blob([data], { type: 'image/svg+xml;charset=utf-8' });
      const url = DOMURL.createObjectURL(svgBlob);
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        DOMURL.revokeObjectURL(url);
        const imgURI = canvas.toDataURL('image/jpeg');
        const a = document.createElement('a');
        a.setAttribute('download', filename + '.jpeg');
        a.setAttribute('href', imgURI);
        a.click();
      };
      img.src = url;
    },
    exportSearchResultsToCSV(data, filenamePrefix) {
      let csvContent = 'data:text/csv;charset=utf-8,';
      const headers = [
        'Document Title',
        'Date',
        'Document Category',
        'Approval Type',
        'Document Type',
        'Agency',
        'Agency Division Name',
        'Indications',
        'Therapeutic Areas',
        'Products',
        'Product Types',
        'Product Class',
        'Page Number',
        'Page Content',
        'Link',
      ];

      const content = [];
      data.forEach((hit) => {
        if (hit.inner_hits) {
          const matching_pages = hit.inner_hits.by_doc.hits.hits;
          matching_pages.forEach((match) => {
            const source = match._source;

            let indications = source.indications
              ? source.indications.join(';')
              : '';
            indications = indications.replace(',', ' ');

            const therapeutic_areas = source.therapeutic_areas
              ? source.therapeutic_areas.join(';')
              : '';

            const products = getUniqueProductNames(source.products).join(';');

            const product_types = source.products_product_type
              ? Array.from(new Set(source.products_product_type)).join(';')
              : '';

            const product_class =
              source.products && source.products.product_class
                ? source.products.product_class
                : '';

            let title = '';
            if (source.doc_title) {
              title = source.doc_title;
            } else if (source.doc_type) {
              title =
                source.doc_type instanceof Array
                  ? source.doc_type.join(';')
                  : source.doc_type;
            } else {
              title = source.doc_category;
            }

            let page_content = '';
            if (
              match.highlight &&
              match.highlight.page_content &&
              match.highlight.page_content.length > 0
            ) {
              page_content = this.processHighlight(
                match.highlight.page_content[0]
              );
            } else if (
              match.highlight &&
              match.highlight['page_content.original'] &&
              match.highlight['page_content.original'].length > 0
            ) {
              page_content = match.highlight['page_content.original'][0];
            } else {
              page_content = source.page_content.slice(
                source.page_content.indexOf(' ', 100),
                source.page_content.indexOf(' ', 300)
              );
            }
            page_content = page_content.replaceAll('<mark>', '');
            page_content = page_content.replaceAll('</mark>', '');
            page_content = page_content.replaceAll(/#/g, '');
            page_content = page_content.replaceAll(
              /[[/\]*+?\\^$|\\']/g,
              '\\$&'
            );
            page_content = page_content.replace(/\n/g, ' ');

            const link = encodeURI(
              this.getDocLinkPrefix(source.source_name) + source.bts_file_name
            );

            const row = [
              title,
              source.action_date,
              source.doc_category,
              source.doc_sub_category,
              source.doc_type instanceof Array
                ? source.doc_type.join(';')
                : source.doc_type,
              source.agency_name,
              source.agency_division_name,
              indications,
              therapeutic_areas,
              products,
              product_types,
              product_class,
              source.page_number,
              '"' + page_content + '"',
              '"' + link + '"',
            ];
            content.push(row);
          });
        } else {
          const source = hit._source;

          let indications = source.indications
            ? source.indications.join(';')
            : '';
          indications = indications.replace(',', ' ');

          const therapeutic_areas = source.therapeutic_areas
            ? source.therapeutic_areas.join(';')
            : '';

          const products = getUniqueProductNames(source.products).join(';');

          const product_types = source.products_product_type
            ? Array.from(new Set(source.products_product_type)).join(';')
            : '';

          const product_class =
            source.products && source.products.product_class
              ? source.products.product_class
              : '';

          let title = '';
          if (source.doc_title) {
            title = source.doc_title;
          } else if (source.doc_type) {
            title =
              source.doc_type instanceof Array
                ? source.doc_type.join(';')
                : source.doc_type;
          } else {
            title = source.doc_category;
          }

          let page_content = '';
          if (
            hit.highlight &&
            hit.highlight.page_content &&
            hit.highlight.page_content.length > 0
          ) {
            page_content = this.processHighlight(hit.highlight.page_content[0]);
          } else if (
            hit.highlight &&
            hit.highlight['page_content.original'] &&
            hit.highlight['page_content.original'].length > 0
          ) {
            page_content = hit.highlight['page_content.original'][0];
          } else {
            page_content = source.page_content.slice(
              source.page_content.indexOf(' ', 100),
              source.page_content.indexOf(' ', 300)
            );
          }
          page_content = page_content.replaceAll('<mark>', '');
          page_content = page_content.replaceAll('</mark>', '');
          page_content = page_content.replaceAll(/#/g, '');
          page_content = page_content.replaceAll(/[[/\]*+?\\^$|\\']/g, '\\$&');
          page_content = page_content.replace(/\n/g, ' ');

          const link = encodeURI(
            this.getDocLinkPrefix(source.source_name) + source.bts_file_name
          );

          const row = [
            title,
            source.action_date,
            source.doc_category,
            source.doc_sub_category,
            source.doc_type instanceof Array
              ? source.doc_type.join(';')
              : source.doc_type,
            source.agency_name,
            source.agency_division_name,
            indications,
            therapeutic_areas,
            products,
            product_types,
            product_class,
            source.page_number,
            '"' + page_content + '"',
            '"' + link + '"',
          ];
          content.push(row);
        }
      });

      const metadata = [
        ['"Date/Time"', '"' + new Date() + '"'],
        [
          '"Search Term"',
          '"' +
            (this.currSearchTerm.length > 0 ? this.currSearchTerm : '<empty>') +
            '"',
        ],
        [
          '"Page Filter"',
          '"' +
            (this.selectedSearchMode.length > 0
              ? this.selectedSearchMode
              : '<empty>') +
            '"',
        ],
        [
          '"Document Filters"',
          '"' +
            (this.getFilterTags.length > 0
              ? this.getFilterTags.map((f) => f.val)
              : '<empty>') +
            '"',
        ],
        ['', ''],
        ['', ''],
      ];

      csvContent += [...metadata, headers, ...content]
        .map(
          (row) =>
            row
              .map(String) // convert every value to String
              .map((v) => v.replaceAll('"', '""')) // escape double colons
              .map((v) => `"${v}"`) // quote it
              .join(',') // comma-separated
        )
        .join('\r\n'); // rows starting on new lines
      // .join('\n')
      // .replace(/(^\[)|(\]$}{')/gm, '');

      const csvData = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', csvData);
      link.setAttribute('download', filenamePrefix + '.csv');
      link.click();
    },
    async downloadData(data, idSuffix = '') {
      if (idSuffix === 'search-results' && !this.show_select) {
        this.downloading = true;
        let download_offset = this.searchOffset;
        for (let i = 0; i < 19; i++) {
          download_offset += 10;
          const response = await ESService.searchRegDocPage(
            this.getApiArgs({
              offset: download_offset,
              sortOrder: this.sortOrder,
            })
          );
          data = data.concat(response.data.hits.hits);
        }
        this.exportSearchResultsToCSV(
          data,
          'RegForesight Search Results ' +
            new Date()
              .toLocaleTimeString([], {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })
              .replace(/\//g, '-')
              .replace(/:/g, '.')
        );
        this.downloading = false;
      } else if (idSuffix === 'search-results' && this.show_select) {
        this.downloading = true;
        this.exportSearchResultsToCSV(
          data,
          'RegForesight Page ' + this.currPageNum + ' Selected Results'
        );
        this.downloading = false;
      } else if (data.asTable) {
        this.exportDataToCSV(
          data.visualizeOptions,
          data.yDataType,
          [data.xLabel, data.yLabel],
          'RegForesight ' + data.title
        );
      } else {
        this.exportSVGToJPEG(
          'svg-chart-' + data.id + idSuffix,
          'RegForesight ' + data.title
        );
      }
    },

    reOrganizeSearchObject(search) {
      const organizedSearchObject = {
        id: search.id,
        title: search.title || 'None',
        description: search.description || 'None',
        mode: search.mode,
        query: search.query || 'None',
        filters: this.reOrganizeFilters(JSON.parse(search.filters)),
        actionDateStart: search.actionDateStart,
        actionDateEnd: search.actionDateEnd,
        updatedAt: search.updatedAt,
      };
      return organizedSearchObject;
    },
    reOrganizeFilters(filters) {
      const organizedfilter = {};
      filters.forEach((filter) => {
        if (!(filter.name in organizedfilter)) {
          organizedfilter[filter.name] = [filter.val];
        } else {
          organizedfilter[filter.name].push(filter.val);
        }
      });
      return organizedfilter;
    },
    closePdfViewerModal() {
      this.showPDFViewerModal = false;
    },
    toggle_show_select() {
      this.show_select = this.show_select === false ? true : false;
      this.select_download = [];
    },
  },
};
</script>

<style scoped>
.chart-card {
  height: 460px;
}

.link:hover {
  cursor: pointer;
}

.tab-text {
  font-size: 0.95rem;
}

hr {
  margin: 0px 1px 0px 1px;
}

.progress-modal-text {
  font-size: 1rem;
}

#sidebar {
  font-size: 0.9rem;
}

.text-title {
  font-weight: bold;
}

.show-all-tags,
.hide-tags,
.clear-filters {
  color: blue;
}

.show-all-trials {
  color: blue;
  font-size: 0.8em;
  cursor: pointer;
}

.show-all-tags:hover,
.hide-tags:hover,
.clear-filters:hover {
  cursor: pointer;
}

.search-input-label {
  font-weight: bold;
}

.suggested-ans-body {
  font-family: 'Muli', sans-serif;
  /* cursor: pointer; */
  font-weight: bold;
  font-style: italic;
}

.search-result-title {
  font-family: 'Muli', sans-serif;
  /* cursor: pointer; */
  font-size: 1rem;
  font-weight: bold;
  padding: 0.5rem 0.5rem 0.5rem 0.5rem;
}

.search-result-sub-title {
  font-family: 'Muli', sans-serif;
  /* cursor: pointer; */
  font-size: 0.9rem;
  font-weight: bold;
  padding: 0.5rem 0.5rem 0.5rem 0.5rem;
}

.page-num {
  font-style: 0.8rem;
}

.search-result-body {
  font-family: 'Muli', sans-serif;
  /* cursor: pointer; */
  font-size: 0.95rem;
  padding: 0.5rem 0.5rem 0.5rem 0.5rem;
}

.loading-card-body {
  height: 400px;
}

.search-result-footer {
  font-family: 'Muli', sans-serif;
  font-size: 0.9rem;
  padding: 0.3rem 0.3rem 0.3rem 0.3rem;
}

.feedback-caption {
  color: gray;
}

.thumb-icon,
.flag-icon {
  cursor: pointer;
}

.show-full-doc {
  color: #007bff;
  cursor: pointer;
}
.show-full-doc :hover {
  text-decoration: underline;
}
.show-full-doc-page {
  color: #2f7df6;
  cursor: pointer;
}
.show-full-doc-page:hover {
  color: #2f7df6;
  text-decoration: underline;
}

.form-dropdown-title,
.form-dropdown-search-label,
.form-dropdown-search-input {
  font-size: 0.9rem;
}

.form-dropdown-tags {
  font-size: 1.1rem;
}

.filter-dropdown /deep/ .dropdown-menu {
  max-height: 40vh;
  max-width: 80vh;
  overflow-y: auto;
}

.full-screen-modal .modal-dialog {
  max-width: 100%;
  margin: 0;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100vh;
  display: flex;
  position: fixed;
  z-index: 100000;
}

#search-options
  #filter-options
  #search-modes-options
  #analytics-options
  .modal-dialog {
  max-width: 100%;
  margin: 0;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100vh;
  display: flex;
  position: fixed;
  z-index: 100000;
}

#search-modes {
  max-height: 30vh;
  width: 95%;
  overflow-y: auto;
}

#search-result-pagination {
  margin: 10px;
}

.save-search,
.save-snippet,
.num-clinical-trials {
  cursor: pointer;
}

#toggle-button {
  background-color: #1f73b7;
  margin-left: 20px;
  border-radius: 999rem;
  font-size: 0.9rem;
}

.tooltip-msg {
  background-color: #50596c;
  color: #fff;
}

.error-message {
  color: red;
}

.saved-filter {
  cursor: pointer;
  text-decoration: underline;
}

.saved-filter.active {
  color: #1f73b7;
  font-weight: 700;
}

.support-call-link {
  color: salmon;
  text-decoration: none;
  cursor: pointer;
}
</style>
